import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api, { adminAPI, trackActivity } from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Line, ComposedChart, ReferenceDot, ScatterChart, Scatter,
} from 'recharts';
import XLSX from 'xlsx-js-style';
import './PortfolioOptimiserApp.css';

// Default per-AC constraints matching the DSP optimiser
const DSP_AC_CONSTRAINTS = {
  ALT_DEF: { mode: 'absolute', absMin: 0, absMax: 0, reference: 'defensive', relMin: 0, relMax: 100 },
  Cash: { mode: 'absolute', absMin: 2, absMax: 100, reference: 'defensive', relMin: 0, relMax: 100 },
  CREDIT_AU: { mode: 'relative', absMin: 0, absMax: 100, reference: 'defensive', relMin: 10, relMax: 20 },
  CREDIT_GL: { mode: 'relative', absMin: 0, absMax: 100, reference: 'defensive', relMin: 10, relMax: 20 },
  CURRENCY: { mode: 'relative', absMin: 0, absMax: 100, reference: 'intl_equity', relMin: 50, relMax: 90 },
  EQ_AU: { mode: 'relative', absMin: 0, absMax: 100, reference: 'growth', relMin: 0, relMax: 38.6 },
  EQ_EM: { mode: 'absolute', absMin: 0, absMax: 0, reference: 'growth', relMin: 0, relMax: 100 },
  EQ_WORLD: { mode: 'relative', absMin: 0, absMax: 100, reference: 'growth', relMin: 40, relMax: 100 },
  HF_GM: { mode: 'relative', absMin: 0, absMax: 100, reference: 'growth', relMin: 0, relMax: 3 },
  HF_LS: { mode: 'relative', absMin: 0, absMax: 100, reference: 'growth', relMin: 0, relMax: 3 },
  INFRA: { mode: 'relative', absMin: 0, absMax: 100, reference: 'growth', relMin: 0, relMax: 5 },
  PE: { mode: 'absolute', absMin: 0, absMax: 0, reference: 'growth', relMin: 0, relMax: 100 },
  REITs: { mode: 'relative', absMin: 0, absMax: 100, reference: 'growth', relMin: 0, relMax: 5 },
};

const DEFAULT_CONSTRAINT = { mode: 'none', absMin: 0, absMax: 100, reference: 'growth', relMin: 0, relMax: 100 };

const REFERENCE_OPTIONS = [
  { value: 'growth', label: 'Growth' },
  { value: 'defensive', label: 'Defensive' },
  { value: 'intl_equity', label: 'EQ_WORLD' },
];

const PRESETS = {
  default_dsp: { label: 'Default (DSP)' },
  unconstrained: { label: 'Unconstrained' },
};


// Pre-seeded batch template that mirrors the constraints in
// `Portfolio Modelling/Portfolio Optimisation 2026.py`.
// Backend MERGES this with DSP defaults — multi-asset group constraints
// (CREDIT_AU+CREDIT_GL 20-40% of defensive, FX 50-90% of intl equity) and
// other DSP relative constraints not listed here are preserved automatically.
const DSP_2026_TEMPLATE_NAME = 'DSP 2026 Defaults';
function buildDsp2026Template() {
  const allProfiles = {};
  ALL_DSP_PROFILES.forEach((p) => { allProfiles[p] = true; });

  const growthMin = {};
  const growthMax = {};
  ALL_DSP_PROFILES.forEach((p) => {
    const g = DSP_DEFAULTS[p]?.growth_pct;
    if (g != null) {
      growthMin[p] = String(g);
      growthMax[p] = String(g);
    }
  });

  const feeBps = {};
  ALL_DSP_PROFILES.forEach((p) => {
    const f = DSP_DEFAULTS[p]?.fee_bps;
    if (f != null) feeBps[p] = String(f);
  });

  // Per-SAC bound rules from the script. Each profile gets the same baseline
  // plus profile-specific overrides for Cash, PE, and AU credit.
  const baselineBounds = {
    ALT_DEF:  { mode: 'absolute', min: '0', max: '0' },
    EQ_EM:    { mode: 'absolute', min: '0', max: '0' },
    EQ_AU:    { mode: 'growth',   min: '',  max: '38.6' },
    EQ_WORLD: { mode: 'growth',   min: '40', max: '50' },
    HF_LS:    { mode: 'growth',   min: '',  max: '3' },
    HF_GM:    { mode: 'growth',   min: '',  max: '3' },
    REITs:    { mode: 'growth',   min: '',  max: '5' },
    INFRA:    { mode: 'growth',   min: '',  max: '5' },
    CURRENCY: { mode: 'intl_equity', min: '50', max: '90' },
  };

  const cashByProfile = {
    'DSP 20':       { mode: 'absolute', min: '2',  max: '8' },   // 2% min, 10% of 80% defensive
    'DSP 30':       { mode: 'absolute', min: '2',  max: '7' },   // 2% min, 10% of 70% defensive
    'DSP 40':       { mode: 'absolute', min: '2',  max: '6' },
    'DSP 50':       { mode: 'absolute', min: '2',  max: '5' },
    'DSP 60':       { mode: 'absolute', min: '2',  max: '4' },
    'DSP 70':       { mode: 'absolute', min: '2',  max: '3' },
    'DSP 80':       { mode: 'absolute', min: '2',  max: '2' },
    'DSP 90':       { mode: 'absolute', min: '0',  max: '1' },
    'DSP 100':      { mode: 'absolute', min: '0',  max: '0' },
    'DSP 100 Plus': { mode: 'absolute', min: '0',  max: '0' },
  };

  // PE: 0/0 absolute for DSP 30-100, max 20% of growth for DSP 100 Plus
  const peByProfile = {};
  ALL_DSP_PROFILES.forEach((p) => {
    peByProfile[p] = (p === 'DSP 100 Plus')
      ? { mode: 'growth', min: '', max: '20' }
      : { mode: 'absolute', min: '0', max: '0' };
  });

  // CREDIT_AU: forced to 0 in DSP 100 / DSP 100 Plus per the script
  const auCreditByProfile = {
    'DSP 100':      { mode: 'absolute', min: '0', max: '0' },
    'DSP 100 Plus': { mode: 'absolute', min: '0', max: '0' },
  };

  const bounds = {};
  ALL_DSP_PROFILES.forEach((p) => {
    const profileBounds = { ...baselineBounds };
    Object.entries(profileBounds).forEach(([sac, cfg]) => {
      profileBounds[sac] = { ...cfg };
    });
    if (cashByProfile[p]) profileBounds.Cash = { ...cashByProfile[p] };
    if (peByProfile[p]) profileBounds.PE = { ...peByProfile[p] };
    if (auCreditByProfile[p]) profileBounds.CREDIT_AU = { ...auCreditByProfile[p] };
    bounds[p] = profileBounds;
  });

  return {
    batchProfiles: allProfiles,
    batchObjective: 'max_sharpe',
    batchProfileGrowth: {},
    batchProfileGrowthMin: growthMin,
    batchProfileGrowthMax: growthMax,
    batchProfileFee: feeBps,
    batchProfileBounds: bounds,
  };
}

const OBJECTIVE_CONFIG = {
  max_sharpe: { label: 'Max Sharpe', color: '#f0a500', headerClass: 'sharpe' },
  min_vol: { label: 'Min Vol', color: '#072040', headerClass: 'minvol' },
  risk_parity: { label: 'Risk Parity', color: '#91CF9B', headerClass: 'riskparity' },
  hrp: { label: 'HRP', color: '#599BE3', headerClass: 'hrp' },
  target_return: { label: 'Target Return', color: '#599BE3', headerClass: 'targetret' },
  target_vol: { label: 'Target Vol', color: '#6c757d', headerClass: 'targetvol' },
};

const ALL_DSP_PROFILES = [
  'DSP 20', 'DSP 30', 'DSP 40', 'DSP 50', 'DSP 60', 'DSP 70',
  'DSP 80', 'DSP 90', 'DSP 100', 'DSP 100 Plus',
];

// Mirror of engine.DSP_PROFILES defaults — used to show placeholders in the
// per-portfolio constraint matrix. Keep in sync with app/apps/optimiser/engine.py.
const DSP_DEFAULTS = {
  'DSP 20':       { growth_pct: 20,  fee_bps: 65 },
  'DSP 30':       { growth_pct: 30,  fee_bps: 65 },
  'DSP 40':       { growth_pct: 40,  fee_bps: 65 },
  'DSP 50':       { growth_pct: 50,  fee_bps: 65 },
  'DSP 60':       { growth_pct: 60,  fee_bps: 65 },
  'DSP 70':       { growth_pct: 70,  fee_bps: 65 },
  'DSP 80':       { growth_pct: 80,  fee_bps: 65 },
  'DSP 90':       { growth_pct: 90,  fee_bps: 60 },
  'DSP 100':      { growth_pct: 100, fee_bps: 65 },
  'DSP 100 Plus': { growth_pct: 100, fee_bps: 65 },
};

function roundHalf(v) {
  return Math.round(v * 2) / 2;
}

function corrColor(val) {
  if (val >= 0.8) return '#072040';
  if (val >= 0.5) return '#2a5a8a';
  if (val >= 0.2) return '#599BE3';
  if (val >= 0) return '#c5ddf5';
  if (val >= -0.2) return '#ffeaaa';
  if (val >= -0.5) return '#f0a500';
  return '#c97800';
}

// ─── Shared Excel export helpers ──────────────────────────────────────────────
// Style objects + data-driven sheet builders shared by the optimiser's Excel
// exports. Kept at module scope (not inside the component) so both the single-run
// export and the multi-run batch export reuse identical formatting and layouts.
const OPT_XLSX_STYLES = {
  header: {
    font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
    fill: { fgColor: { rgb: '072040' } },
    alignment: { horizontal: 'center' },
  },
  section: {
    font: { bold: true, color: { rgb: '072040' }, sz: 11 },
    fill: { fgColor: { rgb: 'E7E6E6' } },
  },
  total: {
    font: { bold: true, color: { rgb: '072040' }, sz: 10 },
    fill: { fgColor: { rgb: 'F0A500' } },
  },
  cell: {
    font: { sz: 10 },
    border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } },
  },
};
OPT_XLSX_STYLES.totalNum = { ...OPT_XLSX_STYLES.total, alignment: { horizontal: 'right' }, numFmt: '0.0' };
OPT_XLSX_STYLES.num = { ...OPT_XLSX_STYLES.cell, alignment: { horizontal: 'right' }, numFmt: '0.0' };
OPT_XLSX_STYLES.num4 = { ...OPT_XLSX_STYLES.num, numFmt: '0.0000' };

// Apply the navy header style to row 0 and set column widths.
function styleHeaderRow(ws, colCount, colWidths) {
  if (colWidths) ws['!cols'] = colWidths.map((w) => ({ wch: w }));
  for (let c = 0; c < colCount; c++) {
    const ref = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[ref]) ws[ref].s = OPT_XLSX_STYLES.header;
  }
}

// Excel sheet names are capped at 31 chars and must be unique within a workbook.
function safeSheetName(name, used) {
  let base = String(name || 'Sheet').replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 31) || 'Sheet';
  let candidate = base;
  let i = 2;
  while (used.has(candidate)) {
    const suffix = ` ${i}`;
    candidate = base.slice(0, 31 - suffix.length) + suffix;
    i += 1;
  }
  used.add(candidate);
  return candidate;
}

// Per-profile metrics (Return/Vol/Sharpe/Growth) + per-SAC weight rows.
function buildBatchWeightsSheet(profiles, batchResults, acCodes) {
  const headers = ['Metric', ...profiles];
  const aoa = [headers];
  const rowMeta = [];
  ['Return (%)', 'Volatility (%)', 'Sharpe Ratio', 'Growth (%)'].forEach((label, mi) => {
    const row = [label];
    profiles.forEach((p) => {
      const r = batchResults[p];
      if (!r) { row.push(''); return; }
      if (mi === 0) row.push((r.portfolio_return * 100).toFixed(2));
      else if (mi === 1) row.push((r.portfolio_volatility * 100).toFixed(2));
      else if (mi === 2) row.push((r.sharpe_ratio ?? 0).toFixed(4));
      else row.push(roundHalf((r.growth_exposure ?? 0) * 100).toFixed(1));
    });
    aoa.push(row); rowMeta.push('total');
  });
  aoa.push(Array(headers.length).fill('')); rowMeta.push('blank');
  acCodes.forEach((code) => {
    const row = [code];
    profiles.forEach((p) => { row.push(roundHalf((batchResults[p]?.weights?.[code] || 0) * 100)); });
    aoa.push(row); rowMeta.push('data');
  });
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 18 }, ...profiles.map(() => ({ wch: 14 }))];
  for (let c = 0; c < headers.length; c++) {
    const ref = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[ref]) ws[ref].s = OPT_XLSX_STYLES.header;
  }
  for (let r = 0; r < rowMeta.length; r++) {
    const excelRow = r + 1;
    for (let c = 0; c < headers.length; c++) {
      const ref = XLSX.utils.encode_cell({ r: excelRow, c });
      if (!ws[ref]) ws[ref] = { v: '', t: 's' };
      if (rowMeta[r] === 'total') ws[ref].s = c >= 1 ? OPT_XLSX_STYLES.totalNum : OPT_XLSX_STYLES.total;
      else if (rowMeta[r] === 'data') ws[ref].s = c >= 1 ? OPT_XLSX_STYLES.num : OPT_XLSX_STYLES.cell;
    }
  }
  return ws;
}

// CMAs sheet from /data asset_classes (decimals). Fees shown in bps to match the
// app's fee-budget convention.
function buildCMAsSheetFromData(assetClasses) {
  const headers = ['Asset Class', 'Type', 'Return (%)', 'Volatility (%)', 'Mgmt Fee (bps)'];
  const aoa = [headers];
  (assetClasses || []).forEach((ac) => {
    const type = ac.is_overlay ? 'Overlay' : ac.is_growth ? 'Growth' : 'Defensive';
    aoa.push([
      ac.code, type,
      parseFloat((ac.annual_return * 100).toFixed(2)),
      parseFloat((ac.annual_volatility * 100).toFixed(2)),
      ac.mgmt_fee != null ? Math.round(ac.mgmt_fee * 10000) : '',
    ]);
  });
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  styleHeaderRow(ws, headers.length, [18, 12, 14, 14, 16]);
  for (let r = 1; r < aoa.length; r++) {
    for (let c = 0; c < headers.length; c++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (ws[ref]) ws[ref].s = (c === 2 || c === 3) ? OPT_XLSX_STYLES.num : OPT_XLSX_STYLES.cell;
    }
  }
  return ws;
}

// Correlation matrix sheet from a {code: {code: value}} object.
function buildCorrSheet(corrMatrix, acCodes) {
  const corrCodes = (acCodes || []).filter((c) => corrMatrix && corrMatrix[c]);
  const headers = ['', ...corrCodes];
  const aoa = [headers];
  corrCodes.forEach((rowCode) => {
    const row = [rowCode];
    corrCodes.forEach((colCode) => {
      const v = corrMatrix[rowCode]?.[colCode];
      row.push(typeof v === 'number' ? parseFloat(v.toFixed(4)) : '');
    });
    aoa.push(row);
  });
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  styleHeaderRow(ws, headers.length, [14, ...corrCodes.map(() => 10)]);
  for (let r = 1; r < aoa.length; r++) {
    for (let c = 0; c < headers.length; c++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (!ws[ref]) continue;
      ws[ref].s = c === 0 ? OPT_XLSX_STYLES.section : OPT_XLSX_STYLES.num4;
    }
  }
  return ws;
}

// Constraints sheet from a saved batch run's `inputs`.
function buildBatchConstraintsSheet(inputs, profiles) {
  const g = inputs.batch_profile_growth || {};
  const gmin = inputs.batch_profile_growth_min || {};
  const gmax = inputs.batch_profile_growth_max || {};
  const fee = inputs.batch_profile_fee || {};
  const bounds = inputs.batch_profile_bounds || {};

  const aoa = [['Section', 'Profile', 'Detail', 'Value 1', 'Value 2']];
  aoa.push(['Objective', '', inputs.batch_objective || '', '', '']);
  aoa.push(['Diversification λ', '', inputs.batch_diversification_lambda ?? '', '', '']);
  aoa.push([]);
  aoa.push(['Growth / Fee', 'Profile', 'Growth Target %', 'Growth Min/Max %', 'Fee Budget (bps)']);
  profiles.forEach((p) => {
    const mm = (gmin[p] != null || gmax[p] != null) ? `${gmin[p] ?? ''} / ${gmax[p] ?? ''}` : '';
    aoa.push(['Growth / Fee', p, g[p] ?? '', mm, fee[p] ?? '']);
  });
  aoa.push([]);
  aoa.push(['Per-SAC Bounds', 'Profile', 'SAC', 'Mode / Min %', 'Max %']);
  profiles.forEach((p) => {
    const cells = bounds[p] || {};
    Object.entries(cells).forEach(([sac, cell]) => {
      const mode = cell.mode || 'absolute';
      const minLabel = mode === 'absolute' ? (cell.min ?? '') : `${mode}: ${cell.min ?? ''}`;
      aoa.push(['Per-SAC Bounds', p, sac, minLabel, cell.max ?? '']);
    });
  });
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  styleHeaderRow(ws, 5, [16, 12, 18, 18, 16]);
  return ws;
}

// Run metadata sheet.
function buildRunSummarySheet(meta) {
  const aoa = [['Parameter', 'Value']];
  aoa.push(['Run Label', meta.label || '']);
  aoa.push(['Run Name', meta.name || '']);
  aoa.push(['Description', meta.description || '']);
  aoa.push(['CMA Scenario', meta.scenario_name || (meta.scenario_id != null ? `id=${meta.scenario_id}` : 'Default')]);
  aoa.push(['Scenario ID', meta.scenario_id ?? '(default)']);
  aoa.push(['Objective', meta.objective || '']);
  aoa.push(['Diversification λ', meta.diversification_lambda ?? '']);
  aoa.push(['Profiles', (meta.profiles || []).join(', ')]);
  aoa.push(['Saved By', meta.created_by_email || '']);
  aoa.push(['Saved At', meta.created_at || '']);
  aoa.push(['CMAs / Correlation', 'As at export time (live scenario data)']);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  styleHeaderRow(ws, 2, [22, 48]);
  return ws;
}

// Risk-contribution sheet (only when at least one profile has them).
function buildBatchRiskContribSheet(profiles, batchResults, acCodes) {
  const headers = ['Asset Class'];
  profiles.forEach((p) => headers.push(`${p} MCTR`, `${p} CCTR`, `${p} PCTR (%)`));
  const aoa = [headers];
  acCodes.forEach((code) => {
    const row = [code];
    profiles.forEach((p) => {
      const rc = batchResults[p]?.risk_contributions?.[code];
      row.push(rc ? rc.mctr : '');
      row.push(rc ? rc.cctr : '');
      row.push(rc ? roundHalf(rc.pctr * 100) : '');
    });
    aoa.push(row);
  });
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  styleHeaderRow(ws, headers.length, [18, ...profiles.flatMap(() => [12, 12, 12])]);
  for (let r = 1; r < aoa.length; r++) {
    for (let c = 1; c < headers.length; c++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (ws[ref]) ws[ref].s = OPT_XLSX_STYLES.num4;
    }
    const codeRef = XLSX.utils.encode_cell({ r, c: 0 });
    if (ws[codeRef]) ws[codeRef].s = OPT_XLSX_STYLES.cell;
  }
  return ws;
}

const FrontierTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="frontier-tooltip">
      <div>Return: {(d.ret * 100).toFixed(2)}%</div>
      <div>Volatility: {(d.vol * 100).toFixed(2)}%</div>
      <div>Sharpe: {d.sharpe.toFixed(3)}</div>
    </div>
  );
};

const PortfolioOptimiserApp = () => {
  const [assetClasses, setAssetClasses] = useState([]);
  const [corrMatrix, setCorrMatrix] = useState({});
  const [overrides, setOverrides] = useState({});
  const [growthMode, setGrowthMode] = useState('exact');
  const [growthTarget, setGrowthTarget] = useState(70);
  const [growthMin, setGrowthMin] = useState(50);
  const [growthMax, setGrowthMax] = useState(80);
  const [activePreset, setActivePreset] = useState(null);
  const [acConstraints, setAcConstraints] = useState({});
  const [showConstraints, setShowConstraints] = useState(false);

  // Saved asset-constraint sets (persisted to RDS via /saved-configs)
  const [savedConstraintSets, setSavedConstraintSets] = useState({});
  const [activeConstraintSet, setActiveConstraintSet] = useState('');
  const [loading, setLoading] = useState(true);
  const [optimising, setOptimising] = useState(false);
  const [error, setError] = useState(null);

  // Results for each objective
  const [results, setResults] = useState({}); // keyed by objective name
  const [frontierData, setFrontierData] = useState(null);

  // Objectives toggle
  const [activeObjectives, setActiveObjectives] = useState({
    max_sharpe: true,
    min_vol: true,
    risk_parity: false,
    hrp: false,
    target_return: false,
    target_vol: false,
  });
  const [targetReturnValue, setTargetReturnValue] = useState(6.0);
  const [targetVolValue, setTargetVolValue] = useState(8.0);
  const [diversificationLambda, setDiversificationLambda] = useState(0);

  // Scenario freeze
  const [frozenScenarios, setFrozenScenarios] = useState([]);
  const [scenarioName, setScenarioName] = useState('');
  const [showScenarioInput, setShowScenarioInput] = useState(false);

  // Risk contributions panel
  const [showRiskContribs, setShowRiskContribs] = useState(false);

  // CMA scenario selector (admin only)
  const [cmaScenarios, setCmaScenarios] = useState([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState(null);

  // Portfolio registration modal
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerName, setRegisterName] = useState('');
  const [registerCode, setRegisterCode] = useState('');
  const [registerObj, setRegisterObj] = useState(null);
  const [registering, setRegistering] = useState(false);

  // Batch mode (primary tab — batch is the typical workflow)
  const [activeTab, setActiveTab] = useState('batch');
  const [batchProfiles, setBatchProfiles] = useState(
    Object.fromEntries(ALL_DSP_PROFILES.map((p) => [p, true]))
  );
  const [batchObjective, setBatchObjective] = useState('max_sharpe');
  const [batchDiversificationLambda, setBatchDiversificationLambda] = useState(0);
  const [batchResults, setBatchResults] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  // Per-profile constraint-set assignments: { profileName: savedSetName }
  // Empty / "" means use the profile's DSP defaults.
  // Per-profile growth target override (% as string for input control)
  // '' = inherit DSP default. Kept for backwards compatibility with older saved runs.
  const [batchProfileGrowth, setBatchProfileGrowth] = useState({});
  // Per-profile growth range overrides (min/max % as strings). Either or both
  // can be set. If both set and equal, treated as exact; if both set and
  // different, treated as a range; if only one set, treated as that bound only.
  const [batchProfileGrowthMin, setBatchProfileGrowthMin] = useState({});
  const [batchProfileGrowthMax, setBatchProfileGrowthMax] = useState({});
  // Per-profile fee budget override (bps as string for input control)
  // '' = inherit DSP default
  const [batchProfileFee, setBatchProfileFee] = useState({});
  // Per-profile per-SAC bounds: { [profile]: { [sac]: { min: '', max: '' } } }
  // Min/max values stored as strings (% units). Empty = no bound on that side.
  const [batchProfileBounds, setBatchProfileBounds] = useState({});

  // Saved batch templates (whole matrix config) — persisted to RDS via /saved-configs
  const [savedBatchTemplates, setSavedBatchTemplates] = useState({});
  const [activeBatchTemplate, setActiveBatchTemplate] = useState('');
  // True when the matrix has been edited since the active template was loaded.
  // Tracked separately so Save still knows what to overwrite.
  const [batchTemplateDirty, setBatchTemplateDirty] = useState(false);
  // Collapsible per-SAC section

  // Saved-runs (DB-backed)
  const [savedRuns, setSavedRuns] = useState([]);
  const [savedRunsLoading, setSavedRunsLoading] = useState(false);
  const [showSavedRunsModal, setShowSavedRunsModal] = useState(false);
  const [savedRunsFavOnly, setSavedRunsFavOnly] = useState(false);
  const [savedRunsMineOnly, setSavedRunsMineOnly] = useState(false);
  const [showSaveRunModal, setShowSaveRunModal] = useState(false);
  const [saveRunName, setSaveRunName] = useState('');
  const [saveRunDescription, setSaveRunDescription] = useState('');
  const [saveRunFavourite, setSaveRunFavourite] = useState(false);
  const [savingRun, setSavingRun] = useState(false);
  // Tracks the currently-loaded saved run (if any) so "Save" can overwrite it
  // in place; "Save As" always opens the modal to create a new run.
  const [loadedRunId, setLoadedRunId] = useState(null);
  const [loadedRunName, setLoadedRunName] = useState('');
  // Whether the currently loaded run may be overwritten in place (false when it's
  // locked, or read-only and owned by someone else). Gates the "Save" button.
  const [loadedRunCanEdit, setLoadedRunCanEdit] = useState(true);

  // Batch Excel export picker — multi-select saved batch runs, then build one
  // workbook with full scenario / CMA / correlation / constraints / results
  // detail per run plus a cross-run comparison sheet.
  const [showBatchExportPicker, setShowBatchExportPicker] = useState(false);
  const [batchExportChecked, setBatchExportChecked] = useState({});
  const [batchExportLoading, setBatchExportLoading] = useState(false);

  // Blend Batches tab — weight-averages SAC vectors across multiple saved
  // batch runs. blendSelectedRuns: [{ run_id, name, weight }, ...]
  // blendResults: same shape as batchResults (Dict[profile, OptimiseResult]).
  const [blendSelectedRuns, setBlendSelectedRuns] = useState([]);
  const [blendResults, setBlendResults] = useState(null);
  const [blendSource, setBlendSource] = useState([]);   // echoed back from server, used for re-save
  const [blendPerProfileSources, setBlendPerProfileSources] = useState({});  // {profile: {source_name: {sac: weight}}}
  const [blendComparisonProfile, setBlendComparisonProfile] = useState('');  // active profile in the drill-down dropdown
  // Historic SAA comparison in the Source-batch SAC comparison table.
  // historicSAA: full /historic-saa response; selectedExistingSAA: "blender_code__FY".
  const [historicSAA, setHistoricSAA] = useState(null);
  const [selectedExistingSAA, setSelectedExistingSAA] = useState('');
  const [blendLoading, setBlendLoading] = useState(false);
  const [blendError, setBlendError] = useState(null);
  const [showBlendPicker, setShowBlendPicker] = useState(false);
  // Multi-select state for the picker modal: { [run_id]: true }
  const [blendPickerChecked, setBlendPickerChecked] = useState({});

  const loadCmaData = useCallback((scenarioId) => {
    setLoading(true);
    const params = scenarioId ? `?scenario_id=${scenarioId}` : '';
    api.get(`/api/optimiser/data${params}`)
      .then((res) => {
        setAssetClasses(res.data.asset_classes);
        setCorrMatrix(res.data.correlation_matrix);
        setLoading(false);
      })
      .catch((err) => {
        setError('Failed to load CMA data: ' + (err.response?.data?.detail || err.message));
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    trackActivity('app_access', 'portfolio_optimiser');
    loadCmaData(selectedScenarioId);
    // Load available CMA scenarios for admin dropdown
    api.get('/api/investment-config/cma/scenarios')
      .then((res) => setCmaScenarios(res.data.scenarios || []))
      .catch(() => {}); // Non-admin users won't have access, silently ignore
  }, []);

  // Refresh assetClasses when the user returns to this tab — picks up new SACs
  // that were added in the CMA admin while we were elsewhere.
  useEffect(() => {
    const handleFocus = () => {
      if (document.visibilityState === 'visible') {
        loadCmaData(selectedScenarioId);
      }
    };
    document.addEventListener('visibilitychange', handleFocus);
    return () => document.removeEventListener('visibilitychange', handleFocus);
  }, [selectedScenarioId, loadCmaData]);

  const handleScenarioChange = useCallback((newId) => {
    setSelectedScenarioId(newId);
    setResults({});
    setFrontierData(null);
    loadCmaData(newId);
  }, [loadCmaData]);

  // {name: id} lookup so save/delete can target the right row by ID.
  const [constraintSetIds, setConstraintSetIds] = useState({});

  // Load saved constraint sets from RDS on mount
  useEffect(() => {
    (async () => {
      try {
        const rows = await adminAPI.listOptimiserConfigs('constraint_set');
        const byName = {};
        const ids = {};
        for (const r of rows || []) {
          byName[r.name] = r.payload || {};
          ids[r.name] = r.id;
        }
        setSavedConstraintSets(byName);
        setConstraintSetIds(ids);
      } catch (err) {
        console.error('Failed to load constraint sets from RDS:', err);
      }
    })();
  }, []);

  // Save: overwrite the currently loaded constraint set. If nothing is loaded,
  // falls back to Save As (prompts for a name).
  const handleSaveConstraintSet = useCallback(async () => {
    let name = activeConstraintSet;
    if (!name) {
      name = (window.prompt('Name this asset-constraint set:') || '').trim();
      if (!name) return;
    }
    try {
      const id = constraintSetIds[name];
      const row = id
        ? await adminAPI.updateOptimiserConfig(id, { name, payload: acConstraints })
        : await adminAPI.createOptimiserConfig({
            kind: 'constraint_set', name, payload: acConstraints,
          });
      setSavedConstraintSets((prev) => ({ ...prev, [name]: row.payload || acConstraints }));
      setConstraintSetIds((prev) => ({ ...prev, [name]: row.id }));
      setActiveConstraintSet(name);
    } catch (err) {
      setError('Failed to save constraint set: ' + (err.response?.data?.detail || err.message));
    }
  }, [activeConstraintSet, constraintSetIds, acConstraints]);

  // Save As: always prompt for a new name (pre-filled with "<loaded> (copy)").
  const handleSaveAsConstraintSet = useCallback(async () => {
    const proposed = activeConstraintSet ? `${activeConstraintSet} (copy)` : '';
    const name = (window.prompt('Save as new asset-constraint set — name it:', proposed) || '').trim();
    if (!name) return;
    if (savedConstraintSets[name] && !window.confirm(`A set named "${name}" already exists. Overwrite?`)) {
      return;
    }
    try {
      const row = await adminAPI.createOptimiserConfig({
        kind: 'constraint_set', name, payload: acConstraints,
      });
      setSavedConstraintSets((prev) => ({ ...prev, [name]: row.payload || acConstraints }));
      setConstraintSetIds((prev) => ({ ...prev, [name]: row.id }));
      setActiveConstraintSet(name);
    } catch (err) {
      setError('Failed to save constraint set: ' + (err.response?.data?.detail || err.message));
    }
  }, [activeConstraintSet, savedConstraintSets, acConstraints]);

  const handleLoadConstraintSet = useCallback((name) => {
    if (!name) {
      setActiveConstraintSet('');
      return;
    }
    const cfg = savedConstraintSets[name];
    if (!cfg) return;
    // Deep-copy so subsequent edits don't mutate the saved set
    setAcConstraints(Object.fromEntries(Object.entries(cfg).map(([k, v]) => [k, { ...v }])));
    setActiveConstraintSet(name);
    setActivePreset(null);
    setShowConstraints(true);
  }, [savedConstraintSets]);

  // ── Single-tab: import per-profile bounds from a saved Batch template ─
  const [showImportBatchProfile, setShowImportBatchProfile] = useState(false);
  const [importBatchTemplateName, setImportBatchTemplateName] = useState('');
  const [importBatchProfileName, setImportBatchProfileName] = useState('');

  // Translate a batch-tab profile bounds dict to the single-tab acConstraints
  // schema. Returns { acConstraints, skipped, growthMode, growthTarget, growthMin, growthMax, feeBudget }.
  const translateBatchProfileToConstraints = useCallback((templateName, profileName) => {
    const template = savedBatchTemplates[templateName];
    if (!template) return null;
    const bounds = (template.batchProfileBounds || {})[profileName] || {};
    const out = {};
    let skipped = 0;
    for (const [sac, cell] of Object.entries(bounds)) {
      if (!cell) continue;
      const mode = cell.mode || 'absolute';
      const minRaw = cell.min;
      const maxRaw = cell.max;
      const minIsBlank = minRaw === '' || minRaw == null;
      const maxIsBlank = maxRaw === '' || maxRaw == null;
      if (minIsBlank && maxIsBlank) { skipped += 1; continue; }
      const minN = minIsBlank ? 0 : Number(minRaw);
      const maxN = maxIsBlank ? 100 : Number(maxRaw);
      if (mode === 'absolute') {
        out[sac] = {
          mode: 'absolute',
          absMin: Number.isFinite(minN) ? minN : 0,
          absMax: Number.isFinite(maxN) ? maxN : 100,
          reference: 'growth',
          relMin: 0,
          relMax: 100,
        };
      } else {
        // 'growth' | 'defensive' | 'intl_equity'
        out[sac] = {
          mode: 'relative',
          absMin: 0,
          absMax: 100,
          reference: mode,
          relMin: Number.isFinite(minN) ? minN : 0,
          relMax: Number.isFinite(maxN) ? maxN : 100,
        };
      }
    }
    // Growth target + fee budget passthrough (profile-level overrides on the batch tab).
    const gt = (template.batchProfileGrowth || {})[profileName];
    const gmin = (template.batchProfileGrowthMin || {})[profileName];
    const gmax = (template.batchProfileGrowthMax || {})[profileName];
    const fee = (template.batchProfileFee || {})[profileName];
    let growthMode = null, growthTarget = null, growthMin = null, growthMax = null;
    if (gt !== '' && gt != null && !Number.isNaN(Number(gt))) {
      growthMode = 'exact';
      growthTarget = Number(gt);
    } else if ((gmin !== '' && gmin != null) || (gmax !== '' && gmax != null)) {
      growthMode = 'range';
      growthMin = (gmin === '' || gmin == null) ? null : Number(gmin);
      growthMax = (gmax === '' || gmax == null) ? null : Number(gmax);
    }
    const feeBudget = (fee === '' || fee == null) ? null : Number(fee);
    return { acConstraints: out, skipped, growthMode, growthTarget, growthMin, growthMax, feeBudget };
  }, [savedBatchTemplates]);

  const handleImportBatchProfileConfirm = useCallback(() => {
    if (!importBatchTemplateName || !importBatchProfileName) return;
    const result = translateBatchProfileToConstraints(importBatchTemplateName, importBatchProfileName);
    if (!result) return;
    setAcConstraints(result.acConstraints);
    setActiveConstraintSet('');  // imported set is not (yet) a named saved set
    setActivePreset(null);
    setShowConstraints(true);
    if (result.growthMode === 'exact') {
      setGrowthMode('exact');
      if (Number.isFinite(result.growthTarget)) setGrowthTarget(result.growthTarget);
    } else if (result.growthMode === 'range') {
      setGrowthMode('range');
      if (Number.isFinite(result.growthMin)) setGrowthMin(result.growthMin);
      if (Number.isFinite(result.growthMax)) setGrowthMax(result.growthMax);
    }
    setShowImportBatchProfile(false);
  }, [importBatchTemplateName, importBatchProfileName, translateBatchProfileToConstraints]);

  const handleDeleteConstraintSet = useCallback(async () => {
    if (!activeConstraintSet) return;
    if (!window.confirm(`Delete the saved set "${activeConstraintSet}"?`)) return;
    const id = constraintSetIds[activeConstraintSet];
    try {
      if (id) await adminAPI.deleteOptimiserConfig(id);
      setSavedConstraintSets((prev) => {
        const next = { ...prev }; delete next[activeConstraintSet]; return next;
      });
      setConstraintSetIds((prev) => {
        const next = { ...prev }; delete next[activeConstraintSet]; return next;
      });
      setActiveConstraintSet('');
    } catch (err) {
      setError('Failed to delete constraint set: ' + (err.response?.data?.detail || err.message));
    }
  }, [activeConstraintSet, constraintSetIds]);

  // ── Batch templates (whole matrix config) ─────────────────────────────
  const [batchTemplateIds, setBatchTemplateIds] = useState({});

  // Load saved batch templates from RDS on mount. If "DSP 2026 Defaults"
  // doesn't exist yet, seed it via the API so every environment has the
  // same baseline template available.
  useEffect(() => {
    (async () => {
      try {
        const rows = await adminAPI.listOptimiserConfigs('batch_template');
        const byName = {};
        const ids = {};
        for (const r of rows || []) {
          byName[r.name] = r.payload || {};
          ids[r.name] = r.id;
        }
        if (!byName[DSP_2026_TEMPLATE_NAME]) {
          const seed = buildDsp2026Template();
          try {
            const seeded = await adminAPI.createOptimiserConfig({
              kind: 'batch_template', name: DSP_2026_TEMPLATE_NAME, payload: seed,
            });
            byName[DSP_2026_TEMPLATE_NAME] = seeded.payload || seed;
            ids[DSP_2026_TEMPLATE_NAME] = seeded.id;
          } catch (seedErr) {
            console.warn('Could not seed DSP 2026 Defaults template:', seedErr);
            byName[DSP_2026_TEMPLATE_NAME] = seed;
          }
        }
        setSavedBatchTemplates(byName);
        setBatchTemplateIds(ids);
      } catch (err) {
        console.error('Failed to load batch templates from RDS:', err);
      }
    })();
  }, []);

  const snapshotBatchMatrix = useCallback(() => ({
    batchProfiles,
    batchObjective,
    batchProfileGrowth,
    batchProfileGrowthMin,
    batchProfileGrowthMax,
    batchProfileFee,
    batchProfileBounds,
  }), [
    batchProfiles, batchObjective,
    batchProfileGrowth, batchProfileGrowthMin, batchProfileGrowthMax,
    batchProfileFee, batchProfileBounds,
  ]);

  // Save: overwrite the currently loaded template. If nothing is loaded, falls
  // back to Save As (prompts for a name).
  const handleSaveBatchTemplate = useCallback(async () => {
    let name = activeBatchTemplate;
    if (!name) {
      name = (window.prompt('Name this batch template:') || '').trim();
      if (!name) return;
    }
    const payload = snapshotBatchMatrix();
    try {
      const id = batchTemplateIds[name];
      const row = id
        ? await adminAPI.updateOptimiserConfig(id, { name, payload })
        : await adminAPI.createOptimiserConfig({
            kind: 'batch_template', name, payload,
          });
      setSavedBatchTemplates((prev) => ({ ...prev, [name]: row.payload || payload }));
      setBatchTemplateIds((prev) => ({ ...prev, [name]: row.id }));
      setActiveBatchTemplate(name);
      setBatchTemplateDirty(false);
    } catch (err) {
      setError('Failed to save batch template: ' + (err.response?.data?.detail || err.message));
    }
  }, [activeBatchTemplate, batchTemplateIds, snapshotBatchMatrix]);

  // Save As: always prompt for a new name (pre-filled with "<loaded> (copy)").
  const handleSaveAsBatchTemplate = useCallback(async () => {
    const proposed = activeBatchTemplate ? `${activeBatchTemplate} (copy)` : '';
    const name = (window.prompt('Save as new batch template — name it:', proposed) || '').trim();
    if (!name) return;
    if (savedBatchTemplates[name] && !window.confirm(`A template named "${name}" already exists. Overwrite?`)) {
      return;
    }
    const payload = snapshotBatchMatrix();
    try {
      const row = await adminAPI.createOptimiserConfig({
        kind: 'batch_template', name, payload,
      });
      setSavedBatchTemplates((prev) => ({ ...prev, [name]: row.payload || payload }));
      setBatchTemplateIds((prev) => ({ ...prev, [name]: row.id }));
      setActiveBatchTemplate(name);
      setBatchTemplateDirty(false);
    } catch (err) {
      setError('Failed to save batch template: ' + (err.response?.data?.detail || err.message));
    }
  }, [activeBatchTemplate, savedBatchTemplates, snapshotBatchMatrix]);

  const handleLoadBatchTemplate = useCallback((name) => {
    if (!name) { setActiveBatchTemplate(''); setBatchTemplateDirty(false); return; }
    const t = savedBatchTemplates[name];
    if (!t) return;
    if (t.batchProfiles) setBatchProfiles(t.batchProfiles);
    if (t.batchObjective) setBatchObjective(t.batchObjective);
    if (t.batchProfileGrowth) setBatchProfileGrowth(t.batchProfileGrowth);
    if (t.batchProfileGrowthMin) setBatchProfileGrowthMin(t.batchProfileGrowthMin);
    if (t.batchProfileGrowthMax) setBatchProfileGrowthMax(t.batchProfileGrowthMax);
    if (t.batchProfileFee) setBatchProfileFee(t.batchProfileFee);
    if (t.batchProfileBounds) setBatchProfileBounds(t.batchProfileBounds);
    setActiveBatchTemplate(name);
    setBatchTemplateDirty(false);
  }, [savedBatchTemplates]);

  const handleDeleteBatchTemplate = useCallback(async () => {
    if (!activeBatchTemplate) return;
    if (!window.confirm(`Delete batch template "${activeBatchTemplate}"?`)) return;
    const id = batchTemplateIds[activeBatchTemplate];
    try {
      if (id) await adminAPI.deleteOptimiserConfig(id);
      setSavedBatchTemplates((prev) => {
        const next = { ...prev }; delete next[activeBatchTemplate]; return next;
      });
      setBatchTemplateIds((prev) => {
        const next = { ...prev }; delete next[activeBatchTemplate]; return next;
      });
      setActiveBatchTemplate('');
      setBatchTemplateDirty(false);
    } catch (err) {
      setError('Failed to delete batch template: ' + (err.response?.data?.detail || err.message));
    }
  }, [activeBatchTemplate, batchTemplateIds]);

  // Rebuild + persist the pre-seeded DSP 2026 template (mirrors the script's
  // constraints). Writes the fresh blob back to RDS so other browsers also
  // pick up the reset.
  const handleResetDsp2026Template = useCallback(async () => {
    const fresh = buildDsp2026Template();
    try {
      const id = batchTemplateIds[DSP_2026_TEMPLATE_NAME];
      const row = id
        ? await adminAPI.updateOptimiserConfig(id, {
            name: DSP_2026_TEMPLATE_NAME, payload: fresh,
          })
        : await adminAPI.createOptimiserConfig({
            kind: 'batch_template', name: DSP_2026_TEMPLATE_NAME, payload: fresh,
          });
      setSavedBatchTemplates((prev) => ({ ...prev, [DSP_2026_TEMPLATE_NAME]: row.payload || fresh }));
      setBatchTemplateIds((prev) => ({ ...prev, [DSP_2026_TEMPLATE_NAME]: row.id }));
    } catch (err) {
      setError('Failed to reset DSP 2026 template: ' + (err.response?.data?.detail || err.message));
      // Even if the API failed, fall back to in-memory so the user can use it.
      setSavedBatchTemplates((prev) => ({ ...prev, [DSP_2026_TEMPLATE_NAME]: fresh }));
    }
    if (fresh.batchProfiles) setBatchProfiles(fresh.batchProfiles);
    if (fresh.batchObjective) setBatchObjective(fresh.batchObjective);
    if (fresh.batchProfileGrowth) setBatchProfileGrowth(fresh.batchProfileGrowth);
    if (fresh.batchProfileGrowthMin) setBatchProfileGrowthMin(fresh.batchProfileGrowthMin);
    if (fresh.batchProfileGrowthMax) setBatchProfileGrowthMax(fresh.batchProfileGrowthMax);
    if (fresh.batchProfileFee) setBatchProfileFee(fresh.batchProfileFee);
    if (fresh.batchProfileBounds) setBatchProfileBounds(fresh.batchProfileBounds);
    setActiveBatchTemplate(DSP_2026_TEMPLATE_NAME);
    setBatchTemplateDirty(false);
  }, [batchTemplateIds]);

  // When user changes the matrix manually, mark the active template as dirty.
  // The active template name is preserved so Save still knows what to overwrite.
  const markBatchTemplateDirty = useCallback(() => {
    setBatchTemplateDirty(true);
  }, []);

  // Update a single bound cell
  const setBoundCell = useCallback((profile, sac, side, value) => {
    setBatchProfileBounds((prev) => ({
      ...prev,
      [profile]: {
        ...(prev[profile] || {}),
        [sac]: { ...((prev[profile] || {})[sac] || {}), [side]: value },
      },
    }));
    markBatchTemplateDirty();
  }, [markBatchTemplateDirty]);

  const handlePreset = useCallback((key) => {
    setActivePreset(key);

    if (key === 'default_dsp') {
      setGrowthMode('exact');
      setGrowthTarget(70);
      setAcConstraints(Object.fromEntries(
        Object.entries(DSP_AC_CONSTRAINTS).map(([k, v]) => [k, { ...v }])
      ));
      setShowConstraints(true);
      return;
    }

    if (key === 'unconstrained') {
      setAcConstraints({});
      setGrowthMode('none');
    }
  }, []);

  const handleConstraintChange = useCallback((code, field, value) => {
    setAcConstraints((prev) => {
      const existing = prev[code] || { ...DEFAULT_CONSTRAINT };
      const parsed = (field === 'mode' || field === 'reference') ? value : (value === '' ? 0 : parseFloat(value));
      return { ...prev, [code]: { ...existing, [field]: parsed } };
    });
    setActivePreset(null);
    setActiveConstraintSet('');
  }, []);

  const toggleObjective = useCallback((key) => {
    setActiveObjectives((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const buildRequest = useCallback((objective) => {
    const asset_overrides = {};
    for (const [code, ov] of Object.entries(overrides)) {
      if (ov.annual_return !== null || ov.annual_volatility !== null) {
        asset_overrides[code] = {
          annual_return: ov.annual_return != null ? ov.annual_return / 100 : null,
          annual_volatility: ov.annual_volatility != null ? ov.annual_volatility / 100 : null,
        };
      }
    }

    const ab = {};
    const rc = [];
    for (const [code, cfg] of Object.entries(acConstraints)) {
      if (cfg.mode === 'absolute') {
        ab[code] = { min: (cfg.absMin || 0) / 100, max: (cfg.absMax ?? 100) / 100 };
      } else if (cfg.mode === 'relative') {
        if (cfg.relMin > 0) {
          rc.push({ assets: [code], type: 'min_pct_of', reference: cfg.reference, value: cfg.relMin / 100 });
        }
        if (cfg.relMax < 100) {
          rc.push({ assets: [code], type: 'max_pct_of', reference: cfg.reference, value: cfg.relMax / 100 });
        }
        // If max == 0, clamp the absolute bound to {0, 0} so the asset is
        // forced to zero regardless of any inherited lower bound from a saved
        // set or DSP defaults (otherwise the relative max=0 and an absolute
        // min>0 conflict and SLSQP falls back to the absolute bound).
        if (cfg.relMax === 0) {
          ab[code] = { min: 0, max: 0 };
        }
      }
    }

    const constraints = {
      asset_bounds: ab,
      fee_budget: null,
      relative_constraints: rc,
      target_return: null,
      target_vol: null,
    };

    if (growthMode === 'none') {
      constraints.growth_target = null;
      constraints.growth_min = null;
      constraints.growth_max = null;
    } else if (growthMode === 'exact') {
      constraints.growth_target = growthTarget / 100;
      constraints.growth_min = null;
      constraints.growth_max = null;
    } else {
      constraints.growth_target = null;
      constraints.growth_min = growthMin / 100;
      constraints.growth_max = growthMax / 100;
    }

    if (objective === 'target_return') {
      constraints.target_return = targetReturnValue / 100;
    }
    if (objective === 'target_vol') {
      constraints.target_vol = targetVolValue / 100;
    }

    const req = { asset_overrides, constraints, objective, diversification_lambda: diversificationLambda };
    if (selectedScenarioId) req.scenario_id = selectedScenarioId;
    return req;
  }, [overrides, acConstraints, growthMode, growthTarget, growthMin, growthMax, targetReturnValue, targetVolValue, selectedScenarioId, diversificationLambda]);

  const handleOptimise = useCallback(async () => {
    setOptimising(true);
    setError(null);
    setResults({});
    setFrontierData(null);

    const enabledObjectives = Object.entries(activeObjectives)
      .filter(([, v]) => v)
      .map(([k]) => k);

    if (enabledObjectives.length === 0) {
      setError('Select at least one objective.');
      setOptimising(false);
      return;
    }

    try {
      const promises = enabledObjectives.map((obj) =>
        api.post('/api/optimiser/optimise', buildRequest(obj))
          .then((res) => ({ key: obj, data: res.data }))
      );
      // Also fetch frontier
      promises.push(
        api.post('/api/optimiser/frontier', buildRequest('min_vol'))
          .then((res) => ({ key: '_frontier', data: res.data }))
          .catch(() => ({ key: '_frontier', data: null }))
      );

      const settled = await Promise.all(promises);
      const newResults = {};
      for (const { key, data } of settled) {
        if (key === '_frontier') {
          setFrontierData(data);
        } else {
          newResults[key] = data;
        }
      }
      setResults(newResults);
    } catch (err) {
      setError('Optimisation failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setOptimising(false);
    }
  }, [buildRequest, activeObjectives]);

  const handleSaveScenario = useCallback(() => {
    if (!scenarioName.trim() || frozenScenarios.length >= 5) return;
    setFrozenScenarios((prev) => [
      ...prev,
      {
        name: scenarioName.trim(),
        results: { ...results },
        frontierData,
        timestamp: Date.now(),
      },
    ]);
    setScenarioName('');
    setShowScenarioInput(false);
  }, [scenarioName, results, frontierData, frozenScenarios]);

  const handleRemoveScenario = useCallback((idx) => {
    setFrozenScenarios((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  // Convert an acConstraints-shaped object (per-asset cfg with mode/abs*/rel*/reference)
  // into the backend Constraints shape ({asset_bounds, relative_constraints}).
  const acConstraintsToPayload = useCallback((acCfg) => {
    const ab = {};
    const rc = [];
    for (const [code, cfg] of Object.entries(acCfg || {})) {
      if (cfg.mode === 'absolute') {
        ab[code] = { min: (cfg.absMin || 0) / 100, max: (cfg.absMax ?? 100) / 100 };
      } else if (cfg.mode === 'relative') {
        if (cfg.relMin > 0) {
          rc.push({ assets: [code], type: 'min_pct_of', reference: cfg.reference, value: cfg.relMin / 100 });
        }
        if (cfg.relMax < 100) {
          rc.push({ assets: [code], type: 'max_pct_of', reference: cfg.reference, value: cfg.relMax / 100 });
        }
        // If max == 0, clamp the absolute bound to {0, 0} so the asset is
        // forced to zero regardless of any inherited lower bound from a saved
        // set or DSP defaults (otherwise the relative max=0 and an absolute
        // min>0 conflict and SLSQP falls back to the absolute bound).
        if (cfg.relMax === 0) {
          ab[code] = { min: 0, max: 0 };
        }
      }
    }
    return { asset_bounds: ab, relative_constraints: rc };
  }, []);

  // Batch mode
  const handleRunBatch = useCallback(async () => {
    setBatchLoading(true);
    setError(null);
    setBatchResults(null);

    const selectedProfiles = ALL_DSP_PROFILES.filter((p) => batchProfiles[p]);
    if (selectedProfiles.length === 0) {
      setError('Select at least one profile.');
      setBatchLoading(false);
      return;
    }

    const asset_overrides = {};
    for (const [code, ov] of Object.entries(overrides)) {
      if (ov.annual_return !== null || ov.annual_volatility !== null) {
        asset_overrides[code] = {
          annual_return: ov.annual_return != null ? ov.annual_return / 100 : null,
          annual_volatility: ov.annual_volatility != null ? ov.annual_volatility / 100 : null,
        };
      }
    }

    try {
      // Build per-profile constraint overrides from the matrix assignments.
      // For each profile, asset_bounds start from the loaded saved set (if any),
      // then per-SAC matrix overrides REPLACE individual entries.
      const per_profile_constraints = {};
      const parseNum = (v) => (v !== '' && v != null && !isNaN(parseFloat(v))) ? parseFloat(v) : null;

      for (const profile of selectedProfiles) {
        // All constraints come from per-cell matrix entries.
        const setPayload = { asset_bounds: {}, relative_constraints: [] };

        // Layer per-SAC matrix overrides on top.
        // Each cell has {mode, min, max}. mode = 'absolute' → asset_bounds,
        // mode = 'growth' | 'defensive' | 'intl_equity' → relative_constraints.
        const cellBounds = batchProfileBounds[profile] || {};
        const mergedBounds = { ...setPayload.asset_bounds };
        const mergedRelativeConstraints = [...setPayload.relative_constraints];
        for (const [sac, cell] of Object.entries(cellBounds)) {
          const min = parseNum(cell.min);
          const max = parseNum(cell.max);
          const mode = cell.mode || 'absolute';
          // For absolute mode, skip when both blank (no override to apply).
          // For relative modes, the user has explicitly chosen a non-default
          // reference — push intent markers even if min/max are blank, so the
          // backend sees this SAC is user-touched (defaults: 0 / 100).
          if (min === null && max === null && mode === 'absolute') continue;
          if (mode === 'absolute') {
            const existing = mergedBounds[sac] || { min: 0.0, max: 1.0 };
            mergedBounds[sac] = {
              min: min !== null ? min / 100 : existing.min,
              max: max !== null ? max / 100 : existing.max,
            };
          } else {
            // Relative to growth / defensive / intl_equity.
            // Always push BOTH min and max (treating null as 0 and 1.0) so the
            // backend can identify this SAC as user-touched and strip any DSP
            // default relative constraints involving it. Otherwise "0/100" or
            // "blank" cells silently leave the DSP default in place.
            const effMin = min !== null ? min / 100 : 0;
            const effMax = max !== null ? max / 100 : 1.0;
            mergedRelativeConstraints.push({
              assets: [sac], type: 'min_pct_of', reference: mode, value: effMin,
            });
            mergedRelativeConstraints.push({
              assets: [sac], type: 'max_pct_of', reference: mode, value: effMax,
            });
            // If max == 0, also clamp the absolute bound to {0, 0} so the asset
            // is forced to zero regardless of any inherited lower bound.
            if (max === 0) {
              mergedBounds[sac] = { min: 0, max: 0 };
              for (let i = mergedRelativeConstraints.length - 1; i >= 0; i--) {
                const rci = mergedRelativeConstraints[i];
                if (rci.type === 'min_pct_of' && Array.isArray(rci.assets) && rci.assets.includes(sac)) {
                  mergedRelativeConstraints.splice(i, 1);
                }
              }
            }
          }
        }

        // Growth: prefer min/max range if either set; else fall back to single target
        const gMinPct = parseNum(batchProfileGrowthMin[profile]);
        const gMaxPct = parseNum(batchProfileGrowthMax[profile]);
        const gTargetPct = parseNum(batchProfileGrowth[profile]);
        let growth_target = null, growth_min = null, growth_max = null;
        if (gMinPct !== null && gMaxPct !== null && Math.abs(gMinPct - gMaxPct) < 1e-9) {
          growth_target = gMinPct / 100;
        } else if (gMinPct !== null || gMaxPct !== null) {
          growth_min = gMinPct !== null ? gMinPct / 100 : null;
          growth_max = gMaxPct !== null ? gMaxPct / 100 : null;
        } else if (gTargetPct !== null) {
          growth_target = gTargetPct / 100;
        }

        const feeBpsRaw = parseNum(batchProfileFee[profile]);
        const fee_budget = feeBpsRaw !== null ? feeBpsRaw / 10000 : null;

        const hasAnyOverride = (
          Object.keys(cellBounds).length > 0 ||
          growth_target !== null || growth_min !== null || growth_max !== null ||
          fee_budget !== null
        );
        if (!hasAnyOverride) continue;

        per_profile_constraints[profile] = {
          asset_bounds: mergedBounds,
          relative_constraints: mergedRelativeConstraints,
          growth_target,
          growth_min,
          growth_max,
          fee_budget,
        };
      }

      const batchPayload = {
        asset_overrides,
        profiles: selectedProfiles,
        objective: batchObjective,
        per_profile_constraints,
        diversification_lambda: batchDiversificationLambda,
      };
      console.log('[BATCH] per_profile_constraints:', per_profile_constraints);
      if (selectedScenarioId) batchPayload.scenario_id = selectedScenarioId;
      const res = await api.post('/api/optimiser/batch', batchPayload);
      setBatchResults(res.data.results);
    } catch (err) {
      setError('Batch failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBatchLoading(false);
    }
  }, [batchProfiles, batchObjective, overrides, batchProfileGrowth, batchProfileGrowthMin, batchProfileGrowthMax, batchProfileFee, batchProfileBounds, selectedScenarioId]);

  // ── Blend Batches handlers ─────────────────────────────────────────
  const runBlendBatches = useCallback(async () => {
    setBlendLoading(true);
    setBlendError(null);
    setBlendResults(null);
    try {
      if (blendSelectedRuns.length === 0) {
        throw new Error('Add at least one saved batch before computing a blend.');
      }
      const runs = blendSelectedRuns
        .map((r) => ({ run_id: r.run_id, weight: Number(r.weight) || 0 }))
        .filter((r) => r.weight > 0);
      if (runs.length === 0) {
        throw new Error('Total weight must be greater than zero.');
      }
      const data = await adminAPI.blendOptimiserBatches({
        runs,
        scenario_id: selectedScenarioId || null,
      });
      setBlendResults(data.results || {});
      setBlendSource(data.blend_source || []);
      const sources = data.per_profile_sources || {};
      setBlendPerProfileSources(sources);
      // Default the drill-down dropdown to the first profile in the response.
      const firstProfile = Object.keys(data.results || {})[0] || '';
      setBlendComparisonProfile(firstProfile);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || String(err);
      setBlendError('Blend failed: ' + msg);
    } finally {
      setBlendLoading(false);
    }
  }, [blendSelectedRuns, selectedScenarioId]);

  const addCheckedBlendRuns = useCallback(() => {
    const checkedIds = Object.entries(blendPickerChecked)
      .filter(([, v]) => v)
      .map(([k]) => Number(k));
    if (checkedIds.length === 0) {
      setShowBlendPicker(false);
      return;
    }
    setBlendSelectedRuns((prev) => {
      const existingIds = new Set(prev.map((r) => r.run_id));
      const additions = savedRuns
        .filter((r) => checkedIds.includes(r.id) && !existingIds.has(r.id))
        .map((r) => ({ run_id: r.id, name: r.name, weight: 1 }));
      return [...prev, ...additions];
    });
    setBlendPickerChecked({});
    setShowBlendPicker(false);
  }, [blendPickerChecked, savedRuns]);

  const updateBlendWeight = useCallback((run_id, weight) => {
    setBlendSelectedRuns((prev) => prev.map((r) =>
      r.run_id === run_id ? { ...r, weight } : r
    ));
  }, []);

  const removeBlendRun = useCallback((run_id) => {
    setBlendSelectedRuns((prev) => prev.filter((r) => r.run_id !== run_id));
  }, []);

  const handleCopyProfileSetToAll = useCallback((sourceProfile) => {
    // Copy the per-SAC bounds for sourceProfile across every selected profile.
    // Growth Min/Max and Fee Budget are deliberately NOT copied — those are
    // profile-specific by design.
    const selectedProfiles = ALL_DSP_PROFILES.filter((p) => batchProfiles[p]);
    const boundsValue = batchProfileBounds[sourceProfile] || {};
    const nextBounds = { ...batchProfileBounds };
    selectedProfiles.forEach((p) => {
      // Deep-copy bounds so subsequent edits don't mutate the source row
      nextBounds[p] = Object.fromEntries(
        Object.entries(boundsValue).map(([sac, cell]) => [sac, { ...cell }])
      );
    });
    setBatchProfileBounds(nextBounds);
    markBatchTemplateDirty();
  }, [batchProfileBounds, batchProfiles, markBatchTemplateDirty]);

  // Wipe every per-SAC bound across every profile. Leaves profile-level
  // settings intact (Growth Min/Max, Fee Budget) — only the per-asset
  // min/max/mode cells are cleared.
  const handleResetSacConstraints = useCallback(() => {
    if (!window.confirm('Reset every per-SAC bound across every profile? Growth / Fee Budget rows are untouched.')) return;
    setBatchProfileBounds({});
    markBatchTemplateDirty();
  }, [markBatchTemplateDirty]);

  // Propagate ONE per-SAC row across every selected profile. Source is the
  // first selected profile's cell for that SAC; deep-copied to all others so
  // later edits don't mutate the source.
  const handlePropagateSacAcrossRow = useCallback((sac) => {
    const selectedProfiles = ALL_DSP_PROFILES.filter((p) => batchProfiles[p]);
    if (selectedProfiles.length < 2) return;
    const sourceProfile = selectedProfiles[0];
    const sourceCell = (batchProfileBounds[sourceProfile] || {})[sac] || {};
    const nextBounds = { ...batchProfileBounds };
    selectedProfiles.forEach((p) => {
      nextBounds[p] = {
        ...(nextBounds[p] || {}),
        [sac]: { ...sourceCell },
      };
    });
    setBatchProfileBounds(nextBounds);
    markBatchTemplateDirty();
  }, [batchProfiles, batchProfileBounds, markBatchTemplateDirty]);

  // ── Saved Runs (load/save table backed by DB) ─────────────────────────
  const fetchSavedRuns = useCallback(async () => {
    setSavedRunsLoading(true);
    try {
      const data = await adminAPI.listSavedOptimiserRuns({
        favouritesOnly: savedRunsFavOnly,
        mineOnly: savedRunsMineOnly,
      });
      setSavedRuns(data || []);
    } catch (err) {
      setError('Failed to load saved runs: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavedRunsLoading(false);
    }
  }, [savedRunsFavOnly, savedRunsMineOnly]);

  useEffect(() => {
    if (showSavedRunsModal) fetchSavedRuns();
  }, [showSavedRunsModal, fetchSavedRuns]);

  // Lazily load historic SAA the first time a blend comparison is on screen, so
  // the Source-batch SAC comparison can offer an existing-SAA column to compare against.
  useEffect(() => {
    if (historicSAA != null) return;
    if (Object.keys(blendPerProfileSources).length === 0) return;
    let cancelled = false;
    adminAPI.getHistoricSAA()
      .then((data) => { if (!cancelled) setHistoricSAA(data || { portfolios: [], financial_years: [], rows: [] }); })
      .catch(() => { if (!cancelled) setHistoricSAA({ portfolios: [], financial_years: [], rows: [] }); });
    return () => { cancelled = true; };
  }, [blendPerProfileSources, historicSAA]);

  // Dropdown options for the existing-SAA comparison: one entry per
  // (portfolio × financial year) present in the historic SAA tab.
  const existingSAAOptions = useMemo(() => {
    if (!historicSAA) return [];
    const rows = historicSAA.rows || [];
    const portName = {};
    (historicSAA.portfolios || []).forEach((p) => { portName[p.blender_code] = p.name; });
    const portOrder = (historicSAA.portfolios || []).map((p) => p.blender_code);
    const fys = (historicSAA.financial_years && historicSAA.financial_years.length)
      ? historicSAA.financial_years
      : [...new Set(rows.map((r) => r.financial_year))].sort((a, b) => b - a);
    const opts = [];
    fys.forEach((fy) => {
      const codes = portOrder.filter((bc) => rows.some((r) => r.financial_year === fy && r.blender_code === bc));
      rows.filter((r) => r.financial_year === fy).forEach((r) => {
        if (!codes.includes(r.blender_code)) codes.push(r.blender_code);
      });
      codes.forEach((bc) => opts.push({ value: `${bc}__${fy}`, label: `${portName[bc] || bc} — FY${fy}` }));
    });
    return opts;
  }, [historicSAA]);

  // SAC→weight (%) map for the selected existing SAA, or null when none chosen.
  const selectedExistingSAAWeights = useMemo(() => {
    if (!selectedExistingSAA || !historicSAA) return null;
    const sep = selectedExistingSAA.lastIndexOf('__');
    if (sep < 0) return null;
    const bc = selectedExistingSAA.slice(0, sep);
    const fy = Number(selectedExistingSAA.slice(sep + 2));
    const map = {};
    (historicSAA.rows || []).forEach((r) => {
      if (r.blender_code === bc && r.financial_year === fy) {
        map[r.sub_asset_class] = Number(r.weight) || 0;  // already a percentage
      }
    });
    return map;
  }, [selectedExistingSAA, historicSAA]);

  const collectSaveSnapshot = useCallback(() => {
    // Snapshot is context-aware: capture state based on the active tab.
    const selectedScenario = cmaScenarios.find((s) => s.id === selectedScenarioId);
    if (activeTab === 'blend_batches') {
      return {
        kind: 'batch',  // saved blends behave like any other batch on reload
        inputs: {
          scenario_id: selectedScenarioId,
          blend_source: blendSelectedRuns,  // [{run_id, name, weight}]
        },
        results: {
          batch_results: blendResults,
          blend_source_echo: blendSource,
          per_profile_sources: blendPerProfileSources,
        },
        scenario_name: selectedScenario ? selectedScenario.name : null,
        objectives: 'blend',
      };
    }
    if (activeTab === 'batch') {
      return {
        kind: 'batch',
        inputs: {
          scenario_id: selectedScenarioId,
          batch_profiles: batchProfiles,
          batch_objective: batchObjective,
          batch_profile_growth: batchProfileGrowth,
          batch_profile_growth_min: batchProfileGrowthMin,
          batch_profile_growth_max: batchProfileGrowthMax,
          batch_profile_fee: batchProfileFee,
          batch_profile_bounds: batchProfileBounds,
          batch_diversification_lambda: batchDiversificationLambda,
          overrides,
        },
        results: {
          batch_results: batchResults,
        },
        scenario_name: selectedScenario ? selectedScenario.name : null,
        objectives: batchObjective,
      };
    }
    return {
      kind: 'single',
      inputs: {
        scenario_id: selectedScenarioId,
        active_objectives: { ...activeObjectives },
        growth_mode: growthMode,
        growth_target: growthTarget,
        growth_min: growthMin,
        growth_max: growthMax,
        target_return_value: targetReturnValue,
        target_vol_value: targetVolValue,
        ac_constraints: acConstraints,
        active_preset: activePreset,
        active_constraint_set: activeConstraintSet,
        overrides,
        diversification_lambda: diversificationLambda,
      },
      results: {
        results,
        frontier_data: frontierData,
      },
      scenario_name: selectedScenario ? selectedScenario.name : null,
      objectives: Object.entries(activeObjectives).filter(([, v]) => v).map(([k]) => k).join(','),
    };
  }, [
    activeTab, selectedScenarioId, cmaScenarios, activeObjectives,
    growthMode, growthTarget, growthMin, growthMax,
    targetReturnValue, targetVolValue, acConstraints,
    activePreset, activeConstraintSet, overrides, diversificationLambda,
    results, frontierData,
    batchProfiles, batchObjective, batchProfileGrowth,
    batchProfileGrowthMin, batchProfileGrowthMax, batchProfileFee,
    batchProfileBounds, batchDiversificationLambda, batchResults,
    blendSelectedRuns, blendResults, blendSource, blendPerProfileSources,
  ]);

  const handleSaveRunSubmit = useCallback(async () => {
    if (!saveRunName.trim()) return;
    setSavingRun(true);
    try {
      const snapshot = collectSaveSnapshot();
      const created = await adminAPI.createSavedOptimiserRun({
        name: saveRunName.trim(),
        description: saveRunDescription.trim() || null,
        kind: snapshot.kind,
        scenario_id: snapshot.inputs.scenario_id,
        scenario_name: snapshot.scenario_name,
        objectives: snapshot.objectives,
        inputs: snapshot.inputs,
        results: snapshot.results,
        is_favourite: saveRunFavourite,
      });
      // Treat the just-created run as the currently loaded one so subsequent
      // Save clicks overwrite it in place (rather than re-prompting).
      if (created && created.id != null) {
        setLoadedRunId(created.id);
        setLoadedRunName(created.name || saveRunName.trim());
        setLoadedRunCanEdit(created.can_edit !== false);  // creator owns it -> editable
      }
      setShowSaveRunModal(false);
      setSaveRunName('');
      setSaveRunDescription('');
      setSaveRunFavourite(false);
    } catch (err) {
      setError('Failed to save run: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingRun(false);
    }
  }, [saveRunName, saveRunDescription, saveRunFavourite, collectSaveSnapshot]);

  // In-place save: overwrites the run currently loaded via "Load Batch / Load
  // Run". Wired to the "Save" button on both Single and Batch results panels.
  const handleSaveLoadedRun = useCallback(async () => {
    if (!loadedRunId) return;
    setSavingRun(true);
    try {
      const snapshot = collectSaveSnapshot();
      const updated = await adminAPI.updateSavedOptimiserRun(loadedRunId, {
        kind: snapshot.kind,
        scenario_id: snapshot.inputs?.scenario_id ?? null,
        scenario_name: snapshot.scenario_name ?? null,
        objectives: snapshot.objectives ?? null,
        inputs: snapshot.inputs ?? null,
        results: snapshot.results ?? null,
      });
      setLoadedRunName(updated?.name || loadedRunName);
    } catch (err) {
      setError('Failed to save run: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingRun(false);
    }
  }, [loadedRunId, loadedRunName, collectSaveSnapshot]);

  const handleLoadRun = useCallback(async (runId) => {
    try {
      const run = await adminAPI.getSavedOptimiserRun(runId);
      const i = run.inputs || {};
      const r = run.results || {};
      const kind = run.kind || 'single';
      setLoadedRunId(run.id);
      setLoadedRunName(run.name || '');
      setLoadedRunCanEdit(run.can_edit !== false);

      // Common: scenario
      if (i.scenario_id != null) setSelectedScenarioId(i.scenario_id);

      if (kind === 'batch') {
        // Saved blends carry a blend_source array — route them to the
        // Blend Batches tab so the user lands in the right place with the
        // source-runs list rehydrated. Otherwise this is a regular batch.
        if (Array.isArray(i.blend_source) && i.blend_source.length > 0) {
          setBlendSelectedRuns(i.blend_source.map((s) => ({
            run_id: s.run_id,
            name: s.name,
            weight: s.weight,
          })));
          if (r.batch_results) setBlendResults(r.batch_results);
          if (Array.isArray(r.blend_source_echo)) setBlendSource(r.blend_source_echo);
          if (r.per_profile_sources && typeof r.per_profile_sources === 'object') {
            setBlendPerProfileSources(r.per_profile_sources);
            const firstProfile = Object.keys(r.batch_results || r.per_profile_sources || {})[0] || '';
            setBlendComparisonProfile(firstProfile);
          } else {
            setBlendPerProfileSources({});
            setBlendComparisonProfile('');
          }
          setActiveTab('blend_batches');
        } else {
          if (i.batch_profiles) setBatchProfiles(i.batch_profiles);
          if (i.batch_objective) setBatchObjective(i.batch_objective);
          if (i.batch_profile_growth) setBatchProfileGrowth(i.batch_profile_growth);
          if (i.batch_profile_growth_min) setBatchProfileGrowthMin(i.batch_profile_growth_min);
          if (i.batch_profile_growth_max) setBatchProfileGrowthMax(i.batch_profile_growth_max);
          if (i.batch_profile_fee) setBatchProfileFee(i.batch_profile_fee);
          if (i.batch_profile_bounds) setBatchProfileBounds(i.batch_profile_bounds);
          if (typeof i.batch_diversification_lambda === 'number') setBatchDiversificationLambda(i.batch_diversification_lambda);
          if (i.overrides) setOverrides(i.overrides);
          if (r.batch_results) setBatchResults(r.batch_results);
          setActiveTab('batch');
        }
      } else {
        if (i.active_objectives) setActiveObjectives((prev) => ({ ...prev, ...i.active_objectives }));
        if (i.growth_mode) setGrowthMode(i.growth_mode);
        if (typeof i.growth_target === 'number') setGrowthTarget(i.growth_target);
        if (typeof i.growth_min === 'number') setGrowthMin(i.growth_min);
        if (typeof i.growth_max === 'number') setGrowthMax(i.growth_max);
        if (typeof i.target_return_value === 'number') setTargetReturnValue(i.target_return_value);
        if (typeof i.target_vol_value === 'number') setTargetVolValue(i.target_vol_value);
        if (i.ac_constraints) setAcConstraints(i.ac_constraints);
        if (i.active_preset !== undefined) setActivePreset(i.active_preset);
        if (i.active_constraint_set !== undefined) setActiveConstraintSet(i.active_constraint_set || '');
        if (i.overrides) setOverrides(i.overrides);
        if (typeof i.diversification_lambda === 'number') setDiversificationLambda(i.diversification_lambda);
        if (r.results) setResults(r.results);
        if (r.frontier_data) setFrontierData(r.frontier_data);
        setActiveTab('single');
      }

      setShowSavedRunsModal(false);
    } catch (err) {
      setError('Failed to load run: ' + (err.response?.data?.detail || err.message));
    }
  }, []);

  const handleToggleRunFavourite = useCallback(async (run) => {
    try {
      const updated = await adminAPI.updateSavedOptimiserRun(run.id, { is_favourite: !run.is_favourite });
      setSavedRuns((prev) => prev.map((x) => (x.id === run.id ? updated : x)));
    } catch (err) {
      setError('Failed to update favourite: ' + (err.response?.data?.detail || err.message));
    }
  }, []);

  // Toggle a run's sharing between read/edit (any user) and read-only (owner only).
  const handleToggleRunSharing = useCallback(async (run) => {
    const next = run.access === 'read' ? 'edit' : 'read';
    try {
      const updated = await adminAPI.setSavedOptimiserRunSharing(run.id, next);
      setSavedRuns((prev) => prev.map((x) => (x.id === run.id ? updated : x)));
      if (loadedRunId === run.id) setLoadedRunCanEdit(updated.can_edit !== false);
    } catch (err) {
      setError('Failed to change sharing: ' + (err.response?.data?.detail || err.message));
    }
  }, [loadedRunId]);

  // Permanently lock a run (frozen for everyone, cannot be unlocked).
  const handleLockRun = useCallback(async (run) => {
    if (!window.confirm(
      `Permanently lock "${run.name}"?\n\nOnce locked it is read-only for everyone — it can no longer be edited or deleted, and it cannot be unlocked.`
    )) return;
    try {
      const updated = await adminAPI.lockSavedOptimiserRun(run.id);
      setSavedRuns((prev) => prev.map((x) => (x.id === run.id ? updated : x)));
      if (loadedRunId === run.id) setLoadedRunCanEdit(false);
    } catch (err) {
      setError('Failed to lock run: ' + (err.response?.data?.detail || err.message));
    }
  }, [loadedRunId]);

  const handleDeleteSavedRun = useCallback(async (run) => {
    if (!window.confirm(`Delete saved run "${run.name}"?`)) return;
    try {
      await adminAPI.deleteSavedOptimiserRun(run.id);
      setSavedRuns((prev) => prev.filter((x) => x.id !== run.id));
      if (loadedRunId === run.id) {
        setLoadedRunId(null);
        setLoadedRunName('');
        setLoadedRunCanEdit(true);
      }
    } catch (err) {
      setError('Failed to delete run: ' + (err.response?.data?.detail || err.message));
    }
  }, [loadedRunId]);

  // Excel export — dumps every piece of state the app is showing
  const handleExportExcel = useCallback(() => {
    const wb = XLSX.utils.book_new();
    const enabledKeys = Object.keys(results);
    const acCodes = assetClasses.map((ac) => ac.code);

    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
      fill: { fgColor: { rgb: '072040' } },
      alignment: { horizontal: 'center' },
    };
    const sectionStyle = {
      font: { bold: true, color: { rgb: '072040' }, sz: 11 },
      fill: { fgColor: { rgb: 'E7E6E6' } },
    };
    const totalStyle = {
      font: { bold: true, color: { rgb: '072040' }, sz: 10 },
      fill: { fgColor: { rgb: 'F0A500' } },
    };
    const totalNumStyle = {
      ...totalStyle,
      alignment: { horizontal: 'right' },
      numFmt: '0.0',
    };
    const cellStyle = {
      font: { sz: 10 },
      border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } },
    };
    const numStyle = {
      ...cellStyle,
      alignment: { horizontal: 'right' },
      numFmt: '0.0',
    };
    const numStyle4 = { ...numStyle, numFmt: '0.0000' };

    const styleSheet = (ws, colCount, colWidths) => {
      ws['!cols'] = colWidths.map((w) => ({ wch: w }));
      for (let c = 0; c < colCount; c++) {
        const ref = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[ref]) ws[ref].s = headerStyle;
      }
    };

    const selectedScenario = cmaScenarios.find((s) => s.id === selectedScenarioId);

    // ─── Sheet 1: Summary ───────────────────────────────────────────────
    {
      const aoa = [['Parameter', 'Value']];
      aoa.push(['Exported At', new Date().toISOString()]);
      aoa.push(['CMA Scenario', selectedScenario ? `${selectedScenario.name}${selectedScenario.is_default ? ' (Default)' : ''}` : 'Default']);
      aoa.push(['Scenario ID', selectedScenarioId ?? '(default)']);
      aoa.push(['Active Preset', activePreset || 'None']);
      aoa.push(['Growth Mode', growthMode]);
      if (growthMode === 'exact') aoa.push(['Growth Target', `${growthTarget}%`]);
      if (growthMode === 'range') {
        aoa.push(['Growth Min', `${growthMin}%`]);
        aoa.push(['Growth Max', `${growthMax}%`]);
      }
      aoa.push(['Target Return (if target_return enabled)', `${targetReturnValue}%`]);
      aoa.push(['Target Vol (if target_vol enabled)', `${targetVolValue}%`]);
      aoa.push(['Objectives Run', enabledKeys.map((k) => OBJECTIVE_CONFIG[k]?.label || k).join(', ') || '(none)']);
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      styleSheet(ws, 2, [40, 40]);
      XLSX.utils.book_append_sheet(wb, ws, 'Summary');
    }

    // ─── Sheet 2: Optimal Weights ───────────────────────────────────────
    {
      const headers = ['Asset Class', 'Type', ...enabledKeys.map((k) => `${OBJECTIVE_CONFIG[k]?.label || k} (%)`)];
      if (enabledKeys.length >= 2) headers.push('Diff (first two)');
      const aoa = [headers];
      const rowMeta = [];

      acCodes.forEach((code) => {
        const ac = assetClasses.find((a) => a.code === code);
        const type = ac?.is_overlay ? 'Overlay' : ac?.is_growth ? 'Growth' : 'Defensive';
        const row = [code, type];
        enabledKeys.forEach((k) => {
          row.push(roundHalf((results[k]?.weights[code] || 0) * 100));
        });
        if (enabledKeys.length >= 2) {
          const w1 = roundHalf((results[enabledKeys[0]]?.weights[code] || 0) * 100);
          const w2 = roundHalf((results[enabledKeys[1]]?.weights[code] || 0) * 100);
          row.push(w1 - w2);
        }
        aoa.push(row);
        rowMeta.push('data');
      });

      // Summary rows
      aoa.push([]);
      rowMeta.push('blank');
      ['Return (%)', 'Volatility (%)', 'Sharpe Ratio', 'Growth (%)'].forEach((label, mi) => {
        const row = [label, ''];
        enabledKeys.forEach((k) => {
          const r = results[k];
          if (mi === 0) row.push((r?.portfolio_return * 100).toFixed(2));
          else if (mi === 1) row.push((r?.portfolio_volatility * 100).toFixed(2));
          else if (mi === 2) row.push(r?.sharpe_ratio.toFixed(4));
          else row.push(roundHalf(r?.growth_exposure * 100).toFixed(1));
        });
        if (enabledKeys.length >= 2) row.push('');
        aoa.push(row);
        rowMeta.push('total');
      });

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      styleSheet(ws, headers.length, [18, 12, ...enabledKeys.map(() => 16), 12]);

      for (let r = 0; r < rowMeta.length; r++) {
        const excelRow = r + 1;
        for (let c = 0; c < headers.length; c++) {
          const ref = XLSX.utils.encode_cell({ r: excelRow, c });
          if (!ws[ref]) ws[ref] = { v: '', t: 's' };
          if (rowMeta[r] === 'total') ws[ref].s = c >= 2 ? totalNumStyle : totalStyle;
          else if (rowMeta[r] === 'data') ws[ref].s = c >= 2 ? numStyle : cellStyle;
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, 'Optimal Weights');
    }

    // ─── Sheet 3: Risk Contributions ────────────────────────────────────
    if (enabledKeys.some((k) => results[k]?.risk_contributions)) {
      // For each objective produce three columns (mctr, cctr, pctr) per asset
      const headers = ['Asset Class'];
      enabledKeys.forEach((k) => {
        const lbl = OBJECTIVE_CONFIG[k]?.label || k;
        headers.push(`${lbl} MCTR`, `${lbl} CCTR`, `${lbl} PCTR (%)`);
      });
      const aoa = [headers];
      acCodes.forEach((code) => {
        const row = [code];
        enabledKeys.forEach((k) => {
          const rc = results[k]?.risk_contributions?.[code];
          row.push(rc ? rc.mctr : '');
          row.push(rc ? rc.cctr : '');
          row.push(rc ? roundHalf(rc.pctr * 100) : '');
        });
        aoa.push(row);
      });
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      styleSheet(ws, headers.length, [18, ...enabledKeys.flatMap(() => [12, 12, 12])]);
      for (let r = 1; r < aoa.length; r++) {
        for (let c = 1; c < headers.length; c++) {
          const ref = XLSX.utils.encode_cell({ r, c });
          if (ws[ref]) ws[ref].s = numStyle4;
        }
        const codeRef = XLSX.utils.encode_cell({ r, c: 0 });
        if (ws[codeRef]) ws[codeRef].s = cellStyle;
      }
      XLSX.utils.book_append_sheet(wb, ws, 'Risk Contributions');
    }

    // ─── Sheet 4: CMAs ──────────────────────────────────────────────────
    {
      const headers = ['Asset Class', 'Type', 'Return (%)', 'Volatility (%)', 'Return overridden?', 'Vol overridden?'];
      const aoa = [headers];
      assetClasses.forEach((ac) => {
        const type = ac?.is_overlay ? 'Overlay' : ac?.is_growth ? 'Growth' : 'Defensive';
        const ov = overrides[ac.code] || {};
        const retOverridden = ov.annual_return !== null && ov.annual_return !== undefined;
        const volOverridden = ov.annual_volatility !== null && ov.annual_volatility !== undefined;
        const retVal = retOverridden ? ov.annual_return : ac.annual_return * 100;
        const volVal = volOverridden ? ov.annual_volatility : ac.annual_volatility * 100;
        aoa.push([
          ac.code,
          type,
          typeof retVal === 'number' ? parseFloat(retVal.toFixed(2)) : retVal,
          typeof volVal === 'number' ? parseFloat(volVal.toFixed(2)) : volVal,
          retOverridden ? 'Yes' : '',
          volOverridden ? 'Yes' : '',
        ]);
      });

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      styleSheet(ws, headers.length, [18, 12, 14, 14, 18, 18]);
      for (let r = 1; r < aoa.length; r++) {
        for (let c = 0; c < headers.length; c++) {
          const ref = XLSX.utils.encode_cell({ r, c });
          if (ws[ref]) ws[ref].s = (c === 2 || c === 3) ? numStyle : cellStyle;
        }
      }
      XLSX.utils.book_append_sheet(wb, ws, 'CMAs');
    }

    // ─── Sheet 5: Correlation Matrix ────────────────────────────────────
    if (corrMatrix && Object.keys(corrMatrix).length > 0) {
      const corrCodes = acCodes.filter((c) => corrMatrix[c]);
      const headers = ['', ...corrCodes];
      const aoa = [headers];
      corrCodes.forEach((rowCode) => {
        const row = [rowCode];
        corrCodes.forEach((colCode) => {
          const v = corrMatrix[rowCode]?.[colCode];
          row.push(typeof v === 'number' ? parseFloat(v.toFixed(4)) : '');
        });
        aoa.push(row);
      });
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      styleSheet(ws, headers.length, [14, ...corrCodes.map(() => 10)]);
      for (let r = 1; r < aoa.length; r++) {
        for (let c = 0; c < headers.length; c++) {
          const ref = XLSX.utils.encode_cell({ r, c });
          if (!ws[ref]) continue;
          if (c === 0) ws[ref].s = sectionStyle;
          else ws[ref].s = numStyle4;
        }
      }
      XLSX.utils.book_append_sheet(wb, ws, 'Correlation Matrix');
    }

    // ─── Sheet 6: Constraints ───────────────────────────────────────────
    {
      const aoa = [['Section', 'Parameter', 'Value', 'Detail 1', 'Detail 2']];
      aoa.push(['Growth', 'Mode', growthMode, '', '']);
      if (growthMode === 'exact') aoa.push(['Growth', 'Target (%)', growthTarget, '', '']);
      if (growthMode === 'range') {
        aoa.push(['Growth', 'Min (%)', growthMin, '', '']);
        aoa.push(['Growth', 'Max (%)', growthMax, '', '']);
      }
      aoa.push([]);
      aoa.push(['Per-Asset', 'Code', 'Mode', 'Min %', 'Max %']);
      Object.entries(acConstraints).forEach(([code, cfg]) => {
        if (cfg.mode === 'absolute') {
          aoa.push(['Per-Asset', code, 'absolute', cfg.absMin ?? 0, cfg.absMax ?? 100]);
        } else if (cfg.mode === 'relative') {
          aoa.push(['Per-Asset', code, `relative to ${cfg.reference || ''}`, cfg.relMin ?? 0, cfg.relMax ?? 100]);
        }
      });

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      styleSheet(ws, 5, [12, 18, 22, 12, 12]);
      XLSX.utils.book_append_sheet(wb, ws, 'Constraints');
    }

    // ─── Sheet 7: Efficient Frontier ────────────────────────────────────
    if (frontierData?.points?.length) {
      const headers = ['Point #', 'Marker', 'Return (%)', 'Volatility (%)', 'Sharpe', ...acCodes.map((c) => `${c} (%)`)];
      const aoa = [headers];
      frontierData.points.forEach((p, i) => {
        const markers = [];
        if (i === frontierData.max_sharpe_index) markers.push('Max Sharpe');
        if (i === frontierData.min_vol_index) markers.push('Min Vol');
        const row = [
          i + 1,
          markers.join(' / '),
          (p.portfolio_return * 100).toFixed(2),
          (p.portfolio_volatility * 100).toFixed(2),
          p.sharpe_ratio?.toFixed(4) ?? '',
        ];
        acCodes.forEach((code) => {
          row.push(roundHalf((p.weights?.[code] || 0) * 100));
        });
        aoa.push(row);
      });
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      styleSheet(ws, headers.length, [8, 18, 12, 12, 10, ...acCodes.map(() => 10)]);
      for (let r = 1; r < aoa.length; r++) {
        for (let c = 0; c < headers.length; c++) {
          const ref = XLSX.utils.encode_cell({ r, c });
          if (ws[ref] && c >= 2) ws[ref].s = numStyle;
          else if (ws[ref]) ws[ref].s = cellStyle;
        }
      }
      XLSX.utils.book_append_sheet(wb, ws, 'Efficient Frontier');
    }

    // ─── Sheet 8: Frozen Scenarios ──────────────────────────────────────
    if (frozenScenarios.length > 0) {
      // Comparison sheet — one section per scenario with summary metrics + weights
      const headers = ['Asset Class / Metric', ...frozenScenarios.map((s) => s.name)];
      const aoa = [headers];
      ['Return (%)', 'Volatility (%)', 'Sharpe', 'Growth (%)'].forEach((label, mi) => {
        const row = [label];
        frozenScenarios.forEach((s) => {
          // Use the first result inside each frozen scenario
          const firstKey = Object.keys(s.results || {})[0];
          const r = firstKey ? s.results[firstKey] : null;
          if (!r) { row.push(''); return; }
          if (mi === 0) row.push((r.portfolio_return * 100).toFixed(2));
          else if (mi === 1) row.push((r.portfolio_volatility * 100).toFixed(2));
          else if (mi === 2) row.push(r.sharpe_ratio?.toFixed(4) ?? '');
          else row.push(roundHalf(r.growth_exposure * 100).toFixed(1));
        });
        aoa.push(row);
      });
      aoa.push([]);
      acCodes.forEach((code) => {
        const row = [code];
        frozenScenarios.forEach((s) => {
          const firstKey = Object.keys(s.results || {})[0];
          const r = firstKey ? s.results[firstKey] : null;
          row.push(r ? roundHalf((r.weights?.[code] || 0) * 100) : '');
        });
        aoa.push(row);
      });
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      styleSheet(ws, headers.length, [22, ...frozenScenarios.map(() => 16)]);
      XLSX.utils.book_append_sheet(wb, ws, 'Frozen Scenarios');
    }

    // ─── Sheet 9: Run Status ────────────────────────────────────────────
    if (enabledKeys.length > 0) {
      const aoa = [['Objective', 'Success', 'Message', 'Return (%)', 'Vol (%)', 'Sharpe', 'Growth (%)']];
      enabledKeys.forEach((k) => {
        const r = results[k];
        aoa.push([
          OBJECTIVE_CONFIG[k]?.label || k,
          r?.success ? 'Yes' : 'No',
          r?.message || '',
          r ? (r.portfolio_return * 100).toFixed(2) : '',
          r ? (r.portfolio_volatility * 100).toFixed(2) : '',
          r ? r.sharpe_ratio?.toFixed(4) ?? '' : '',
          r ? roundHalf(r.growth_exposure * 100).toFixed(1) : '',
        ]);
      });
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      styleSheet(ws, 7, [16, 10, 40, 12, 12, 10, 12]);
      XLSX.utils.book_append_sheet(wb, ws, 'Run Status');
    }

    const scenarioSuffix = selectedScenario ? `_${selectedScenario.name.replace(/[^A-Za-z0-9_-]/g, '_')}` : '';
    const ts = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Portfolio_Optimiser${scenarioSuffix}_${ts}.xlsx`);
  }, [
    results, assetClasses, overrides, acConstraints,
    growthMode, growthTarget, growthMin, growthMax,
    targetReturnValue, targetVolValue,
    frontierData, frozenScenarios, corrMatrix,
    cmaScenarios, selectedScenarioId, activePreset,
  ]);

  // Open the saved-batch picker for the rich multi-run Excel export.
  const openBatchExportPicker = useCallback(() => {
    setBatchExportChecked({});
    if (!savedRuns.length) fetchSavedRuns();
    setShowBatchExportPicker(true);
  }, [savedRuns.length, fetchSavedRuns]);

  // Build one workbook covering every selected saved batch run: per-run Summary,
  // Weights, CMAs, Correlation, Constraints (+ Risk Contributions), plus an Index
  // and a cross-run Comparison sheet. CMAs/correlation are fetched live per each
  // run's own saved scenario (cached so a shared scenario is fetched once).
  // NOTE: batch runs don't persist asset_overrides, so the CMAs reflect the
  // scenario's base values as they are now (flagged on each Summary sheet).
  const handleExportSelectedBatches = useCallback(async () => {
    const ids = Object.keys(batchExportChecked).filter((id) => batchExportChecked[id]).map(Number);
    if (ids.length === 0) return;
    setBatchExportLoading(true);
    setError(null);
    try {
      // 1. Full payloads for each selected run.
      const runs = await Promise.all(ids.map((id) => adminAPI.getSavedOptimiserRun(id)));

      // 2. CMA data per unique scenario (null = default), fetched once each.
      const scenarioCache = new Map();
      for (const run of runs) {
        const key = run.scenario_id ?? '__default__';
        if (!scenarioCache.has(key)) {
          scenarioCache.set(key, await adminAPI.getOptimiserData(run.scenario_id ?? null));
        }
      }

      // 3. Normalise each run into the pieces the sheet builders need.
      const prepared = runs.map((run, i) => {
        const label = `R${i + 1}`;
        const inputs = run.inputs || {};
        const cma = scenarioCache.get(run.scenario_id ?? '__default__') || {};
        const assetClassesData = cma.asset_classes || [];
        const corrMatrix = cma.correlation_matrix || {};
        const acCodes = assetClassesData.map((ac) => ac.code);
        // results may wrap the per-profile map under batch_results / results.
        let batchResultsData = run.results || {};
        if (batchResultsData.batch_results) batchResultsData = batchResultsData.batch_results;
        else if (batchResultsData.results) batchResultsData = batchResultsData.results;
        const profiles = Object.keys(batchResultsData);
        return { run, label, inputs, assetClassesData, corrMatrix, acCodes, batchResultsData, profiles };
      });

      const wb = XLSX.utils.book_new();
      const usedNames = new Set();

      // ─── Index sheet (R# → run name / scenario) ───────────────────────
      {
        const aoa = [['Label', 'Run Name', 'Scenario', 'Objective', 'Profiles', 'Saved At']];
        prepared.forEach(({ run, label, inputs, profiles }) => {
          aoa.push([
            label,
            run.name || '',
            run.scenario_name || (run.scenario_id != null ? `id=${run.scenario_id}` : 'Default'),
            run.objectives || inputs.batch_objective || '',
            profiles.join(', '),
            run.created_at || '',
          ]);
        });
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        styleHeaderRow(ws, 6, [8, 28, 24, 16, 30, 22]);
        XLSX.utils.book_append_sheet(wb, ws, safeSheetName('Index', usedNames));
      }

      // ─── Comparison sheet (columns = R# · profile) ────────────────────
      {
        const colDefs = [];
        prepared.forEach(({ label, profiles, batchResultsData }) => {
          profiles.forEach((p) => colDefs.push({ label: `${label} · ${p}`, r: batchResultsData[p] }));
        });
        const sacUnion = [];
        const seen = new Set();
        prepared.forEach(({ acCodes }) => acCodes.forEach((c) => { if (!seen.has(c)) { seen.add(c); sacUnion.push(c); } }));

        const headers = ['Metric', ...colDefs.map((c) => c.label)];
        const aoa = [headers];
        const rowMeta = [];
        ['Return (%)', 'Volatility (%)', 'Sharpe Ratio', 'Growth (%)'].forEach((label, mi) => {
          const row = [label];
          colDefs.forEach(({ r }) => {
            if (!r) { row.push(''); return; }
            if (mi === 0) row.push((r.portfolio_return * 100).toFixed(2));
            else if (mi === 1) row.push((r.portfolio_volatility * 100).toFixed(2));
            else if (mi === 2) row.push((r.sharpe_ratio ?? 0).toFixed(4));
            else row.push(roundHalf((r.growth_exposure ?? 0) * 100).toFixed(1));
          });
          aoa.push(row); rowMeta.push('total');
        });
        aoa.push(Array(headers.length).fill('')); rowMeta.push('blank');
        sacUnion.forEach((code) => {
          const row = [code];
          colDefs.forEach(({ r }) => row.push(roundHalf((r?.weights?.[code] || 0) * 100)));
          aoa.push(row); rowMeta.push('data');
        });
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!cols'] = [{ wch: 18 }, ...colDefs.map(() => ({ wch: 16 }))];
        for (let c = 0; c < headers.length; c++) {
          const ref = XLSX.utils.encode_cell({ r: 0, c });
          if (ws[ref]) ws[ref].s = OPT_XLSX_STYLES.header;
        }
        for (let r = 0; r < rowMeta.length; r++) {
          const excelRow = r + 1;
          for (let c = 0; c < headers.length; c++) {
            const ref = XLSX.utils.encode_cell({ r: excelRow, c });
            if (!ws[ref]) ws[ref] = { v: '', t: 's' };
            if (rowMeta[r] === 'total') ws[ref].s = c >= 1 ? OPT_XLSX_STYLES.totalNum : OPT_XLSX_STYLES.total;
            else if (rowMeta[r] === 'data') ws[ref].s = c >= 1 ? OPT_XLSX_STYLES.num : OPT_XLSX_STYLES.cell;
          }
        }
        XLSX.utils.book_append_sheet(wb, ws, safeSheetName('Comparison', usedNames));
      }

      // ─── Per-run sheet groups ─────────────────────────────────────────
      prepared.forEach(({ run, label, inputs, assetClassesData, corrMatrix, acCodes, batchResultsData, profiles }) => {
        const summary = buildRunSummarySheet({
          label,
          name: run.name,
          description: run.description,
          scenario_name: run.scenario_name,
          scenario_id: run.scenario_id,
          objective: run.objectives || inputs.batch_objective,
          diversification_lambda: inputs.batch_diversification_lambda,
          profiles,
          created_by_email: run.created_by_email,
          created_at: run.created_at,
        });
        XLSX.utils.book_append_sheet(wb, summary, safeSheetName(`${label} Summary`, usedNames));
        XLSX.utils.book_append_sheet(wb, buildBatchWeightsSheet(profiles, batchResultsData, acCodes), safeSheetName(`${label} Weights`, usedNames));
        XLSX.utils.book_append_sheet(wb, buildCMAsSheetFromData(assetClassesData), safeSheetName(`${label} CMAs`, usedNames));
        XLSX.utils.book_append_sheet(wb, buildCorrSheet(corrMatrix, acCodes), safeSheetName(`${label} Corr`, usedNames));
        XLSX.utils.book_append_sheet(wb, buildBatchConstraintsSheet(inputs, profiles), safeSheetName(`${label} Constraints`, usedNames));
        const hasRC = profiles.some((p) => batchResultsData[p]?.risk_contributions);
        if (hasRC) {
          XLSX.utils.book_append_sheet(wb, buildBatchRiskContribSheet(profiles, batchResultsData, acCodes), safeSheetName(`${label} RiskContrib`, usedNames));
        }
      });

      const ts = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `DSP_Batch_Export_${ts}.xlsx`);
      setShowBatchExportPicker(false);
    } catch (err) {
      setError('Batch export failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBatchExportLoading(false);
    }
  }, [batchExportChecked]);

  // ── Export Blend Batches result to Excel ──────────────────────────
  const handleExportBlendBatches = useCallback(() => {
    if (!blendResults || Object.keys(blendResults).length === 0) return;
    const wb = XLSX.utils.book_new();
    const profiles = Object.keys(blendResults);
    const acCodes = assetClasses.map((ac) => ac.code);
    const overlayCodes = new Set(
      (assetClasses || []).filter((ac) => ac && ac.is_overlay).map((ac) => ac.code)
    );

    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
      fill: { fgColor: { rgb: '072040' } },
      alignment: { horizontal: 'center' },
    };
    const totalStyle = {
      font: { bold: true, color: { rgb: '072040' }, sz: 10 },
      fill: { fgColor: { rgb: 'F0A500' } },
    };
    const totalNumStyle = { ...totalStyle, alignment: { horizontal: 'right' }, numFmt: '0.0' };
    const cellStyle = { font: { sz: 10 }, border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } } };
    const numStyle = { ...cellStyle, alignment: { horizontal: 'right' }, numFmt: '0.0' };
    const numStyle4 = { ...numStyle, numFmt: '0.0000' };
    const blendedColStyle = { ...numStyle, fill: { fgColor: { rgb: 'E7F0FB' } }, font: { bold: true, sz: 10 } };

    const selectedScenario = cmaScenarios.find((s) => s.id === selectedScenarioId);

    // ─── Sheet 1: Summary (source runs + weights, scenario) ─────────────
    {
      const aoa = [['Parameter', 'Value']];
      aoa.push(['Exported At', new Date().toISOString()]);
      aoa.push(['CMA Scenario', selectedScenario ? `${selectedScenario.name}${selectedScenario.is_default ? ' (Default)' : ''}` : '(default)']);
      aoa.push(['Profiles Blended', profiles.join(', ')]);
      aoa.push([]);
      aoa.push(['Source Run', 'Relative Weight', 'Normalised Weight', 'Run ID']);
      const sourceRows = blendSource && blendSource.length > 0
        ? blendSource
        : blendSelectedRuns.map((r) => {
            const total = blendSelectedRuns.reduce((s, x) => s + (Number(x.weight) || 0), 0);
            return {
              run_id: r.run_id,
              name: r.name,
              weight: Number(r.weight) || 0,
              normalised_weight: total > 0 ? (Number(r.weight) || 0) / total : 0,
            };
          });
      sourceRows.forEach((s) => {
        aoa.push([s.name, s.weight, s.normalised_weight, s.run_id]);
      });

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = [{ wch: 40 }, { wch: 18 }, { wch: 20 }, { wch: 10 }];
      // Style row 0 (param/value header) and row 5 (source-runs header)
      for (let c = 0; c < 2; c++) {
        const ref = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[ref]) ws[ref].s = headerStyle;
      }
      for (let c = 0; c < 4; c++) {
        const ref = XLSX.utils.encode_cell({ r: 5, c });
        if (ws[ref]) ws[ref].s = headerStyle;
      }
      // Number format on the source-run weight columns
      for (let r = 6; r < 6 + sourceRows.length; r++) {
        for (const c of [1, 2]) {
          const ref = XLSX.utils.encode_cell({ r, c });
          if (ws[ref]) ws[ref].s = numStyle4;
        }
      }
      XLSX.utils.book_append_sheet(wb, ws, 'Summary');
    }

    // ─── Sheet 2: Blended profile weights + metrics (mirrors batch export) ─
    {
      const headers = ['Metric', ...profiles];
      const aoa = [headers];
      const rowMeta = [];

      ['Return (%)', 'Volatility (%)', 'Sharpe Ratio', 'Growth (%)'].forEach((label, mi) => {
        const row = [label];
        profiles.forEach((p) => {
          const r = blendResults[p];
          if (mi === 0) row.push((r.portfolio_return * 100).toFixed(2));
          else if (mi === 1) row.push((r.portfolio_volatility * 100).toFixed(2));
          else if (mi === 2) row.push(r.sharpe_ratio.toFixed(4));
          else row.push(roundHalf(r.growth_exposure * 100).toFixed(1));
        });
        aoa.push(row);
        rowMeta.push('total');
      });

      aoa.push(Array(headers.length).fill(''));
      rowMeta.push('blank');

      acCodes.forEach((code) => {
        const row = [code];
        profiles.forEach((p) => {
          row.push(roundHalf((blendResults[p]?.weights[code] || 0) * 100));
        });
        aoa.push(row);
        rowMeta.push('data');
      });

      // Total (excluding overlay) row
      const totalRow = ['Total (excl. overlay)'];
      profiles.forEach((p) => {
        const total = Object.entries(blendResults[p]?.weights || {})
          .filter(([sac]) => !overlayCodes.has(sac))
          .reduce((s, [, v]) => s + (v || 0), 0);
        totalRow.push(roundHalf(total * 100));
      });
      aoa.push(totalRow);
      rowMeta.push('total');

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = [{ wch: 22 }, ...profiles.map(() => ({ wch: 14 }))];
      for (let c = 0; c < headers.length; c++) {
        const ref = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[ref]) ws[ref].s = headerStyle;
      }
      for (let r = 0; r < rowMeta.length; r++) {
        const excelRow = r + 1;
        for (let c = 0; c < headers.length; c++) {
          const ref = XLSX.utils.encode_cell({ r: excelRow, c });
          if (!ws[ref]) ws[ref] = { v: '', t: 's' };
          if (rowMeta[r] === 'total') ws[ref].s = c >= 1 ? totalNumStyle : totalStyle;
          else if (rowMeta[r] === 'data') ws[ref].s = c >= 1 ? numStyle : cellStyle;
        }
      }
      XLSX.utils.book_append_sheet(wb, ws, 'Blended Profiles');
    }

    // ─── Sheet 3: Source comparison per profile (long form) ─────────────
    if (blendPerProfileSources && Object.keys(blendPerProfileSources).length > 0) {
      // Union of source names across all profiles, in the order they first appear.
      const sourceNames = [];
      const seen = new Set();
      profiles.forEach((p) => {
        const srcs = blendPerProfileSources[p] || {};
        Object.keys(srcs).forEach((n) => {
          if (!seen.has(n)) {
            seen.add(n);
            sourceNames.push(n);
          }
        });
      });

      const headers = ['Profile', 'Sub Asset Class', 'Type', ...sourceNames.map((n) => `${n} (%)`), 'Blended (%)'];
      const aoa = [headers];
      const rowMeta = [];

      profiles.forEach((profile) => {
        const sources = blendPerProfileSources[profile] || {};
        const blended = blendResults[profile]?.weights || {};
        // Union of SACs across sources + blended for this profile.
        const sacSet = new Set();
        sourceNames.forEach((n) => Object.keys(sources[n] || {}).forEach((s) => sacSet.add(s)));
        Object.keys(blended).forEach((s) => sacSet.add(s));
        // Order by taxonomy then any extras at the end
        const orderedSacs = [
          ...acCodes.filter((c) => sacSet.has(c)),
          ...[...sacSet].filter((c) => !acCodes.includes(c)),
        ];
        orderedSacs.forEach((sac) => {
          const ac = assetClasses.find((a) => a.code === sac);
          const type = ac?.is_overlay ? 'Overlay' : ac?.is_growth ? 'Growth' : 'Defensive';
          const row = [profile, sac, type];
          sourceNames.forEach((n) => {
            const v = (sources[n] && sources[n][sac]) || 0;
            row.push(roundHalf(v * 100));
          });
          row.push(roundHalf((blended[sac] || 0) * 100));
          aoa.push(row);
          rowMeta.push('data');
        });
        // Totals (excl. overlay) per profile.
        const totalRow = [profile, 'Total (excl. overlay)', ''];
        sourceNames.forEach((n) => {
          const total = Object.entries(sources[n] || {})
            .filter(([sac]) => !overlayCodes.has(sac))
            .reduce((s, [, v]) => s + (v || 0), 0);
          totalRow.push(roundHalf(total * 100));
        });
        const blendedTotal = Object.entries(blended)
          .filter(([sac]) => !overlayCodes.has(sac))
          .reduce((s, [, v]) => s + (v || 0), 0);
        totalRow.push(roundHalf(blendedTotal * 100));
        aoa.push(totalRow);
        rowMeta.push('total');
        // Spacer row between profiles
        aoa.push(Array(headers.length).fill(''));
        rowMeta.push('blank');
      });

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = [
        { wch: 14 }, { wch: 18 }, { wch: 11 },
        ...sourceNames.map(() => ({ wch: 26 })),
        { wch: 14 },
      ];
      for (let c = 0; c < headers.length; c++) {
        const ref = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[ref]) ws[ref].s = headerStyle;
      }
      const blendedColIdx = headers.length - 1;
      for (let r = 0; r < rowMeta.length; r++) {
        const excelRow = r + 1;
        for (let c = 0; c < headers.length; c++) {
          const ref = XLSX.utils.encode_cell({ r: excelRow, c });
          if (!ws[ref]) ws[ref] = { v: '', t: 's' };
          if (rowMeta[r] === 'total') {
            ws[ref].s = c >= 3 ? totalNumStyle : totalStyle;
          } else if (rowMeta[r] === 'data') {
            if (c === blendedColIdx) ws[ref].s = blendedColStyle;
            else if (c >= 3) ws[ref].s = numStyle;
            else ws[ref].s = cellStyle;
          }
        }
      }
      XLSX.utils.book_append_sheet(wb, ws, 'Source Comparison');
    }

    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Blend_Batches_${stamp}.xlsx`);
  }, [blendResults, blendSelectedRuns, blendSource, blendPerProfileSources, assetClasses, cmaScenarios, selectedScenarioId]);

  // Derived data
  const enabledObjectiveKeys = useMemo(
    () => Object.entries(activeObjectives).filter(([, v]) => v).map(([k]) => k),
    [activeObjectives]
  );

  const hasResults = Object.keys(results).length > 0;
  const acCodes = useMemo(() => assetClasses.map((ac) => ac.code), [assetClasses]);

  const chartData = useMemo(() => {
    if (!hasResults) return [];
    return acCodes.map((code) => {
      const row = { name: code };
      Object.entries(results).forEach(([key, res]) => {
        row[OBJECTIVE_CONFIG[key]?.label || key] = roundHalf((res.weights[code] || 0) * 100);
      });
      return row;
    });
  }, [results, acCodes, hasResults]);

  const frontierChartData = useMemo(() => {
    if (!frontierData?.points?.length) return null;
    return frontierData.points.map((p) => ({
      vol: p.portfolio_volatility,
      ret: p.portfolio_return,
      sharpe: p.sharpe_ratio,
    }));
  }, [frontierData]);

  // First result for risk contributions display
  const primaryResult = useMemo(() => {
    const keys = Object.keys(results);
    return keys.length > 0 ? results[keys[0]] : null;
  }, [results]);

  // Colour palette for the batch profile series
  const BATCH_COLOURS = [
    '#072040', '#599BE3', '#f0a500', '#91CF9B', '#dc3545',
    '#6c757d', '#9b59b6', '#1abc9c', '#e67e22',
  ];

  const batchScatterData = useMemo(() => {
    if (!batchResults) return null;
    return Object.entries(batchResults).map(([name, r]) => ({
      name,
      vol: r.portfolio_volatility,
      ret: r.portfolio_return,
      sharpe: r.sharpe_ratio,
    }));
  }, [batchResults]);

  const acScatterData = useMemo(() => {
    if (!assetClasses?.length) return [];
    return assetClasses
      .filter((ac) => !ac.is_overlay)  // CURRENCY is a notional overlay, not a standalone asset
      .map((ac) => ({
        name: ac.code,
        vol: ac.annual_volatility,
        ret: ac.annual_return,
        is_growth: ac.is_growth,
      }));
  }, [assetClasses]);

  const [showAssetClassDots, setShowAssetClassDots] = useState(true);

  const batchWeightsChartData = useMemo(() => {
    if (!batchResults) return [];
    return acCodes.map((code) => {
      const row = { name: code };
      Object.entries(batchResults).forEach(([profile, r]) => {
        row[profile] = roundHalf((r.weights[code] || 0) * 100);
      });
      return row;
    }).filter((row) => {
      // Hide rows where every profile has near-zero weight
      const max = Math.max(...Object.entries(row).filter(([k]) => k !== 'name').map(([, v]) => v));
      return max >= 0.1;
    });
  }, [batchResults, acCodes]);

  const riskContribData = useMemo(() => {
    if (!primaryResult?.risk_contributions) return [];
    return acCodes
      .map((code) => {
        const w = roundHalf((primaryResult.weights[code] || 0) * 100);
        const rc = primaryResult.risk_contributions[code];
        const riskPct = rc ? roundHalf(rc.pctr * 100) : 0;
        const ratio = w > 0.1 ? riskPct / w : 0;
        return { name: code, weight: w, risk: riskPct, ratio: Math.round(ratio * 100) / 100 };
      })
      .filter((d) => d.weight > 0.1 || d.risk > 0.1);
  }, [primaryResult, acCodes]);

  if (loading) {
    return (
      <div className="optimiser-container">
        <div className="optimiser-loading">
          <div className="spinner-border spinner-border-sm me-2" role="status" />
          Loading capital market assumptions...
        </div>
      </div>
    );
  }

  return (
    <div className="optimiser-container">
      <div className="optimiser-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2>Portfolio Optimiser</h2>
          <p>Mean-variance optimisation using Drummond capital market assumptions</p>
          {cmaScenarios.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#E7E6E6' }}>CMA Scenario:</label>
              <select
                className="form-select form-select-sm"
                style={{ maxWidth: '250px', fontSize: '0.85rem' }}
                value={selectedScenarioId || ''}
                onChange={e => handleScenarioChange(parseInt(e.target.value) || null)}
              >
                <option value="">Default</option>
                {cmaScenarios.map(s => (
                  <option key={s.id} value={s.id}>{s.name}{s.is_default ? ' (Default)' : ''}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <button
          onClick={() => setShowSavedRunsModal(true)}
          title="Browse, load, favourite, or delete previously-saved optimisation runs"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            backgroundColor: 'var(--secondary-color)',
            color: 'var(--primary-color)',
            border: 'none',
            padding: '8px 16px',
            borderRadius: 5,
            fontWeight: 700,
            fontSize: '0.85rem',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <i className="bi bi-folder2-open"></i>
          Load Saved Run
        </button>
      </div>

      {error && <div className="optimiser-error">{error}</div>}

      {/* Tab bar — Batch is primary */}
      <div className="optimiser-tabs">
        <button className={`tab-btn ${activeTab === 'batch' ? 'active' : ''}`} onClick={() => setActiveTab('batch')}>Batch</button>
        <button className={`tab-btn ${activeTab === 'single' ? 'active' : ''}`} onClick={() => setActiveTab('single')}>Single</button>
        <button className={`tab-btn ${activeTab === 'blend_batches' ? 'active' : ''}`} onClick={() => setActiveTab('blend_batches')}>Blend Batches</button>
      </div>

      {activeTab === 'single' && (
        <div className="optimiser-layout">
          {/* Left Panel - Inputs */}
          <div>
            {/* Constraints */}
            <div className="optimiser-panel" style={{ marginBottom: 16 }}>
              <div className="optimiser-panel-header">Constraints</div>
              <div className="optimiser-panel-body">
                {/* 1. Objectives */}
                <div className="constraint-section">
                  <div className="constraint-label">Objectives</div>
                  <div className="objectives-toggle">
                    {Object.entries(OBJECTIVE_CONFIG).map(([key, cfg]) => (
                      <button
                        key={key}
                        className={`objective-btn ${activeObjectives[key] ? 'active' : ''}`}
                        style={activeObjectives[key] ? { background: cfg.color, borderColor: cfg.color, color: key === 'min_vol' || key === 'target_vol' ? '#fff' : '#072040' } : {}}
                        onClick={() => toggleObjective(key)}
                      >
                        {cfg.label}
                      </button>
                    ))}
                  </div>
                  {activeObjectives.target_return && (
                    <div className="constraint-row" style={{ marginTop: 8 }}>
                      <label>Target Return</label>
                      <input type="number" className="growth-number-input" step="0.5" value={targetReturnValue}
                        onChange={(e) => setTargetReturnValue(e.target.value === '' ? 0 : parseFloat(e.target.value))} />
                      <span className="constraint-pct">%</span>
                    </div>
                  )}
                  {activeObjectives.target_vol && (
                    <div className="constraint-row" style={{ marginTop: 8 }}>
                      <label>Target Vol</label>
                      <input type="number" className="growth-number-input" step="0.5" value={targetVolValue}
                        onChange={(e) => setTargetVolValue(e.target.value === '' ? 0 : parseFloat(e.target.value))} />
                      <span className="constraint-pct">%</span>
                    </div>
                  )}
                  <div style={{ marginTop: 10 }}>
                    <label style={{ fontSize: '0.78rem', color: '#6c757d', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>Diversification λ:</span>
                      <strong style={{ minWidth: 30, textAlign: 'right' }}>{diversificationLambda.toFixed(2)}</strong>
                    </label>
                    <input
                      type="range"
                      min="0" max="1" step="0.05"
                      value={diversificationLambda}
                      onChange={(e) => setDiversificationLambda(parseFloat(e.target.value))}
                      style={{ width: '100%' }}
                      title="HHI concentration penalty added to the objective. 0 = pure mean-variance (corner solutions, 2-3 winning SACs). 0.3-0.5 typically spreads weight nicely. 1.0 ≈ equal-weight. Risk Parity and HRP ignore it (they're diversification-by-construction)."
                    />
                  </div>
                </div>

                {/* 2. Growth Allocation */}
                <div className="constraint-section">
                  <div className="constraint-label">Growth Allocation</div>
                  <div className="growth-mode-toggle">
                    <button className={`growth-mode-btn ${growthMode === 'none' ? 'active' : ''}`} onClick={() => { setGrowthMode('none'); setActivePreset(null); }}>None</button>
                    <button className={`growth-mode-btn ${growthMode === 'exact' ? 'active' : ''}`} onClick={() => { setGrowthMode('exact'); setActivePreset(null); }}>Exact</button>
                    <button className={`growth-mode-btn ${growthMode === 'range' ? 'active' : ''}`} onClick={() => { setGrowthMode('range'); setActivePreset(null); }}>Range</button>
                  </div>
                  {growthMode === 'none' ? (
                    <div style={{ fontSize: '0.8rem', color: '#6c757d', padding: '4px 0' }}>No growth/defensive constraint</div>
                  ) : growthMode === 'exact' ? (
                    <div className="constraint-row">
                      <label>Target</label>
                      <input type="range" min="0" max="100" step="1" value={growthTarget} onChange={(e) => { setGrowthTarget(parseInt(e.target.value)); setActivePreset(null); }} />
                      <input type="number" className="growth-number-input" min="0" max="100" step="1" value={growthTarget} onChange={(e) => { setGrowthTarget(e.target.value === '' ? 0 : parseInt(e.target.value)); setActivePreset(null); }} />
                      <span className="constraint-pct">%</span>
                    </div>
                  ) : (
                    <>
                      <div className="constraint-row">
                        <label>Min</label>
                        <input type="range" min="0" max="100" step="1" value={growthMin} onChange={(e) => { setGrowthMin(parseInt(e.target.value)); setActivePreset(null); }} />
                        <input type="number" className="growth-number-input" min="0" max="100" step="1" value={growthMin} onChange={(e) => { setGrowthMin(e.target.value === '' ? 0 : parseInt(e.target.value)); setActivePreset(null); }} />
                        <span className="constraint-pct">%</span>
                      </div>
                      <div className="constraint-row">
                        <label>Max</label>
                        <input type="range" min="0" max="100" step="1" value={growthMax} onChange={(e) => { setGrowthMax(parseInt(e.target.value)); setActivePreset(null); }} />
                        <input type="number" className="growth-number-input" min="0" max="100" step="1" value={growthMax} onChange={(e) => { setGrowthMax(e.target.value === '' ? 0 : parseInt(e.target.value)); setActivePreset(null); }} />
                        <span className="constraint-pct">%</span>
                      </div>
                    </>
                  )}
                </div>

                {/* 3. Per-Asset Constraints (with preset + saved-set bar) */}
                <div className="constraint-section">
                  {/* Top: built-in presets */}
                  <div className="constraint-label" style={{ marginBottom: 6 }}>Asset Constraints</div>
                  <div className="preset-buttons" style={{ marginBottom: 8 }}>
                    {Object.entries(PRESETS).map(([key, p]) => (
                      <button
                        key={key}
                        className={`preset-btn ${activePreset === key ? 'active' : ''}`}
                        onClick={() => handlePreset(key)}
                        title={
                          key === 'default_dsp'
                            ? 'Apply Drummond Strategic Portfolio defaults: 70% growth target + the standard DSP per-asset caps'
                            : 'Clear all per-asset constraints and disable the growth target'
                        }
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>

                  {/* Saved sets bar */}
                  <div
                    style={{
                      display: 'flex',
                      gap: 6,
                      alignItems: 'center',
                      marginBottom: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <label style={{ fontSize: '0.78rem', color: '#6c757d', margin: 0 }}>Saved Sets:</label>
                    <select
                      className="constraint-select"
                      style={{ minWidth: 140 }}
                      value={activeConstraintSet}
                      onChange={(e) => handleLoadConstraintSet(e.target.value)}
                      title="Load a previously-saved asset-constraint set"
                    >
                      <option value="">-- none --</option>
                      {Object.keys(savedConstraintSets).sort().map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                    <button
                      className="preset-btn"
                      onClick={handleSaveConstraintSet}
                      disabled={!activeConstraintSet}
                      title={
                        activeConstraintSet
                          ? `Overwrite the currently loaded set "${activeConstraintSet}" with the current per-asset constraints`
                          : 'No set is currently loaded — use Save As… instead'
                      }
                    >
                      Save
                    </button>
                    <button
                      className="preset-btn"
                      onClick={handleSaveAsConstraintSet}
                      title="Save the current per-asset constraints as a new set (or as a copy of the loaded one). Persists in the database — visible across browsers and environments."
                    >
                      Save As…
                    </button>
                    {activeConstraintSet && (
                      <button
                        className="preset-btn"
                        style={{ color: '#dc3545', borderColor: '#dc3545' }}
                        onClick={handleDeleteConstraintSet}
                        title={`Delete the saved set "${activeConstraintSet}"`}
                      >
                        Delete
                      </button>
                    )}
                    <button
                      className="preset-btn"
                      onClick={() => {
                        const firstTemplate = Object.keys(savedBatchTemplates).sort()[0] || '';
                        setImportBatchTemplateName(firstTemplate);
                        setImportBatchProfileName(firstTemplate && Object.keys(savedBatchTemplates[firstTemplate]?.batchProfileBounds || {})[0] || 'DSP 70');
                        setShowImportBatchProfile(true);
                      }}
                      disabled={Object.keys(savedBatchTemplates).length === 0}
                      title="Pick a saved batch template and a profile within it to import that profile's per-SAC bounds + growth + fee settings here."
                    >
                      Import from batch profile…
                    </button>
                  </div>

                  <div
                    className={`bounds-toggle ${showConstraints ? 'open' : ''}`}
                    onClick={() => setShowConstraints(!showConstraints)}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                    {showConstraints ? 'Hide' : 'Edit'} Per-Asset Bounds {Object.values(acConstraints).filter((c) => c.mode !== 'none').length > 0 && `(${Object.values(acConstraints).filter((c) => c.mode !== 'none').length} active)`}
                  </div>
                  {showConstraints && (
                    <div className="constraint-table-wrap">
                      <table className="bounds-table">
                        <thead>
                          <tr>
                            <th>Asset</th>
                            <th>Mode</th>
                            <th>Reference</th>
                            <th>Min %</th>
                            <th>Max %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {acCodes.map((code) => {
                            const cfg = acConstraints[code] || DEFAULT_CONSTRAINT;
                            const isAbs = cfg.mode === 'absolute';
                            const isRel = cfg.mode === 'relative';
                            const isNone = cfg.mode === 'none';
                            return (
                              <tr key={code} className={isNone ? 'constraint-row-none' : ''}>
                                <td style={{ fontWeight: 600 }}>{code}</td>
                                <td>
                                  <select
                                    className="constraint-select"
                                    value={cfg.mode}
                                    onChange={(e) => handleConstraintChange(code, 'mode', e.target.value)}
                                  >
                                    <option value="none">None</option>
                                    <option value="absolute">Absolute</option>
                                    <option value="relative">Relative</option>
                                  </select>
                                </td>
                                <td>
                                  <select
                                    className="constraint-select"
                                    value={cfg.reference || 'growth'}
                                    onChange={(e) => handleConstraintChange(code, 'reference', e.target.value)}
                                    disabled={!isRel}
                                  >
                                    {REFERENCE_OPTIONS.map((o) => (
                                      <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    value={isNone ? '' : (isAbs ? (cfg.absMin ?? 0) : (cfg.relMin ?? 0))}
                                    onChange={(e) => handleConstraintChange(code, isAbs ? 'absMin' : 'relMin', e.target.value)}
                                    disabled={isNone}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    value={isNone ? '' : (isAbs ? (cfg.absMax ?? 100) : (cfg.relMax ?? 100))}
                                    onChange={(e) => handleConstraintChange(code, isAbs ? 'absMax' : 'relMax', e.target.value)}
                                    disabled={isNone}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Optimise + Scenario buttons */}
            <div className="optimise-actions">
              <button className="optimise-btn" onClick={handleOptimise} disabled={optimising}>
                {optimising ? (
                  <><span className="spinner-border spinner-border-sm me-2" role="status" />Optimising...</>
                ) : 'Optimise'}
              </button>
              {hasResults && (
                <div className="scenario-actions">
                  {!showScenarioInput ? (
                    <>
                      <button className="scenario-save-btn" onClick={() => setShowScenarioInput(true)} disabled={frozenScenarios.length >= 5}>
                        Save Scenario
                      </button>
                      {cmaScenarios.length > 0 && primaryResult && (
                        <button
                          className="scenario-save-btn"
                          style={{ background: 'var(--success-color)', color: 'var(--primary-color)' }}
                          onClick={() => {
                            const objKey = Object.keys(results)[0];
                            setRegisterObj(objKey);
                            setRegisterName('');
                            setRegisterCode('');
                            setShowRegisterModal(true);
                          }}
                        >
                          Register as Portfolio
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="scenario-input-row">
                      <input
                        className="scenario-name-input"
                        placeholder="Scenario name..."
                        value={scenarioName}
                        onChange={(e) => setScenarioName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveScenario()}
                        autoFocus
                      />
                      <button className="scenario-confirm-btn" onClick={handleSaveScenario}>Save</button>
                      <button className="scenario-cancel-btn" onClick={() => setShowScenarioInput(false)}>Cancel</button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Frozen Scenarios */}
            {frozenScenarios.length > 0 && (
              <div className="frozen-scenarios">
                {frozenScenarios.map((s, i) => {
                  const firstKey = Object.keys(s.results)[0];
                  const firstRes = s.results[firstKey];
                  return (
                    <div key={i} className="frozen-pill">
                      <span className="frozen-pill-name">{s.name}</span>
                      {firstRes && (
                        <span className="frozen-pill-metrics">
                          Ret {(firstRes.portfolio_return * 100).toFixed(1)}% | Vol {(firstRes.portfolio_volatility * 100).toFixed(1)}%
                        </span>
                      )}
                      <button className="frozen-pill-remove" onClick={() => handleRemoveScenario(i)}>x</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Panel - Results */}
          <div>
            {hasResults && (
              <>
                {/* Results Header Bar — prominent Export button */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'var(--primary-color)',
                    color: '#fff',
                    padding: '14px 20px',
                    borderRadius: 6,
                    marginBottom: 16,
                    boxShadow: '0 2px 6px rgba(7,32,64,0.15)',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.1 }}>
                      Optimisation Results
                    </div>
                    <div style={{ fontSize: '0.78rem', opacity: 0.85, marginTop: 2 }}>
                      {Object.keys(results).length} objective{Object.keys(results).length === 1 ? '' : 's'} run
                      {frontierData?.points?.length ? ` · ${frontierData.points.length} frontier points` : ''}
                      {frozenScenarios.length ? ` · ${frozenScenarios.length} frozen scenario${frozenScenarios.length === 1 ? '' : 's'}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {loadedRunId && (
                      <span
                        style={{
                          fontSize: '0.78rem', color: '#fff', opacity: 0.85,
                          background: 'rgba(255,255,255,0.12)', padding: '4px 8px',
                          borderRadius: 4, whiteSpace: 'nowrap',
                        }}
                        title={`This run is "${loadedRunName}". Save overwrites it; Save As makes a copy.`}
                      >
                        Loaded: {loadedRunName}
                      </span>
                    )}
                    <button
                      onClick={handleSaveLoadedRun}
                      disabled={!loadedRunId || savingRun || !loadedRunCanEdit}
                      title={
                        !loadedRunId
                          ? 'No run is currently loaded — use Save As to create a new one'
                          : !loadedRunCanEdit
                            ? `"${loadedRunName}" is read-only or locked — use Save As to make an editable copy`
                            : `Overwrite the loaded run "${loadedRunName}" with the current inputs + results`
                      }
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        backgroundColor: '#fff', color: 'var(--primary-color)',
                        border: '1px solid var(--secondary-color)', padding: '10px 16px',
                        borderRadius: 5, fontWeight: 700, fontSize: '0.88rem',
                        cursor: !loadedRunId || savingRun || !loadedRunCanEdit ? 'not-allowed' : 'pointer',
                        opacity: !loadedRunId || savingRun || !loadedRunCanEdit ? 0.55 : 1,
                      }}
                    >
                      <i className="bi bi-floppy" style={{ fontSize: '1.05rem' }}></i>
                      {savingRun && loadedRunId ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setSaveRunName(loadedRunId ? `${loadedRunName} (copy)` : '');
                        setSaveRunDescription('');
                        setSaveRunFavourite(false);
                        setShowSaveRunModal(true);
                      }}
                      title="Save this optimisation as a new saved run (always creates a copy — won't touch the loaded run if any)"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        backgroundColor: '#fff', color: 'var(--primary-color)',
                        border: '1px solid var(--secondary-color)', padding: '10px 16px',
                        borderRadius: 5, fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer',
                      }}
                    >
                      <i className="bi bi-floppy2" style={{ fontSize: '1.05rem' }}></i>
                      Save As…
                    </button>
                    <button
                      onClick={() => setShowSavedRunsModal(true)}
                      title="Browse, load, favourite, or delete previously-saved optimisation runs"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        backgroundColor: '#fff',
                        color: 'var(--primary-color)',
                        border: '1px solid var(--secondary-color)',
                        padding: '10px 16px',
                        borderRadius: 5,
                        fontWeight: 700,
                        fontSize: '0.88rem',
                        cursor: 'pointer',
                      }}
                    >
                      <i className="bi bi-folder2-open" style={{ fontSize: '1.05rem' }}></i>
                      Load Run
                    </button>
                    <button
                      onClick={handleExportExcel}
                      title="Download a workbook containing every piece of data in this view: summary, weights, risk contributions, CMAs, correlation matrix, constraints, efficient frontier, frozen scenarios, and run status."
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        backgroundColor: 'var(--secondary-color)',
                        color: 'var(--primary-color)',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: 5,
                        fontWeight: 700,
                        fontSize: '0.92rem',
                        cursor: 'pointer',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                      }}
                    >
                      <i className="bi bi-file-earmark-excel" style={{ fontSize: '1.1rem' }}></i>
                      Export Full Results to Excel
                    </button>
                  </div>
                </div>

                {/* Efficient Frontier */}
                {frontierChartData && (
                  <div className="frontier-chart-container">
                    <h4>Efficient Frontier</h4>
                    <ResponsiveContainer width="100%" height={320}>
                      <ComposedChart data={frontierChartData} margin={{ left: 20, right: 30, top: 20, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="vol"
                          type="number"
                          domain={['auto', 'auto']}
                          tickFormatter={(v) => `${(v * 100).toFixed(1)}%`}
                          label={{ value: 'Volatility (%)', position: 'insideBottom', offset: -5, style: { fontSize: 12 } }}
                        />
                        <YAxis
                          dataKey="ret"
                          type="number"
                          domain={['auto', 'auto']}
                          tickFormatter={(v) => `${(v * 100).toFixed(1)}%`}
                          label={{ value: 'Return (%)', angle: -90, position: 'insideLeft', offset: 0, style: { fontSize: 12 } }}
                        />
                        <Tooltip content={<FrontierTooltip />} />
                        <Line dataKey="ret" stroke="#599BE3" strokeWidth={2} dot={{ r: 3, fill: '#599BE3' }} isAnimationActive={false} />
                        {frontierData.max_sharpe_index < frontierChartData.length && (
                          <ReferenceDot
                            x={frontierChartData[frontierData.max_sharpe_index]?.vol}
                            y={frontierChartData[frontierData.max_sharpe_index]?.ret}
                            r={7}
                            fill="#f0a500"
                            stroke="#072040"
                            strokeWidth={2}
                            label={{ value: 'Max Sharpe', position: 'top', fontSize: 11, fontWeight: 600, fill: '#f0a500' }}
                          />
                        )}
                        {frontierData.min_vol_index < frontierChartData.length && (
                          <ReferenceDot
                            x={frontierChartData[frontierData.min_vol_index]?.vol}
                            y={frontierChartData[frontierData.min_vol_index]?.ret}
                            r={7}
                            fill="#072040"
                            stroke="#f0a500"
                            strokeWidth={2}
                            label={{ value: 'Min Vol', position: 'bottom', fontSize: 11, fontWeight: 600, fill: '#072040' }}
                          />
                        )}
                        {/* Plot active results as reference dots */}
                        {Object.entries(results).filter(([k]) => k !== 'max_sharpe' && k !== 'min_vol').map(([key, res]) => (
                          <ReferenceDot
                            key={key}
                            x={res.portfolio_volatility}
                            y={res.portfolio_return}
                            r={6}
                            fill={OBJECTIVE_CONFIG[key]?.color || '#6c757d'}
                            stroke="#fff"
                            strokeWidth={2}
                            label={{ value: OBJECTIVE_CONFIG[key]?.label || key, position: 'right', fontSize: 10, fill: OBJECTIVE_CONFIG[key]?.color }}
                          />
                        ))}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Summary Cards */}
                <div className="results-cards" style={{ gridTemplateColumns: `repeat(${Math.min(Object.keys(results).length, 3)}, 1fr)` }}>
                  {Object.entries(results).map(([key, res]) => (
                    <div key={key} className="result-card">
                      <div className={`result-card-header ${OBJECTIVE_CONFIG[key]?.headerClass || ''}`}
                        style={{ background: OBJECTIVE_CONFIG[key]?.color || '#6c757d', color: key === 'max_sharpe' ? '#072040' : '#fff' }}>
                        {OBJECTIVE_CONFIG[key]?.label || key}
                      </div>
                      <div className="result-card-body">
                        <div className="result-stat">
                          <span className="result-stat-label">Return</span>
                          <span className="result-stat-value">{(res.portfolio_return * 100).toFixed(2)}%</span>
                        </div>
                        <div className="result-stat">
                          <span className="result-stat-label">Volatility</span>
                          <span className="result-stat-value">{(res.portfolio_volatility * 100).toFixed(2)}%</span>
                        </div>
                        <div className="result-stat">
                          <span className="result-stat-label">Sharpe Ratio</span>
                          <span className="result-stat-value">{res.sharpe_ratio.toFixed(2)}</span>
                        </div>
                        <div className="result-stat">
                          <span className="result-stat-label">Growth</span>
                          <span className="result-stat-value">{roundHalf(res.growth_exposure * 100).toFixed(1)}%</span>
                        </div>
                        {!res.success && (
                          <div style={{ color: '#dc3545', fontSize: '0.78rem', marginTop: 6 }}>{res.message}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Weights Bar Chart */}
                <div className="weights-chart-container">
                  <h4>Optimal Weights Comparison</h4>
                  <ResponsiveContainer width="100%" height={420}>
                    <BarChart data={chartData} margin={{ left: 20, right: 20, top: 10, bottom: 70 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        type="category"
                        dataKey="name"
                        interval={0}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis type="number" domain={[0, 'auto']} tickFormatter={(v) => `${v}%`} />
                      <Tooltip formatter={(v) => `${v.toFixed(1)}%`} />
                      <Legend />
                      {Object.entries(results).map(([key]) => (
                        <Bar
                          key={key}
                          dataKey={OBJECTIVE_CONFIG[key]?.label || key}
                          fill={OBJECTIVE_CONFIG[key]?.color || '#6c757d'}
                          radius={[3, 3, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Weights Table */}
                <div className="optimiser-panel" style={{ marginBottom: 24 }}>
                  <div className="optimiser-panel-header">
                    Detailed Weights
                    <button className="reset-btn" onClick={handleExportExcel} style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}>
                      Export
                    </button>
                  </div>
                  <div className="optimiser-panel-body" style={{ padding: 0 }}>
                    <table className="weights-table">
                      <thead>
                        <tr>
                          <th>Asset Class</th>
                          <th>Type</th>
                          {Object.keys(results).map((key) => (
                            <th key={key}>{OBJECTIVE_CONFIG[key]?.label || key} (%)</th>
                          ))}
                          {Object.keys(results).length >= 2 && <th>Difference</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {acCodes.map((code) => {
                          const ac = assetClasses.find((a) => a.code === code);
                          const keys = Object.keys(results);
                          const weights = keys.map((k) => roundHalf((results[k]?.weights[code] || 0) * 100));
                          const diff = keys.length >= 2 ? weights[0] - weights[1] : 0;
                          return (
                            <tr key={code}>
                              <td style={{ fontWeight: 600 }}>{code}</td>
                              <td>
                                <span className={`growth-badge ${ac?.is_overlay ? 'overlay' : ac?.is_growth ? 'growth' : 'defensive'}`}>
                                  {ac?.is_overlay ? 'Overlay' : ac?.is_growth ? 'Growth' : 'Def'}
                                </span>
                              </td>
                              {weights.map((w, i) => (
                                <td key={i}>{w.toFixed(1)}</td>
                              ))}
                              {keys.length >= 2 && (
                                <td style={{ color: diff > 0 ? '#16a34a' : diff < 0 ? '#dc3545' : '#6c757d' }}>
                                  {diff > 0 ? '+' : ''}{diff.toFixed(1)}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Risk Contributions */}
                {riskContribData.length > 0 && (
                  <div className="optimiser-panel" style={{ marginBottom: 24 }}>
                    <div
                      className="optimiser-panel-header"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setShowRiskContribs(!showRiskContribs)}
                    >
                      <span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 8, transition: 'transform 0.2s', transform: showRiskContribs ? 'rotate(90deg)' : '' }}>
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        Risk Contributions ({Object.keys(results)[0] ? OBJECTIVE_CONFIG[Object.keys(results)[0]]?.label : ''})
                      </span>
                    </div>
                    {showRiskContribs && (
                      <div className="optimiser-panel-body" style={{ padding: 0 }}>
                        <div style={{ padding: 20 }}>
                          <ResponsiveContainer width="100%" height={Math.max(200, riskContribData.length * 32)}>
                            <BarChart data={riskContribData} layout="vertical" margin={{ left: 80, right: 20, top: 10, bottom: 10 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" tickFormatter={(v) => `${v}%`} />
                              <YAxis type="category" dataKey="name" width={75} tick={{ fontSize: 12 }} />
                              <Tooltip formatter={(v) => `${v.toFixed(1)}%`} />
                              <Legend />
                              <Bar dataKey="weight" name="Weight %" fill="#599BE3" radius={[0, 3, 3, 0]} />
                              <Bar dataKey="risk" name="Risk %" fill="#f0a500" radius={[0, 3, 3, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        <table className="weights-table">
                          <thead>
                            <tr>
                              <th>Asset Class</th>
                              <th>Weight %</th>
                              <th>Risk %</th>
                              <th>Ratio</th>
                            </tr>
                          </thead>
                          <tbody>
                            {riskContribData.map((d) => (
                              <tr key={d.name}>
                                <td style={{ fontWeight: 600 }}>{d.name}</td>
                                <td>{d.weight.toFixed(1)}</td>
                                <td>{d.risk.toFixed(1)}</td>
                                <td style={{ color: d.ratio > 1.5 ? '#dc3545' : d.ratio < 0.7 ? '#91CF9B' : '#6c757d', fontWeight: 600 }}>
                                  {d.ratio.toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {riskContribData.length > 0 && (() => {
                          const topRisk = [...riskContribData].sort((a, b) => b.risk - a.risk)[0];
                          if (topRisk) {
                            return (
                              <div className="risk-insight">
                                {topRisk.name} contributes {topRisk.risk.toFixed(1)}% of risk with {topRisk.weight.toFixed(1)}% weight
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                  </div>
                )}

              </>
            )}

            {/* Scenario Comparison — always visible when scenarios exist */}
            {frozenScenarios.length > 0 && (() => {
                  const firstKey = hasResults ? Object.keys(results)[0] : null;
                  const currentRes = firstKey ? results[firstKey] : null;
                  // Collect all columns: current (if exists) + each frozen scenario
                  const columns = [];
                  if (currentRes) columns.push({ label: 'Current', res: currentRes, isCurrent: true });
                  frozenScenarios.forEach((s) => {
                    const sKey = Object.keys(s.results)[0];
                    if (sKey) columns.push({ label: s.name, res: s.results[sKey], isCurrent: false });
                  });
                  // Show all ACs that have >0.1% weight in ANY column
                  const visibleACs = acCodes.filter((code) =>
                    columns.some((col) => (col.res?.weights[code] || 0) * 100 > 0.1)
                  );
                  return (
                    <div className="optimiser-panel" style={{ marginBottom: 24 }}>
                      <div className="optimiser-panel-header">Scenario Comparison</div>
                      <div className="optimiser-panel-body" style={{ padding: 0, overflowX: 'auto' }}>
                        <table className="weights-table scenario-table">
                          <thead>
                            <tr>
                              <th>Metric</th>
                              {columns.map((col, ci) => (
                                <th key={ci} style={col.isCurrent ? { background: '#f0a500', color: '#072040' } : {}}>{col.label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {[
                              { label: 'Return (%)', fn: (r) => r.portfolio_return * 100, fmt: 1 },
                              { label: 'Volatility (%)', fn: (r) => r.portfolio_volatility * 100, fmt: 1 },
                              { label: 'Sharpe Ratio', fn: (r) => r.sharpe_ratio, fmt: 2 },
                              { label: 'Growth (%)', fn: (r) => roundHalf(r.growth_exposure * 100), fmt: 1 },
                            ].map(({ label, fn, fmt }) => {
                              const vals = columns.map((col) => col.res ? fn(col.res) : null);
                              const baseVal = vals[0];
                              return (
                                <tr key={label}>
                                  <td style={{ fontWeight: 600 }}>{label}</td>
                                  {vals.map((v, vi) => {
                                    const diff = vi > 0 && baseVal != null && v != null ? v - baseVal : null;
                                    const isRetOrSharpe = label.startsWith('Return') || label.startsWith('Sharpe');
                                    const isVol = label.startsWith('Vol');
                                    const isGood = diff != null && (isRetOrSharpe ? diff > 0 : isVol ? diff < 0 : false);
                                    const isBad = diff != null && (isRetOrSharpe ? diff < 0 : isVol ? diff > 0 : false);
                                    return (
                                      <td key={vi}>
                                        {v != null ? v.toFixed(fmt) : '-'}
                                        {diff != null && Math.abs(diff) > 0.01 && (
                                          <span style={{ fontSize: '0.72rem', marginLeft: 4, color: isGood ? '#16a34a' : isBad ? '#dc3545' : '#6c757d' }}>
                                            ({diff > 0 ? '+' : ''}{diff.toFixed(fmt)})
                                          </span>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                            <tr><td colSpan={1 + columns.length} style={{ padding: '4px', background: '#f8f9fa' }}></td></tr>
                            {visibleACs.map((code) => {
                              const vals = columns.map((col) => roundHalf((col.res?.weights[code] || 0) * 100));
                              const baseVal = vals[0];
                              return (
                                <tr key={code}>
                                  <td style={{ fontWeight: 600 }}>{code}</td>
                                  {vals.map((v, vi) => {
                                    const diff = vi > 0 ? v - baseVal : null;
                                    return (
                                      <td key={vi}>
                                        {v.toFixed(1)}
                                        {diff != null && Math.abs(diff) > 0.1 && (
                                          <span style={{ fontSize: '0.72rem', marginLeft: 4, color: diff > 0 ? '#16a34a' : diff < 0 ? '#dc3545' : '#6c757d' }}>
                                            ({diff > 0 ? '+' : ''}{diff.toFixed(1)})
                                          </span>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

            {/* Correlation Heatmap */}
            {Object.keys(corrMatrix).length > 0 && (
              <div className="corr-heatmap-container">
                <h4>Asset Class Correlation Matrix</h4>
                <table className="corr-table">
                  <thead>
                    <tr>
                      <th className="row-header"></th>
                      {acCodes.map((code) => (
                        <th key={code}>{code}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {acCodes.map((row) => (
                      <tr key={row}>
                        <th className="row-header">{row}</th>
                        {acCodes.map((col) => {
                          const val = corrMatrix[row]?.[col] ?? 0;
                          return (
                            <td
                              key={col}
                              style={{
                                background: corrColor(val),
                                color: Math.abs(val) > 0.5 ? '#fff' : '#222',
                              }}
                            >
                              {val.toFixed(2)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!hasResults && !optimising && (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6c757d' }}>
                <p style={{ fontSize: '1.1rem', marginBottom: 4 }}>Configure constraints and click Optimise</p>
                <p style={{ fontSize: '0.85rem' }}>Results will appear here</p>
              </div>
            )}
          </div>
        </div>
      )}
      {activeTab === 'batch' && (
        /* Batch Tab */
        <div className="batch-layout">
          {/* Row 1: Profile selection + run controls (compact) */}
          <div className="optimiser-panel">
            <div className="optimiser-panel-header">Profiles &amp; Run</div>
            <div className="optimiser-panel-body">
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ minWidth: 280 }}>
                  <div className="constraint-label">DSP Profiles in Batch</div>
                  <div className="batch-profiles-grid">
                    {ALL_DSP_PROFILES.map((p) => (
                      <label key={p} className="batch-profile-check">
                        <input
                          type="checkbox"
                          checked={batchProfiles[p]}
                          onChange={() => setBatchProfiles((prev) => ({ ...prev, [p]: !prev[p] }))}
                        />
                        <span>{p}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div style={{ minWidth: 180 }}>
                  <div className="constraint-label">Objective</div>
                  <select
                    className="constraint-select"
                    style={{ width: 180 }}
                    value={batchObjective}
                    onChange={(e) => setBatchObjective(e.target.value)}
                    title="Objective applied to every portfolio in the batch"
                  >
                    <option value="max_sharpe">Max Sharpe</option>
                    <option value="min_vol">Min Vol</option>
                    <option value="risk_parity">Risk Parity</option>
                    <option value="hrp">HRP (Hierarchical Risk Parity)</option>
                  </select>
                  <div style={{ marginTop: 10, fontSize: '0.78rem' }}>
                    <label style={{ color: '#6c757d', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>Diversification λ:</span>
                      <strong style={{ minWidth: 30, textAlign: 'right' }}>{batchDiversificationLambda.toFixed(2)}</strong>
                    </label>
                    <input
                      type="range"
                      min="0" max="1" step="0.05"
                      value={batchDiversificationLambda}
                      onChange={(e) => setBatchDiversificationLambda(parseFloat(e.target.value))}
                      style={{ width: '100%' }}
                      title="HHI concentration penalty on the objective. 0 = pure mean-variance (concentrated). 0.3-0.5 typically spreads weight nicely. 1.0 ≈ equal-weight. Only applies to Max Sharpe / Min Vol / Target objectives — Risk Parity and HRP ignore it."
                      disabled={batchObjective === 'risk_parity' || batchObjective === 'hrp'}
                    />
                  </div>
                  <button
                    className="optimise-btn"
                    style={{ marginTop: 14, width: '100%' }}
                    onClick={handleRunBatch}
                    disabled={batchLoading}
                  >
                    {batchLoading ? (
                      <><span className="spinner-border spinner-border-sm me-2" role="status" />Running…</>
                    ) : 'Run Batch'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Per-Portfolio Constraints Matrix (full width) */}
          {(() => {
            const selected = ALL_DSP_PROFILES.filter((p) => batchProfiles[p]);
            if (selected.length === 0) return null;
            return (
              <div className="optimiser-panel">
                <div className="optimiser-panel-header">
                  Per-Portfolio Constraints
                </div>
                <div className="optimiser-panel-body" style={{ padding: 0 }}>
                  <div style={{ padding: '8px 12px' }}>
                    <p className="text-muted mb-2" style={{ fontSize: '0.78rem' }}>
                      Set constraints per portfolio. Blank cells leave that constraint unset for the profile. Per-SAC bounds below let you cap or floor individual asset classes.
                    </p>

                    {/* Saved Batch Templates bar */}
                    <div
                      style={{
                        display: 'flex',
                        gap: 6,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        background: '#f8f9fa',
                        padding: '6px 8px',
                        borderRadius: 4,
                        marginBottom: 8,
                      }}
                    >
                      <label style={{ fontSize: '0.78rem', color: '#6c757d', margin: 0, fontWeight: 600 }}>
                        Batch Template:
                      </label>
                      <select
                        className="constraint-select"
                        style={{ minWidth: 180, fontSize: '0.78rem' }}
                        value={activeBatchTemplate}
                        onChange={(e) => handleLoadBatchTemplate(e.target.value)}
                        title="Load a previously-saved batch template. Restores profile selection, objective, growth/fee/asset-set overrides, and all per-SAC bounds."
                      >
                        <option value="">-- none --</option>
                        {Object.keys(savedBatchTemplates).sort().map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      <button
                        className="preset-btn"
                        onClick={handleSaveBatchTemplate}
                        disabled={!activeBatchTemplate}
                        title={
                          activeBatchTemplate
                            ? `Overwrite the currently loaded template "${activeBatchTemplate}" with the current matrix configuration`
                            : 'No template is currently loaded — use Save As… instead'
                        }
                      >
                        Save
                      </button>
                      <button
                        className="preset-btn"
                        onClick={handleSaveAsBatchTemplate}
                        title="Save the current batch matrix configuration as a new template (or as a copy of the loaded one)"
                      >
                        Save As…
                      </button>
                      <button
                        className="preset-btn"
                        onClick={handleResetDsp2026Template}
                        title="Rebuild and load the 'DSP 2026 Defaults' template — mirrors the constraints in Portfolio Modelling/Portfolio Optimisation 2026.py. Use this to restore the script's setup if you've made unwanted changes."
                      >
                        Reset DSP 2026
                      </button>
                      <button
                        className="preset-btn"
                        onClick={() => loadCmaData(selectedScenarioId)}
                        title="Reload sub-asset classes from the selected CMA scenario. Click after adding a new SAC in the CMA admin tab."
                      >
                        <i className="bi bi-arrow-clockwise me-1"></i>Reload SACs
                      </button>
                      <button
                        className="preset-btn"
                        onClick={handleResetSacConstraints}
                        title="Clear every per-SAC mode/min/max across every profile. Growth Min/Max and Fee Budget rows are not touched."
                        style={{ color: '#dc3545', borderColor: '#dc3545' }}
                      >
                        <i className="bi bi-eraser me-1"></i>Reset Constraints
                      </button>
                      {activeBatchTemplate && (
                        <button
                          className="preset-btn"
                          style={{ color: '#dc3545', borderColor: '#dc3545' }}
                          onClick={handleDeleteBatchTemplate}
                          title={`Delete saved template "${activeBatchTemplate}"`}
                        >
                          Delete
                        </button>
                      )}
                      {activeBatchTemplate && (
                        <span
                          className={`badge ${batchTemplateDirty ? 'bg-warning text-dark' : 'bg-info text-dark'}`}
                          style={{ fontSize: '0.7rem' }}
                          title={batchTemplateDirty ? 'You have unsaved changes — click Save to overwrite the loaded template' : ''}
                        >
                          Loaded: {activeBatchTemplate}{batchTemplateDirty ? ' (modified)' : ''}
                        </span>
                      )}
                    </div>

                  </div>
                  <div style={{ overflowX: 'auto', padding: '0 4px 8px' }}>
                    <table className="batch-constraint-matrix">
                      <thead>
                        <tr>
                          <th className="row-label">&nbsp;</th>
                          {selected.map((p) => (
                            <th key={p}>{p}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Growth Min row */}
                        <tr>
                          <td className="row-label" title="Growth allocation lower bound in %. If both Min and Max are blank, the DSP profile default applies. If both are set and equal, treated as an exact target.">
                            Growth Min %
                          </td>
                          {selected.map((p) => {
                            const dflt = DSP_DEFAULTS[p]?.growth_pct;
                            return (
                              <td key={p}>
                                <input
                                  type="number"
                                  className="constraint-select matrix-input"
                                  min="0" max="100" step="1"
                                  placeholder={dflt != null ? `${dflt}` : ''}
                                  value={batchProfileGrowthMin[p] ?? ''}
                                  onChange={(e) => { setBatchProfileGrowthMin((prev) => ({ ...prev, [p]: e.target.value })); markBatchTemplateDirty(); }}
                                  title={`Growth lower bound for ${p} in %. Default ${dflt ?? 'DSP'}%. Blank inherits.`}
                                />
                              </td>
                            );
                          })}
                        </tr>

                        {/* Growth Max row */}
                        <tr>
                          <td className="row-label" title="Growth allocation upper bound in %. Pair with Growth Min to express a range.">
                            Growth Max %
                          </td>
                          {selected.map((p) => {
                            const dflt = DSP_DEFAULTS[p]?.growth_pct;
                            return (
                              <td key={p}>
                                <input
                                  type="number"
                                  className="constraint-select matrix-input"
                                  min="0" max="100" step="1"
                                  placeholder={dflt != null ? `${dflt}` : ''}
                                  value={batchProfileGrowthMax[p] ?? ''}
                                  onChange={(e) => { setBatchProfileGrowthMax((prev) => ({ ...prev, [p]: e.target.value })); markBatchTemplateDirty(); }}
                                  title={`Growth upper bound for ${p} in %. Default ${dflt ?? 'DSP'}%. Blank inherits.`}
                                />
                              </td>
                            );
                          })}
                        </tr>

                        {/* Fee Budget row */}
                        <tr>
                          <td className="row-label" title="Fee budget (maximum) in basis points. Blank inherits the DSP profile's default. Optimiser enforces (sum of weight × fee) ≤ budget.">
                            Fee Budget (bps)
                          </td>
                          {selected.map((p) => {
                            const dflt = DSP_DEFAULTS[p]?.fee_bps;
                            return (
                              <td key={p}>
                                <input
                                  type="number"
                                  className="constraint-select matrix-input"
                                  min="0" step="5"
                                  placeholder={dflt != null ? `${dflt}` : ''}
                                  value={batchProfileFee[p] ?? ''}
                                  onChange={(e) => { setBatchProfileFee((prev) => ({ ...prev, [p]: e.target.value })); markBatchTemplateDirty(); }}
                                  title={`Fee budget override for ${p} in bps. Default ${dflt ?? 'DSP'} bps. Blank inherits.`}
                                />
                              </td>
                            );
                          })}
                        </tr>

                        {/* Per-SAC bound rows */}
                        {assetClasses.map((ac) => (
                          <tr key={`bound-${ac.code}`}>
                            <td className="row-label" title={`${ac.code} min / max. Mode dropdown selects whether bounds are absolute (% of portfolio) or relative to growth / defensive / international-equity exposure.`}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <span style={{
                                  display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                                  background: ac.is_overlay ? '#6c757d' : (ac.is_growth ? '#91CF9B' : '#599BE3'),
                                }} />
                                {ac.code}
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handlePropagateSacAcrossRow(ac.code); }}
                                  disabled={selected.length < 2}
                                  title={`Propagate ${ac.code}'s ${selected[0] || 'first'}-profile bound to every other selected portfolio in this row`}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#599BE3',
                                    fontSize: '0.8rem',
                                    padding: '0 2px',
                                    cursor: selected.length < 2 ? 'not-allowed' : 'pointer',
                                    opacity: selected.length < 2 ? 0.3 : 1,
                                    lineHeight: 1,
                                  }}
                                >
                                  ⇒
                                </button>
                              </span>
                            </td>
                            {selected.map((p) => {
                              const cell = (batchProfileBounds[p] || {})[ac.code] || {};
                              const mode = cell.mode || 'absolute';
                              return (
                                <td key={p} style={{ padding: '3px 4px' }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <select
                                      className="constraint-select"
                                      style={{ width: '100%', fontSize: '0.7rem', padding: '1px 2px' }}
                                      value={mode}
                                      onChange={(e) => setBoundCell(p, ac.code, 'mode', e.target.value)}
                                      title={`Bounds reference for ${ac.code} in ${p}. 'Absolute' = % of portfolio. Others = % of that group's total weight (e.g. 'EQ_AU max 40% of Growth').`}
                                    >
                                      <option value="absolute">Absolute %</option>
                                      <option value="growth">% Growth</option>
                                      <option value="defensive">% Defensive</option>
                                      <option value="intl_equity">% EQ_WORLD</option>
                                    </select>
                                    <div style={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
                                      <input
                                        type="number"
                                        className="constraint-select"
                                        style={{ width: 48, padding: '2px 3px', textAlign: 'right', fontSize: '0.7rem' }}
                                        min="0" max={mode === 'absolute' ? '100' : '500'} step="1"
                                        placeholder="min"
                                        value={cell.min ?? ''}
                                        onChange={(e) => setBoundCell(p, ac.code, 'min', e.target.value)}
                                        title={`${ac.code} min ${mode === 'absolute' ? 'weight' : `as % of ${mode === 'intl_equity' ? 'intl equity' : mode}`} for ${p}.`}
                                      />
                                      <span style={{ fontSize: '0.65rem', color: '#999' }}>/</span>
                                      <input
                                        type="number"
                                        className="constraint-select"
                                        style={{ width: 48, padding: '2px 3px', textAlign: 'right', fontSize: '0.7rem' }}
                                        min="0" max={mode === 'absolute' ? '100' : '500'} step="1"
                                        placeholder="max"
                                        value={cell.max ?? ''}
                                        onChange={(e) => setBoundCell(p, ac.code, 'max', e.target.value)}
                                        title={`${ac.code} max ${mode === 'absolute' ? 'weight' : `as % of ${mode === 'intl_equity' ? 'intl equity' : mode}`} for ${p}.`}
                                      />
                                    </div>
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}

                        {/* Copy across row */}
                        <tr>
                          <td className="row-label" title="Copy this column's per-SAC bounds to every other selected portfolio. Growth Min/Max and Fee Budget are profile-specific and intentionally excluded.">
                            Copy across
                          </td>
                          {selected.map((p) => (
                            <td key={p} style={{ textAlign: 'center' }}>
                              <button
                                className="preset-btn"
                                style={{ width: '100%', fontSize: '0.72rem', padding: '4px 6px' }}
                                onClick={() => handleCopyProfileSetToAll(p)}
                                disabled={selected.length < 2}
                                title={`Copy ${p}'s per-SAC bounds to every other selected portfolio. Growth Min/Max and Fee Budget stay profile-specific.`}
                              >
                                Copy → all
                              </button>
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}

          {batchResults && (() => {
            const profiles = Object.keys(batchResults);
            return (
              <div className="batch-results">
                {/* Results Header Bar — Save / Load / Export prominent */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'var(--primary-color)',
                    color: '#fff',
                    padding: '14px 20px',
                    borderRadius: 6,
                    marginBottom: 16,
                    boxShadow: '0 2px 6px rgba(7,32,64,0.15)',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.1 }}>
                      Batch Results
                    </div>
                    <div style={{ fontSize: '0.78rem', opacity: 0.85, marginTop: 2 }}>
                      {profiles.length} portfolio{profiles.length === 1 ? '' : 's'} optimised · objective: {batchObjective.replace('_', ' ')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {loadedRunId && (
                      <span
                        style={{
                          fontSize: '0.78rem', color: '#fff', opacity: 0.85,
                          background: 'rgba(255,255,255,0.12)', padding: '4px 8px',
                          borderRadius: 4, whiteSpace: 'nowrap',
                        }}
                        title={`This batch is the loaded run "${loadedRunName}". Save overwrites it in place; Save As makes a copy.`}
                      >
                        Loaded: {loadedRunName}
                      </span>
                    )}
                    <button
                      onClick={handleSaveLoadedRun}
                      disabled={!loadedRunId || savingRun || !loadedRunCanEdit}
                      title={
                        !loadedRunId
                          ? 'No run is currently loaded — use Save As to create a new one'
                          : !loadedRunCanEdit
                            ? `"${loadedRunName}" is read-only or locked — use Save As to make an editable copy`
                            : `Overwrite the loaded run "${loadedRunName}" with the current inputs + results`
                      }
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        backgroundColor: '#fff', color: 'var(--primary-color)',
                        border: '1px solid var(--secondary-color)', padding: '10px 16px',
                        borderRadius: 5, fontWeight: 700, fontSize: '0.88rem',
                        cursor: !loadedRunId || savingRun || !loadedRunCanEdit ? 'not-allowed' : 'pointer',
                        opacity: !loadedRunId || savingRun || !loadedRunCanEdit ? 0.55 : 1,
                      }}
                    >
                      <i className="bi bi-floppy" style={{ fontSize: '1.05rem' }}></i>
                      {savingRun && loadedRunId ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setSaveRunName(loadedRunId ? `${loadedRunName} (copy)` : '');
                        setSaveRunDescription('');
                        setSaveRunFavourite(false);
                        setShowSaveRunModal(true);
                      }}
                      title="Save this batch as a new saved run (always creates a copy — won't touch the loaded run if any)"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        backgroundColor: '#fff', color: 'var(--primary-color)',
                        border: '1px solid var(--secondary-color)', padding: '10px 16px',
                        borderRadius: 5, fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer',
                      }}
                    >
                      <i className="bi bi-floppy2" style={{ fontSize: '1.05rem' }}></i>
                      Save As…
                    </button>
                    <button
                      onClick={() => setShowSavedRunsModal(true)}
                      title="Browse, load, favourite, or delete previously-saved runs (both single and batch)"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        backgroundColor: '#fff', color: 'var(--primary-color)',
                        border: '1px solid var(--secondary-color)', padding: '10px 16px',
                        borderRadius: 5, fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer',
                      }}
                    >
                      <i className="bi bi-folder2-open" style={{ fontSize: '1.05rem' }}></i>
                      Load Batch
                    </button>
                    <button
                      onClick={openBatchExportPicker}
                      title="Select one or more saved batch runs and export a full workbook (scenario, CMAs, correlation, constraints, sub-asset classes and results per run, plus a cross-run comparison)"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        backgroundColor: 'var(--secondary-color)', color: 'var(--primary-color)',
                        border: 'none', padding: '10px 20px', borderRadius: 5,
                        fontWeight: 700, fontSize: '0.92rem', cursor: 'pointer',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                      }}
                    >
                      <i className="bi bi-file-earmark-excel" style={{ fontSize: '1.1rem' }}></i>
                      Export Batch to Excel
                    </button>
                  </div>
                </div>

                {/* Risk/Return Scatter — each profile as a point, plus optional SAC dots */}
                {batchScatterData && batchScatterData.length > 0 && (
                  <div className="frontier-chart-container">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <h4 style={{ margin: 0 }}>Portfolios in Risk/Return Space</h4>
                      <label
                        className="form-check form-switch"
                        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', margin: 0 }}
                        title="Show each individual sub-asset class as a small grey dot for reference. Growth assets are circles, defensive assets are triangles."
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={showAssetClassDots}
                          onChange={(e) => setShowAssetClassDots(e.target.checked)}
                          style={{ margin: 0 }}
                        />
                        <span>Show asset classes</span>
                      </label>
                    </div>
                    <ResponsiveContainer width="100%" height={360}>
                      <ScatterChart margin={{ left: 20, right: 30, top: 20, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="vol"
                          type="number"
                          domain={['auto', 'auto']}
                          tickFormatter={(v) => `${(v * 100).toFixed(1)}%`}
                          label={{ value: 'Volatility (%)', position: 'insideBottom', offset: -5, style: { fontSize: 12 } }}
                        />
                        <YAxis
                          dataKey="ret"
                          type="number"
                          domain={['auto', 'auto']}
                          tickFormatter={(v) => `${(v * 100).toFixed(1)}%`}
                          label={{ value: 'Return (%)', angle: -90, position: 'insideLeft', offset: 0, style: { fontSize: 12 } }}
                        />
                        <Tooltip
                          cursor={{ strokeDasharray: '3 3' }}
                          content={({ payload }) => {
                            if (!payload?.[0]) return null;
                            const d = payload[0].payload;
                            return (
                              <div style={{ background: '#fff', border: '1px solid #ccc', padding: 8, fontSize: 12 }}>
                                <div style={{ fontWeight: 700 }}>{d.name}</div>
                                <div>Return: {(d.ret * 100).toFixed(2)}%</div>
                                <div>Vol: {(d.vol * 100).toFixed(2)}%</div>
                                {d.sharpe != null && <div>Sharpe: {d.sharpe?.toFixed(3)}</div>}
                                {d.is_growth != null && (
                                  <div style={{ color: '#6c757d' }}>{d.is_growth ? 'Growth asset' : 'Defensive asset'}</div>
                                )}
                              </div>
                            );
                          }}
                        />
                        {/* Asset class dots — drawn first so portfolio dots layer on top */}
                        {showAssetClassDots && acScatterData.length > 0 && (
                          <>
                            <Scatter
                              name="Growth assets"
                              data={acScatterData.filter((d) => d.is_growth)}
                              fill="#91CF9B"
                              stroke="#5e8c66"
                              shape="circle"
                            />
                            <Scatter
                              name="Defensive assets"
                              data={acScatterData.filter((d) => !d.is_growth)}
                              fill="#E7E6E6"
                              stroke="#999"
                              shape="triangle"
                            />
                            {acScatterData.map((d) => (
                              <ReferenceDot
                                key={`ac-${d.name}`}
                                x={d.vol}
                                y={d.ret}
                                r={0}
                                label={{
                                  value: d.name,
                                  position: 'top',
                                  fontSize: 9,
                                  fill: '#6c757d',
                                }}
                              />
                            ))}
                          </>
                        )}
                        {/* Portfolio dots — drawn on top */}
                        <Scatter name="Portfolios" data={batchScatterData} fill="#072040" shape="circle" />
                        {batchScatterData.map((d, i) => (
                          <ReferenceDot
                            key={d.name}
                            x={d.vol}
                            y={d.ret}
                            r={7}
                            fill={BATCH_COLOURS[i % BATCH_COLOURS.length]}
                            stroke="#fff"
                            strokeWidth={2}
                            label={{ value: d.name, position: 'right', fontSize: 10, fill: BATCH_COLOURS[i % BATCH_COLOURS.length], fontWeight: 600 }}
                          />
                        ))}
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Summary Cards — one per profile */}
                <div
                  className="results-cards"
                  style={{
                    gridTemplateColumns: `repeat(${Math.min(profiles.length, 4)}, 1fr)`,
                    marginBottom: 16,
                  }}
                >
                  {profiles.map((profile, i) => {
                    const r = batchResults[profile];
                    const colour = BATCH_COLOURS[i % BATCH_COLOURS.length];
                    return (
                      <div key={profile} className="result-card">
                        <div
                          className="result-card-header"
                          style={{ background: colour, color: '#fff' }}
                        >
                          {profile}
                        </div>
                        <div className="result-card-body">
                          <div className="result-stat">
                            <span className="result-stat-label">Return</span>
                            <span className="result-stat-value">{(r.portfolio_return * 100).toFixed(2)}%</span>
                          </div>
                          <div className="result-stat">
                            <span className="result-stat-label">Volatility</span>
                            <span className="result-stat-value">{(r.portfolio_volatility * 100).toFixed(2)}%</span>
                          </div>
                          <div className="result-stat">
                            <span className="result-stat-label">Sharpe</span>
                            <span className="result-stat-value">{r.sharpe_ratio?.toFixed(2)}</span>
                          </div>
                          <div className="result-stat">
                            <span className="result-stat-label">Growth</span>
                            <span className="result-stat-value">{roundHalf(r.growth_exposure * 100).toFixed(1)}%</span>
                          </div>
                          {!r.success && (
                            <div style={{ color: '#dc3545', fontSize: '0.78rem', marginTop: 6 }}>{r.message}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Weights Bar Chart — assets on X, profile as series */}
                {batchWeightsChartData.length > 0 && (
                  <div className="weights-chart-container">
                    <h4>Optimal Weights by Portfolio</h4>
                    <ResponsiveContainer width="100%" height={420}>
                      <BarChart data={batchWeightsChartData} margin={{ left: 20, right: 20, top: 10, bottom: 70 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          type="category"
                          dataKey="name"
                          interval={0}
                          angle={-45}
                          textAnchor="end"
                          height={70}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis type="number" domain={[0, 'auto']} tickFormatter={(v) => `${v}%`} />
                        <Tooltip formatter={(v) => `${v.toFixed(1)}%`} />
                        <Legend />
                        {profiles.map((profile, i) => (
                          <Bar
                            key={profile}
                            dataKey={profile}
                            fill={BATCH_COLOURS[i % BATCH_COLOURS.length]}
                            radius={[3, 3, 0, 0]}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Detailed Weights / Metrics Table */}
                <div className="optimiser-panel">
                  <div className="optimiser-panel-header">
                    DSP Profile Comparison
                  </div>
                  <div className="optimiser-panel-body" style={{ padding: 0, overflowX: 'auto' }}>
                    <table className="weights-table batch-table">
                      <thead>
                        <tr>
                          <th>Metric</th>
                          {profiles.map((p) => (
                            <th key={p}>{p}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Summary rows */}
                        {[
                          { label: 'Return (%)', fn: (r) => (r.portfolio_return * 100).toFixed(2) },
                          { label: 'Volatility (%)', fn: (r) => (r.portfolio_volatility * 100).toFixed(2) },
                          { label: 'Sharpe Ratio', fn: (r) => r.sharpe_ratio.toFixed(3) },
                          { label: 'Growth (%)', fn: (r) => roundHalf(r.growth_exposure * 100).toFixed(1) },
                        ].map(({ label, fn }) => (
                          <tr key={label} className="batch-summary-row">
                            <td style={{ fontWeight: 700 }}>{label}</td>
                            {profiles.map((p) => (
                              <td key={p} style={{ fontWeight: 600 }}>{fn(batchResults[p])}</td>
                            ))}
                          </tr>
                        ))}
                        <tr><td colSpan={1 + profiles.length} style={{ padding: '4px', background: '#f8f9fa' }}></td></tr>
                        {/* Weight rows */}
                        {acCodes.map((code) => {
                          const vals = profiles.map((p) => roundHalf((batchResults[p].weights[code] || 0) * 100));
                          const maxVal = Math.max(...vals);
                          if (maxVal < 0.1) return null;
                          return (
                            <tr key={code}>
                              <td style={{ fontWeight: 600 }}>{code}</td>
                              {vals.map((v, i) => (
                                <td key={i} style={{
                                  background: v > 0.1 ? `rgba(89, 155, 227, ${Math.min(v / maxVal * 0.3, 0.3)})` : 'transparent',
                                }}>
                                  {v.toFixed(1)}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
      {activeTab === 'blend_batches' && (
        /* Blend Batches Tab */
        <div className="batch-layout">
          <div className="optimiser-panel">
            <div className="optimiser-panel-header">Select Saved Batches</div>
            <div className="optimiser-panel-body">
              <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: 12 }}>
                Pick two or more saved <strong>batch</strong> runs and assign relative weights.
                The blend computes a weighted average of the SAC vectors for every DSP profile that appears in all selected batches.
                Return / vol / Sharpe / growth are recomputed live from the current default CMA scenario.
              </p>
              <div style={{ marginBottom: 12 }}>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => { setBlendPickerChecked({}); setShowBlendPicker(true); fetchSavedRuns(); }}
                  style={{ marginRight: 8 }}
                >
                  <i className="bi bi-plus-circle"></i> Add saved batch
                </button>
                <button
                  className="btn btn-success btn-sm"
                  disabled={blendLoading || blendSelectedRuns.length === 0}
                  onClick={runBlendBatches}
                  style={{ marginRight: 8 }}
                >
                  {blendLoading ? 'Computing…' : 'Compute Blend'}
                </button>
                <button
                  className="btn btn-sm"
                  disabled={!blendResults || Object.keys(blendResults || {}).length === 0}
                  onClick={handleExportBlendBatches}
                  title="Export the blended profiles + source-batch comparison to Excel"
                  style={{
                    backgroundColor: 'var(--secondary-color)', color: 'var(--primary-color)',
                    border: 'none', fontWeight: 700,
                  }}
                >
                  <i className="bi bi-file-earmark-excel" style={{ marginRight: 6 }}></i>
                  Export to Excel
                </button>
              </div>

              {blendSelectedRuns.length === 0 ? (
                <div className="text-muted" style={{ fontSize: '0.85rem', padding: '12px 0' }}>
                  No batches selected yet — click "Add saved batch" to begin.
                </div>
              ) : (
                <table className="weights-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '45%' }}>Saved batch</th>
                      <th style={{ width: '20%' }}>Weight</th>
                      <th style={{ width: '20%' }}>% of blend</th>
                      <th style={{ width: '15%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const total = blendSelectedRuns.reduce((s, r) => s + (Number(r.weight) || 0), 0);
                      return blendSelectedRuns.map((r) => {
                        const pct = total > 0 ? ((Number(r.weight) || 0) / total) * 100 : 0;
                        return (
                          <tr key={r.run_id}>
                            <td>{r.name}</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                min="0"
                                step="0.1"
                                value={r.weight}
                                onChange={(e) => updateBlendWeight(r.run_id, e.target.value)}
                                style={{ width: 100 }}
                              />
                            </td>
                            <td>{pct.toFixed(1)}%</td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removeBlendRun(r.run_id)}
                                title="Remove"
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              )}

              {blendError && (
                <div className="optimiser-error" style={{ marginTop: 12 }}>{blendError}</div>
              )}
            </div>
          </div>

          {/* Results */}
          {blendResults && Object.keys(blendResults).length > 0 && (() => {
            const profiles = Object.keys(blendResults);
            // Build weights-by-asset rows for the bar chart, mirroring the batch tab.
            const chartData = acCodes
              .map((code) => {
                const row = { name: code };
                let maxVal = 0;
                profiles.forEach((p) => {
                  const v = roundHalf((blendResults[p].weights[code] || 0) * 100);
                  row[p] = v;
                  if (v > maxVal) maxVal = v;
                });
                row.__max = maxVal;
                return row;
              })
              .filter((row) => row.__max >= 0.1);

            return (
              <div className="optimiser-panel" style={{ marginTop: 12 }}>
                <div className="optimiser-panel-header">Blended Allocation</div>
                <div className="optimiser-panel-body">
                  {/* Summary cards per profile (inline-styled for guaranteed contrast) */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 12,
                    marginBottom: 16,
                  }}>
                    {profiles.map((p) => {
                      const r = blendResults[p];
                      const cellStyle = {
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '6px 12px',
                        borderBottom: '1px solid #e9ecef',
                        fontSize: '0.85rem',
                      };
                      const labelStyle = { color: '#6c757d' };
                      const valueStyle = { fontWeight: 600, color: '#072040' };
                      return (
                        <div key={p} style={{
                          border: '1px solid #d6dae0',
                          borderRadius: 4,
                          background: '#ffffff',
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            background: '#072040',
                            color: '#ffffff',
                            padding: '8px 12px',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                          }}>{p}</div>
                          <div>
                            <div style={cellStyle}><span style={labelStyle}>Return</span><span style={valueStyle}>{(r.portfolio_return * 100).toFixed(2)}%</span></div>
                            <div style={cellStyle}><span style={labelStyle}>Volatility</span><span style={valueStyle}>{(r.portfolio_volatility * 100).toFixed(2)}%</span></div>
                            <div style={cellStyle}><span style={labelStyle}>Sharpe</span><span style={valueStyle}>{r.sharpe_ratio.toFixed(3)}</span></div>
                            <div style={{ ...cellStyle, borderBottom: 'none' }}><span style={labelStyle}>Growth</span><span style={valueStyle}>{roundHalf(r.growth_exposure * 100).toFixed(1)}%</span></div>
                            {!r.success && (
                              <div style={{ color: '#dc3545', fontSize: '0.78rem', padding: '4px 12px 8px' }}>{r.message}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Per-profile source SAC comparison */}
                  {Object.keys(blendPerProfileSources).length > 0 && (
                    <div style={{ marginTop: 24, marginBottom: 16, border: '1px solid #d6dae0', borderRadius: 4, background: '#ffffff' }}>
                      <div style={{
                        background: '#072040', color: '#ffffff',
                        padding: '8px 12px', fontWeight: 700, fontSize: '0.9rem',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}>
                        <span>Source-batch SAC comparison</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <select
                            value={selectedExistingSAA}
                            onChange={(e) => setSelectedExistingSAA(e.target.value)}
                            title="Compare against a saved SAA from the Historic SAA tab"
                            style={{ padding: '4px 8px', borderRadius: 3, border: '1px solid #599BE3', fontSize: '0.8rem', minWidth: 180 }}
                          >
                            <option value="">Compare to existing SAA…</option>
                            {existingSAAOptions.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                          <select
                            value={blendComparisonProfile}
                            onChange={(e) => setBlendComparisonProfile(e.target.value)}
                            style={{ padding: '4px 8px', borderRadius: 3, border: '1px solid #599BE3', fontSize: '0.8rem', minWidth: 140 }}
                          >
                            {profiles.map((p) => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        {(() => {
                          const selProfile = blendComparisonProfile || profiles[0];
                          const sources = blendPerProfileSources[selProfile] || {};
                          const sourceNames = Object.keys(sources);
                          if (sourceNames.length === 0) {
                            return <div style={{ padding: 16, color: '#6c757d' }}>No source data for this profile.</div>;
                          }
                          // Existing-SAA comparison column (weights already in %).
                          const existingSAA = selectedExistingSAAWeights;
                          const hasExisting = existingSAA != null;
                          // Union of SACs across all sources + the existing SAA, ordered by acCodes.
                          const sacSet = new Set();
                          sourceNames.forEach((n) => Object.keys(sources[n] || {}).forEach((s) => sacSet.add(s)));
                          if (hasExisting) Object.keys(existingSAA).forEach((s) => sacSet.add(s));
                          const orderedSacs = [
                            ...acCodes.filter((c) => sacSet.has(c)),
                            ...[...sacSet].filter((c) => !acCodes.includes(c)),
                          ];
                          const blended = (blendResults[selProfile] && blendResults[selProfile].weights) || {};
                          // Overlay SACs are excluded from the per-column Total
                          // (they're additive on top of the 100% physical base).
                          const overlayCodes = new Set(
                            (assetClasses || [])
                              .filter((ac) => ac && ac.is_overlay)
                              .map((ac) => ac.code)
                          );
                          const headerCellStyle = {
                            color: '#072040',
                            padding: '8px 12px',
                            background: '#f5f6f8',
                            borderBottom: '2px solid #d6dae0',
                            fontWeight: 700,
                            fontSize: '0.82rem',
                          };
                          const sumExcludingOverlay = (obj) =>
                            Object.entries(obj || {})
                              .filter(([sac]) => !overlayCodes.has(sac))
                              .reduce((s, [, v]) => s + (v || 0), 0);
                          // Expected return / volatility / return-risk ratio for any
                          // weight vector (decimals) under the selected CMA scenario.
                          // Σ = corr·vol_i·vol_j with diagonal = vol² (matches the
                          // optimiser's own vol — avoids the stored-diagonal issue).
                          const retMap = {}, volMap = {};
                          (assetClasses || []).forEach((ac) => {
                            retMap[ac.code] = ac.annual_return;
                            volMap[ac.code] = ac.annual_volatility;
                          });
                          const portfolioMetrics = (weights) => {
                            const ws = Object.keys(weights || {}).filter((s) => retMap[s] != null);
                            if (!ws.length) return null;
                            let ret = 0, varSum = 0;
                            ws.forEach((a) => { ret += (weights[a] || 0) * (retMap[a] || 0); });
                            ws.forEach((a) => {
                              const wa = weights[a] || 0, va = volMap[a] || 0;
                              ws.forEach((b) => {
                                const wb = weights[b] || 0, vb = volMap[b] || 0;
                                const corr = a === b ? 1 : (corrMatrix?.[a]?.[b] ?? 0);
                                varSum += wa * wb * va * vb * corr;
                              });
                            });
                            const vol = Math.sqrt(Math.max(varSum, 0));
                            return { ret, vol, ratio: vol > 1e-9 ? ret / vol : 0 };
                          };
                          // existingSAA weights are percentages → convert to decimals.
                          const existingSAADecimal = hasExisting
                            ? Object.fromEntries(Object.entries(existingSAA).map(([s, v]) => [s, (v || 0) / 100]))
                            : null;
                          const srcMetrics = sourceNames.map((n) => portfolioMetrics(sources[n]));
                          const blendMetric = portfolioMetrics(blended);
                          const saaMetric = existingSAADecimal ? portfolioMetrics(existingSAADecimal) : null;
                          const metricRows = [
                            { label: 'Expected Return (%)', key: 'ret', scale: 100, dp: 2 },
                            { label: 'Volatility (%)', key: 'vol', scale: 100, dp: 2 },
                            { label: 'Return / Risk', key: 'ratio', scale: 1, dp: 2 },
                          ];
                          return (
                            <table className="weights-table" style={{ width: '100%', marginBottom: 0 }}>
                              <thead>
                                <tr>
                                  <th style={{ ...headerCellStyle, textAlign: 'left' }}>Sub Asset Class</th>
                                  {sourceNames.map((n) => (
                                    <th key={n} style={{ ...headerCellStyle, textAlign: 'right' }}>{n} (%)</th>
                                  ))}
                                  <th style={{ ...headerCellStyle, textAlign: 'right', background: '#e7f0fb' }}>Blended (%)</th>
                                  {hasExisting && (
                                    <>
                                      <th style={{ ...headerCellStyle, textAlign: 'right' }}>Existing SAA (%)</th>
                                      <th style={{ ...headerCellStyle, textAlign: 'right' }}>Diff vs Blended (%)</th>
                                    </>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {orderedSacs.map((sac) => {
                                  const vals = sourceNames.map((n) => (sources[n] && sources[n][sac]) || 0);
                                  const blendedVal = blended[sac] || 0;
                                  const saaVal = hasExisting ? (existingSAA[sac] || 0) : 0;  // percent
                                  const maxRow = Math.max(...vals.map((v) => v * 100), blendedVal * 100, saaVal);
                                  if (maxRow < 0.05) return null;
                                  const diffVal = blendedVal * 100 - saaVal;
                                  const isOverlay = overlayCodes.has(sac);
                                  return (
                                    <tr key={sac}>
                                      <td style={{ padding: '4px 12px', fontWeight: 600, color: '#072040' }}>
                                        {sac}
                                        {isOverlay && (
                                          <span style={{
                                            marginLeft: 6, padding: '1px 6px', borderRadius: 3,
                                            background: '#f0a500', color: '#072040',
                                            fontSize: '0.65rem', fontWeight: 700,
                                          }}>overlay</span>
                                        )}
                                      </td>
                                      {vals.map((v, i) => (
                                        <td key={i} style={{ padding: '4px 12px', textAlign: 'right', color: '#072040' }}>
                                          {(v * 100).toFixed(1)}
                                        </td>
                                      ))}
                                      <td style={{ padding: '4px 12px', textAlign: 'right', fontWeight: 700, color: '#072040', background: '#e7f0fb' }}>
                                        {(blendedVal * 100).toFixed(1)}
                                      </td>
                                      {hasExisting && (
                                        <>
                                          <td style={{ padding: '4px 12px', textAlign: 'right', color: '#072040' }}>
                                            {saaVal.toFixed(1)}
                                          </td>
                                          <td style={{ padding: '4px 12px', textAlign: 'right', fontWeight: 600, color: Math.abs(diffVal) < 0.05 ? '#6c757d' : (diffVal > 0 ? '#198754' : '#c0392b') }}>
                                            {(diffVal > 0 ? '+' : '') + diffVal.toFixed(1)}
                                          </td>
                                        </>
                                      )}
                                    </tr>
                                  );
                                })}
                                <tr>
                                  <td style={{ padding: '6px 12px', fontWeight: 700, color: '#6c757d', borderTop: '2px solid #d6dae0' }}>
                                    Total (excl. overlay)
                                  </td>
                                  {sourceNames.map((n, i) => (
                                    <td key={i} style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 700, color: '#6c757d', borderTop: '2px solid #d6dae0' }}>
                                      {(sumExcludingOverlay(sources[n]) * 100).toFixed(1)}
                                    </td>
                                  ))}
                                  <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 700, color: '#072040', borderTop: '2px solid #d6dae0', background: '#e7f0fb' }}>
                                    {(sumExcludingOverlay(blended) * 100).toFixed(1)}
                                  </td>
                                  {hasExisting && (() => {
                                    const saaTotal = Object.entries(existingSAA)
                                      .filter(([sac]) => !overlayCodes.has(sac))
                                      .reduce((s, [, v]) => s + (v || 0), 0);
                                    const diffTotal = sumExcludingOverlay(blended) * 100 - saaTotal;
                                    return (
                                      <>
                                        <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 700, color: '#6c757d', borderTop: '2px solid #d6dae0' }}>
                                          {saaTotal.toFixed(1)}
                                        </td>
                                        <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 700, color: '#6c757d', borderTop: '2px solid #d6dae0' }}>
                                          {(diffTotal > 0 ? '+' : '') + diffTotal.toFixed(1)}
                                        </td>
                                      </>
                                    );
                                  })()}
                                </tr>
                                {metricRows.map((m, mi) => {
                                  const topBorder = mi === 0 ? '2px solid #072040' : undefined;
                                  const fmt = (val) => (val != null ? (val * m.scale).toFixed(m.dp) : '—');
                                  return (
                                    <tr key={m.label}>
                                      <td style={{ padding: '4px 12px', fontWeight: 700, color: '#072040', borderTop: topBorder }}>
                                        {m.label}
                                      </td>
                                      {srcMetrics.map((mm, i) => (
                                        <td key={i} style={{ padding: '4px 12px', textAlign: 'right', color: '#072040', borderTop: topBorder }}>
                                          {fmt(mm ? mm[m.key] : null)}
                                        </td>
                                      ))}
                                      <td style={{ padding: '4px 12px', textAlign: 'right', fontWeight: 700, color: '#072040', background: '#e7f0fb', borderTop: topBorder }}>
                                        {fmt(blendMetric ? blendMetric[m.key] : null)}
                                      </td>
                                      {hasExisting && (
                                        <>
                                          <td style={{ padding: '4px 12px', textAlign: 'right', color: '#072040', borderTop: topBorder }}>
                                            {fmt(saaMetric ? saaMetric[m.key] : null)}
                                          </td>
                                          <td style={{ padding: '4px 12px', textAlign: 'right', color: '#6c757d', borderTop: topBorder }}>
                                            {blendMetric && saaMetric
                                              ? ((blendMetric[m.key] - saaMetric[m.key]) >= 0 ? '+' : '') + ((blendMetric[m.key] - saaMetric[m.key]) * m.scale).toFixed(m.dp)
                                              : '—'}
                                          </td>
                                        </>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Bar chart — assets on X, profile as series (same pattern as batch tab) */}
                  {chartData.length > 0 && (
                    <div className="weights-chart-container">
                      <h4>Blended Weights by Profile</h4>
                      <ResponsiveContainer width="100%" height={420}>
                        <BarChart data={chartData} margin={{ left: 20, right: 20, top: 10, bottom: 70 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="category" dataKey="name" interval={0} angle={-45} textAnchor="end" height={70} tick={{ fontSize: 11 }} />
                          <YAxis type="number" domain={[0, 'auto']} tickFormatter={(v) => `${v}%`} />
                          <Tooltip formatter={(v) => `${v.toFixed(1)}%`} />
                          <Legend />
                          {profiles.map((profile, i) => (
                            <Bar key={profile} dataKey={profile} fill={BATCH_COLOURS[i % BATCH_COLOURS.length]} radius={[3, 3, 0, 0]} />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Detailed comparison table */}
                  <div className="optimiser-panel" style={{ marginTop: 16 }}>
                    <div className="optimiser-panel-header">Profile Comparison</div>
                    <div className="optimiser-panel-body" style={{ padding: 0, overflowX: 'auto' }}>
                      <table className="weights-table batch-table">
                        <thead>
                          <tr>
                            <th>Metric</th>
                            {profiles.map((p) => (<th key={p}>{p}</th>))}
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { label: 'Return (%)', fn: (r) => (r.portfolio_return * 100).toFixed(2) },
                            { label: 'Volatility (%)', fn: (r) => (r.portfolio_volatility * 100).toFixed(2) },
                            { label: 'Sharpe Ratio', fn: (r) => r.sharpe_ratio.toFixed(3) },
                            { label: 'Growth (%)', fn: (r) => roundHalf(r.growth_exposure * 100).toFixed(1) },
                          ].map(({ label, fn }) => (
                            <tr key={label} className="batch-summary-row">
                              <td style={{ fontWeight: 700 }}>{label}</td>
                              {profiles.map((p) => (
                                <td key={p} style={{ fontWeight: 600 }}>{fn(blendResults[p])}</td>
                              ))}
                            </tr>
                          ))}
                          <tr><td colSpan={1 + profiles.length} style={{ padding: '4px', background: '#f8f9fa' }}></td></tr>
                          {acCodes.map((code) => {
                            const vals = profiles.map((p) => roundHalf((blendResults[p].weights[code] || 0) * 100));
                            const maxVal = Math.max(...vals);
                            if (maxVal < 0.1) return null;
                            return (
                              <tr key={code}>
                                <td style={{ fontWeight: 600 }}>{code}</td>
                                {vals.map((v, i) => (
                                  <td key={i} style={{ background: v > 0.1 ? `rgba(89, 155, 227, ${Math.min(v / maxVal * 0.3, 0.3)})` : 'transparent' }}>
                                    {v.toFixed(1)}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Blend Batches: saved-runs picker (filtered to kind='batch'). Multi-select. */}
      {showBlendPicker && (() => {
        const eligibleRuns = savedRuns
          .filter((r) => r.kind === 'batch')
          .filter((r) => !blendSelectedRuns.some((sel) => sel.run_id === r.id));
        const allChecked = eligibleRuns.length > 0 && eligibleRuns.every((r) => !!blendPickerChecked[r.id]);
        const someChecked = eligibleRuns.some((r) => !!blendPickerChecked[r.id]);
        const toggleAll = () => {
          if (allChecked) {
            setBlendPickerChecked({});
          } else {
            const next = {};
            eligibleRuns.forEach((r) => { next[r.id] = true; });
            setBlendPickerChecked(next);
          }
        };
        const checkedCount = Object.values(blendPickerChecked).filter(Boolean).length;
        const headerCellStyle = {
          color: '#072040',
          background: '#f5f6f8',
          padding: '8px 12px',
          borderBottom: '2px solid #d6dae0',
          fontWeight: 700,
          fontSize: '0.82rem',
          textAlign: 'left',
        };
        return (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(7,32,64,0.55)', zIndex: 9999 }} onClick={(e) => { if (e.target === e.currentTarget) setShowBlendPicker(false); }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header" style={{ background: 'var(--primary-color)', color: '#fff' }}>
                  <h5 className="modal-title" style={{ color: '#fff' }}>Select saved batches to add</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowBlendPicker(false)}></button>
                </div>
                <div className="modal-body">
                  {savedRunsLoading ? (
                    <div className="text-muted">Loading saved runs…</div>
                  ) : eligibleRuns.length === 0 ? (
                    <div className="text-muted" style={{ padding: '12px 0' }}>No saved batch runs available to add.</div>
                  ) : (
                    <table className="weights-table" style={{ width: '100%' }}>
                      <thead>
                        <tr>
                          <th style={{ ...headerCellStyle, width: 40, textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={allChecked}
                              ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                              onChange={toggleAll}
                              title="Select all / none"
                            />
                          </th>
                          <th style={headerCellStyle}>Name</th>
                          <th style={headerCellStyle}>Description</th>
                          <th style={{ ...headerCellStyle, textAlign: 'right' }}>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligibleRuns.map((r) => {
                          const checked = !!blendPickerChecked[r.id];
                          const toggle = () => setBlendPickerChecked((prev) => ({ ...prev, [r.id]: !prev[r.id] }));
                          return (
                            <tr key={r.id} onClick={toggle} style={{ cursor: 'pointer', background: checked ? '#e7f0fb' : undefined }}>
                              <td style={{ textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={toggle}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td style={{ color: '#072040', fontWeight: 600 }}>{r.name}</td>
                              <td className="text-muted" style={{ fontSize: '0.8rem' }}>{r.description || '—'}</td>
                              <td className="text-muted" style={{ fontSize: '0.8rem', textAlign: 'right' }}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="modal-footer">
                  <span className="text-muted" style={{ marginRight: 'auto', fontSize: '0.85rem' }}>
                    {checkedCount > 0 ? `${checkedCount} selected` : 'Nothing selected'}
                  </span>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowBlendPicker(false)}>Cancel</button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={checkedCount === 0}
                    onClick={addCheckedBlendRuns}
                  >
                    Add Selected ({checkedCount})
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Batch export picker: pick saved batch runs to bundle into one workbook */}
      {showBatchExportPicker && (() => {
        const eligibleRuns = savedRuns.filter((r) => r.kind === 'batch');
        const allChecked = eligibleRuns.length > 0 && eligibleRuns.every((r) => !!batchExportChecked[r.id]);
        const someChecked = eligibleRuns.some((r) => !!batchExportChecked[r.id]);
        const toggleAll = () => {
          if (allChecked) {
            setBatchExportChecked({});
          } else {
            const next = {};
            eligibleRuns.forEach((r) => { next[r.id] = true; });
            setBatchExportChecked(next);
          }
        };
        const checkedCount = Object.values(batchExportChecked).filter(Boolean).length;
        const headerCellStyle = {
          color: '#072040',
          background: '#f5f6f8',
          padding: '8px 12px',
          borderBottom: '2px solid #d6dae0',
          fontWeight: 700,
          fontSize: '0.82rem',
          textAlign: 'left',
        };
        return (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(7,32,64,0.55)', zIndex: 9999 }} onClick={(e) => { if (e.target === e.currentTarget && !batchExportLoading) setShowBatchExportPicker(false); }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header" style={{ background: 'var(--primary-color)', color: '#fff' }}>
                  <h5 className="modal-title" style={{ color: '#fff' }}>Select saved batch runs to export</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowBatchExportPicker(false)} disabled={batchExportLoading}></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                    Each selected run is exported with its full detail — scenario, CMAs, correlation,
                    constraints, sub-asset classes and simulation results — plus a cross-run comparison
                    sheet. CMAs/correlation are pulled live from each run's own saved scenario.
                  </p>
                  {savedRunsLoading ? (
                    <div className="text-muted">Loading saved runs…</div>
                  ) : eligibleRuns.length === 0 ? (
                    <div className="text-muted" style={{ padding: '12px 0' }}>No saved batch runs available to export.</div>
                  ) : (
                    <table className="weights-table" style={{ width: '100%' }}>
                      <thead>
                        <tr>
                          <th style={{ ...headerCellStyle, width: 40, textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={allChecked}
                              ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                              onChange={toggleAll}
                              title="Select all / none"
                            />
                          </th>
                          <th style={headerCellStyle}>Name</th>
                          <th style={headerCellStyle}>Scenario</th>
                          <th style={headerCellStyle}>Description</th>
                          <th style={{ ...headerCellStyle, textAlign: 'right' }}>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligibleRuns.map((r) => {
                          const checked = !!batchExportChecked[r.id];
                          const toggle = () => setBatchExportChecked((prev) => ({ ...prev, [r.id]: !prev[r.id] }));
                          return (
                            <tr key={r.id} onClick={toggle} style={{ cursor: 'pointer', background: checked ? '#e7f0fb' : undefined }}>
                              <td style={{ textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={toggle}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td style={{ color: '#072040', fontWeight: 600 }}>{r.name}</td>
                              <td className="text-muted" style={{ fontSize: '0.8rem' }}>{r.scenario_name || (r.scenario_id != null ? `id=${r.scenario_id}` : 'Default')}</td>
                              <td className="text-muted" style={{ fontSize: '0.8rem' }}>{r.description || '—'}</td>
                              <td className="text-muted" style={{ fontSize: '0.8rem', textAlign: 'right' }}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="modal-footer">
                  <span className="text-muted" style={{ marginRight: 'auto', fontSize: '0.85rem' }}>
                    {checkedCount > 0 ? `${checkedCount} selected` : 'Nothing selected'}
                  </span>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowBatchExportPicker(false)} disabled={batchExportLoading}>Cancel</button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={checkedCount === 0 || batchExportLoading}
                    onClick={handleExportSelectedBatches}
                  >
                    {batchExportLoading ? 'Building…' : `Export Selected to Excel (${checkedCount})`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Import-from-batch-profile modal (Single tab) */}
      {showImportBatchProfile && (() => {
        const templateNames = Object.keys(savedBatchTemplates).sort();
        const currentTemplate = savedBatchTemplates[importBatchTemplateName];
        const profileOptions = currentTemplate
          ? Object.keys(currentTemplate.batchProfileBounds || {})
          : [];
        const preview = importBatchTemplateName && importBatchProfileName
          ? translateBatchProfileToConstraints(importBatchTemplateName, importBatchProfileName)
          : null;
        const previewCount = preview ? Object.keys(preview.acConstraints).length : 0;
        return (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(7,32,64,0.55)', zIndex: 9999 }} onClick={(e) => { if (e.target === e.currentTarget) setShowImportBatchProfile(false); }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header" style={{ background: 'var(--primary-color)', color: '#fff' }}>
                  <h5 className="modal-title" style={{ color: '#fff' }}>Import from batch profile</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowImportBatchProfile(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                    Pick a saved batch template, then a profile within it. The profile's per-SAC bounds
                    (absolute + relative), growth target/range, and fee budget will replace the current
                    Single-tab constraints. The "Saved Sets" dropdown clears — use Save As… afterwards to
                    persist as a named constraint set.
                  </p>
                  <div className="mb-3">
                    <label className="form-label" style={{ fontSize: '0.82rem' }}>Batch template</label>
                    <select
                      className="form-select form-select-sm"
                      value={importBatchTemplateName}
                      onChange={(e) => {
                        const next = e.target.value;
                        setImportBatchTemplateName(next);
                        const t = savedBatchTemplates[next];
                        const first = (t && Object.keys(t.batchProfileBounds || {})[0]) || '';
                        setImportBatchProfileName(first);
                      }}
                    >
                      {templateNames.length === 0 && <option value="">(no saved batch templates)</option>}
                      {templateNames.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label" style={{ fontSize: '0.82rem' }}>Profile</label>
                    <select
                      className="form-select form-select-sm"
                      value={importBatchProfileName}
                      onChange={(e) => setImportBatchProfileName(e.target.value)}
                      disabled={profileOptions.length === 0}
                    >
                      {profileOptions.length === 0 && <option value="">(template has no per-profile bounds)</option>}
                      {profileOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  {preview && (
                    <div style={{ background: '#f5f6f8', padding: '8px 12px', borderRadius: 4, fontSize: '0.8rem', color: '#072040' }}>
                      Will import <strong>{previewCount}</strong> SAC bound{previewCount === 1 ? '' : 's'}
                      {preview.growthMode === 'exact' && <> · growth target = <strong>{preview.growthTarget}%</strong></>}
                      {preview.growthMode === 'range' && (
                        <> · growth range <strong>{preview.growthMin ?? '–'}% to {preview.growthMax ?? '–'}%</strong></>
                      )}
                      {preview.feeBudget != null && <> · fee budget = <strong>{preview.feeBudget}bps</strong> (not currently used in Single tab)</>}
                      {preview.skipped > 0 && (
                        <div style={{ color: '#6c757d', marginTop: 4 }}>
                          {preview.skipped} blank cell{preview.skipped === 1 ? '' : 's'} skipped.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowImportBatchProfile(false)}>Cancel</button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleImportBatchProfileConfirm}
                    disabled={!importBatchTemplateName || !importBatchProfileName || previewCount === 0}
                  >
                    Import {previewCount > 0 ? `(${previewCount} bound${previewCount === 1 ? '' : 's'})` : ''}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Save Run Modal */}
      {showSaveRunModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(7,32,64,0.55)', zIndex: 9999 }} onClick={(e) => { if (e.target === e.currentTarget) setShowSaveRunModal(false); }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header" style={{ background: 'var(--primary-color)', color: '#fff' }}>
                <h5 className="modal-title">Save Optimisation Run</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowSaveRunModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label small fw-bold">Name</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="e.g. DSP 70 Sticky Inflation"
                    value={saveRunName}
                    onChange={(e) => setSaveRunName(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold">Description (optional)</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    value={saveRunDescription}
                    onChange={(e) => setSaveRunDescription(e.target.value)}
                  />
                </div>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="saveRunFavSwitch"
                    checked={saveRunFavourite}
                    onChange={(e) => setSaveRunFavourite(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="saveRunFavSwitch">
                    Mark as favourite
                  </label>
                </div>
                <p className="text-muted small mt-3 mb-0">
                  Saves all current inputs (scenario, objectives, growth allocation, asset constraints, target return/vol) and all current outputs (per-objective results + efficient frontier if computed). Creator and timestamp are recorded automatically.
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowSaveRunModal(false)}>Cancel</button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSaveRunSubmit}
                  disabled={savingRun || !saveRunName.trim()}
                >
                  {savingRun ? 'Saving…' : 'Save Run'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Saved Runs Browser Modal */}
      {showSavedRunsModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(7,32,64,0.55)', zIndex: 9999 }} onClick={(e) => { if (e.target === e.currentTarget) setShowSavedRunsModal(false); }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header" style={{ background: 'var(--primary-color)', color: '#fff' }}>
                <h5 className="modal-title" style={{ color: '#fff', background: 'transparent', margin: 0 }}>Saved Optimisation Runs</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowSavedRunsModal(false)} />
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh' }}>
                <div className="d-flex gap-3 mb-3 align-items-center flex-wrap">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="favOnlySwitch"
                      checked={savedRunsFavOnly}
                      onChange={(e) => setSavedRunsFavOnly(e.target.checked)}
                    />
                    <label className="form-check-label small" htmlFor="favOnlySwitch">Favourites only</label>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="mineOnlySwitch"
                      checked={savedRunsMineOnly}
                      onChange={(e) => setSavedRunsMineOnly(e.target.checked)}
                    />
                    <label className="form-check-label small" htmlFor="mineOnlySwitch">My runs only</label>
                  </div>
                  <button
                    className="btn btn-outline-secondary btn-sm ms-auto"
                    onClick={fetchSavedRuns}
                    disabled={savedRunsLoading}
                  >
                    {savedRunsLoading ? 'Loading…' : 'Refresh'}
                  </button>
                </div>
                {savedRunsLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm" role="status"></div>
                    <span className="ms-2 small">Loading…</span>
                  </div>
                ) : savedRuns.length === 0 ? (
                  <div className="text-muted text-center py-4 small">No saved runs match your filters.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover" style={{ fontSize: '0.82rem' }}>
                      <thead>
                        <tr>
                          <th style={{ width: 30 }} title="Toggle favourite"></th>
                          <th style={{ width: 70 }}>Kind</th>
                          <th>Name</th>
                          <th>Scenario</th>
                          <th>Objectives</th>
                          <th>Created by</th>
                          <th>Saved</th>
                          <th style={{ width: 90 }}>Sharing</th>
                          <th style={{ width: 110 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {savedRuns.map((run) => (
                          <tr key={run.id}>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                onClick={() => run.can_edit !== false && handleToggleRunFavourite(run)}
                                disabled={run.can_edit === false}
                                title={run.can_edit === false ? 'Read-only — cannot change' : (run.is_favourite ? 'Unmark as favourite' : 'Mark as favourite')}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: run.is_favourite ? 'var(--secondary-color)' : '#ccc',
                                  fontSize: '1.1rem',
                                  cursor: run.can_edit === false ? 'default' : 'pointer',
                                  opacity: run.can_edit === false ? 0.5 : 1,
                                  padding: 0,
                                  lineHeight: 1,
                                }}
                              >
                                <i className={run.is_favourite ? 'bi bi-star-fill' : 'bi bi-star'}></i>
                              </button>
                            </td>
                            <td>
                              <span
                                className="badge"
                                style={{
                                  background: (run.kind === 'batch') ? 'var(--primary-color)' : 'var(--secondary-color)',
                                  color: (run.kind === 'batch') ? '#fff' : 'var(--primary-color)',
                                  fontSize: '0.68rem',
                                }}
                              >
                                {run.kind === 'batch' ? 'Batch' : 'Single'}
                              </span>
                            </td>
                            <td>
                              <div style={{ fontWeight: 600 }}>{run.name}</div>
                              {run.description && (
                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>{run.description}</div>
                              )}
                            </td>
                            <td>{run.scenario_name || (run.scenario_id ? `#${run.scenario_id}` : '—')}</td>
                            <td>
                              {(run.objectives || '').split(',').filter(Boolean).map((o) => (
                                <span key={o} className="badge bg-light text-dark me-1" style={{ fontSize: '0.68rem' }}>
                                  {OBJECTIVE_CONFIG[o]?.label || o}
                                </span>
                              ))}
                            </td>
                            <td>{run.created_by_email || '—'}</td>
                            <td title={run.updated_at || run.created_at}>
                              {(() => {
                                const ts = run.updated_at || run.created_at;
                                return ts ? new Date(ts).toLocaleString() : '—';
                              })()}
                            </td>
                            <td>
                              {run.is_locked ? (
                                <span
                                  className="badge"
                                  style={{ background: 'var(--dark-color)', color: '#fff', fontSize: '0.66rem' }}
                                  title={`Locked${run.locked_at ? ' on ' + new Date(run.locked_at).toLocaleDateString() : ''}${run.locked_by_email ? ' by ' + run.locked_by_email : ''} — permanently read-only.`}
                                >
                                  <i className="bi bi-lock-fill me-1"></i>Locked
                                </span>
                              ) : run.access === 'read' ? (
                                <span
                                  className="badge"
                                  style={{ background: 'var(--info-color)', color: '#fff', fontSize: '0.66rem' }}
                                  title="Read-only to other users — only the owner can edit or delete."
                                >
                                  Read-only
                                </span>
                              ) : (
                                <span
                                  className="badge"
                                  style={{ background: 'var(--success-color)', color: 'var(--primary-color)', fontSize: '0.66rem' }}
                                  title="Editable by any optimiser user."
                                >
                                  Read/Edit
                                </span>
                              )}
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <button
                                className="btn btn-sm p-0 me-3"
                                onClick={() => handleLoadRun(run.id)}
                                title="Load this run's inputs + results into the current view"
                                style={{ color: 'var(--primary-color)', border: 'none', background: 'none', fontSize: '0.95rem' }}
                              >
                                <i className="bi bi-box-arrow-in-down"></i>
                              </button>
                              {run.can_manage && (
                                <button
                                  className="btn btn-sm p-0 me-3"
                                  onClick={() => handleToggleRunSharing(run)}
                                  title={run.access === 'read'
                                    ? 'Read-only to others — click to make editable by all users'
                                    : 'Editable by all users — click to make read-only to others'}
                                  style={{ color: 'var(--info-color)', border: 'none', background: 'none', fontSize: '0.95rem' }}
                                >
                                  <i className={run.access === 'read' ? 'bi bi-pencil-fill' : 'bi bi-eye-fill'}></i>
                                </button>
                              )}
                              {run.can_manage && (
                                <button
                                  className="btn btn-sm p-0 me-3"
                                  onClick={() => handleLockRun(run)}
                                  title="Permanently lock — read-only for everyone, cannot be edited, deleted or unlocked"
                                  style={{ color: 'var(--dark-color)', border: 'none', background: 'none', fontSize: '0.95rem' }}
                                >
                                  <i className="bi bi-lock"></i>
                                </button>
                              )}
                              <button
                                className="btn btn-sm p-0"
                                onClick={() => run.can_edit !== false && handleDeleteSavedRun(run)}
                                disabled={run.can_edit === false}
                                title={run.can_edit === false
                                  ? (run.is_locked ? 'Locked — cannot be deleted' : 'Read-only — only the owner can delete')
                                  : 'Permanently delete this saved run'}
                                style={{
                                  color: run.can_edit === false ? '#ccc' : '#dc3545',
                                  border: 'none', background: 'none', fontSize: '0.95rem',
                                  cursor: run.can_edit === false ? 'default' : 'pointer',
                                }}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowSavedRunsModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Register as Portfolio Modal */}
      {showRegisterModal && registerObj && results[registerObj] && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header" style={{ background: 'var(--primary-color)', color: '#fff' }}>
                <h5 className="modal-title">Register as Blender Portfolio</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowRegisterModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Portfolio Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={registerName}
                    onChange={e => setRegisterName(e.target.value)}
                    placeholder="e.g. DSP 70 (Optimised May 2026)"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Portfolio Code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={registerCode}
                    onChange={e => setRegisterCode(e.target.value)}
                    placeholder="e.g. opt_dsp70_v2"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Source Objective</label>
                  <select className="form-select" value={registerObj} onChange={e => setRegisterObj(e.target.value)}>
                    {Object.keys(results).map(k => (
                      <option key={k} value={k}>{OBJECTIVE_CONFIG[k]?.label || k}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">SAC Weights</label>
                  <div className="table-responsive" style={{ maxHeight: '200px' }}>
                    <table className="table table-sm table-striped mb-0">
                      <thead><tr><th>Asset Class</th><th>Weight</th></tr></thead>
                      <tbody>
                        {Object.entries(results[registerObj]?.weights || {})
                          .filter(([, w]) => w > 0.001)
                          .sort(([, a], [, b]) => b - a)
                          .map(([ac, w]) => (
                            <tr key={ac}>
                              <td>{ac}</td>
                              <td>{(w * 100).toFixed(1)}%</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowRegisterModal(false)}>Cancel</button>
                <button
                  className="btn btn-primary"
                  disabled={registering || !registerName.trim() || !registerCode.trim()}
                  onClick={async () => {
                    try {
                      setRegistering(true);
                      const { blenderRegistrationAPI } = await import('../services/api');
                      await blenderRegistrationAPI.registerPortfolio({
                        portfolio_code: registerCode.trim(),
                        portfolio_name: registerName.trim(),
                        sac_weights: results[registerObj].weights,
                        source: 'optimiser',
                        suite: 'Optimised',
                      });
                      setShowRegisterModal(false);
                      setError(null);
                      alert('Portfolio registered successfully!');
                    } catch (err) {
                      setError('Registration failed: ' + (err.response?.data?.detail || err.message));
                    } finally {
                      setRegistering(false);
                    }
                  }}
                >
                  {registering ? 'Registering...' : 'Register'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioOptimiserApp;
